// Shared presentation only: no profile data or backend settings are changed.
window.ModlForumPresentation = (() => {
  const palette = ['#F5AA83', '#EAC36B', '#D6D97C', '#AFC48C', '#84C6AA', '#83C8C7',
    '#95BDD9', '#A8AFE0', '#C3AADE', '#D8A3BE', '#D8B596', '#B8C3BF'];
  return {
    avatarStyle(id, name = 'Member') {
      const key = String(id || '').trim().replace(/^gid:\/\/shopify\/Customer\//, '') || String(name || 'Member').trim().toLowerCase();
      let hash = 2166136261;
      for (const char of key) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
      return { backgroundColor: palette[hash % palette.length], color: '#202820' };
    },
    timestamp(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const day = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
      const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      return `${day} · ${time}`;
    },
  };
})();

document.addEventListener('alpine:init', () => {
  // Both shortcut rows use the same forum-aware Back behavior.
  const forumShortcutNavigation = {
    goUp() {
      if (!this.showGuidelinesView && (this.showProfileView || this.showMessagesView)) {
        return this.backWithinForum();
      }
      if (this.selectedArticleHandle && !this.showGuidelinesView) {
        // Return to this category's last visit, never an unrelated topic or profile.
        for (let i = this.forumTrail.length - 1; i >= 0; i--) {
          const visit = this.forumTrail[i];
          if (visit.selectedBlogHandle === this.selectedBlogHandle && !visit.selectedArticleHandle &&
              !visit.showProfileView && !visit.showMessagesView && !visit.showGuidelinesView) {
            this.forumTrail.splice(i + 1);
            return this.backWithinForum();
          }
        }
        return this.backToBlog();
      }
      return this.backToForum();
    },
  };
  Alpine.data('ForumShortcutNavigation', () => ({ ...forumShortcutNavigation }));

  Alpine.data('DesktopForumBack', () => ({
    ...forumShortcutNavigation,
    pinned: false,
    init() {
      const tools = this.$el.closest('.modl-cf__sidebar-tools');
      const anchor = tools.parentElement;
      let frame = 0;
      const update = () => {
        frame = 0;
        const style = getComputedStyle(tools);
        this.pinned = style.position === 'sticky' && anchor.getBoundingClientRect().top < parseFloat(style.top);
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const observer = new ResizeObserver(schedule);
      observer.observe(anchor);
      observer.observe(document.documentElement);
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      this._cleanupDesktopBack = () => {
        observer.disconnect();
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
      };
      this.$nextTick(schedule);
    },
    destroy() { this._cleanupDesktopBack?.(); },
  }));

  // Keep only the mobile shortcuts visible; their anchor preserves the breadcrumb row.
  Alpine.data('MobileForumShortcuts', () => ({
    ...forumShortcutNavigation,
    pinned: false,
    init() {
      const anchor = this.$el;
      const shortcuts = anchor.querySelector('.modl-cf__mobile-shortcuts');
      const forum = anchor.closest('.modl-cf');
      let frame = 0;
      const update = () => {
        frame = 0;
        const rect = anchor.getBoundingClientRect();
        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height')) || 0;
        const top = headerHeight + 12;
        // Keep the current sticky state through small movements at the boundary.
        const releaseBuffer = this.pinned ? 6 : 0;
        const pinned = window.innerWidth < 990 && rect.width > 0 && rect.top < top + releaseBuffer;
        this.pinned = pinned;
        shortcuts.classList.toggle('modl-cf__mobile-shortcuts--pinned', pinned);
        shortcuts.style.left = pinned ? `${rect.left - 38}px` : '';
        // Follow the normal row within the release buffer so unpinning cannot snap down.
        shortcuts.style.top = pinned ? `${Math.min(Math.max(top, rect.top), forum.getBoundingClientRect().bottom - 44)}px` : '';
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const observer = new ResizeObserver(schedule);
      observer.observe(anchor);
      observer.observe(document.documentElement);
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      this._cleanupShortcuts = () => {
        observer.disconnect();
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
      };
      this.$nextTick(schedule);
    },
    destroy() { this._cleanupShortcuts?.(); },
  }));

  const DEFAULT_API_BASE = '/apps/modl-community';
  const PROFILE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);
  const PROFILE_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
  /** Trailing slash required so TinyMCE resolves icons/skins/plugins URLs correctly. */
  const TINYMCE_CDN = 'https://cdn.jsdelivr.net/npm/tinymce@7/';

  // A visit owns its sorting and reading position; fresh links never inherit it.
  const forumNavigation = {
    commentSort: 'newest',
    _returnPosition: null,
    _cancelReadingScroll: null,
    _readingTargetId: '',
    captureForumVisit() {
      const elements = this.showProfileView || this.showMessagesView ? [] :
        [...(this.getMainViewContainer()?.querySelectorAll('[id^="modl-cf-comment-"]') || [])];
      const visible = elements.filter(el => { const r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < window.innerHeight; });
      // Prefer the reply itself when its parent spans most of the viewport.
      const anchor = visible.sort((a, b) => Math.abs(a.getBoundingClientRect().top) - Math.abs(b.getBoundingClientRect().top))[0];
      return {
        selectedBlogHandle: this.selectedBlogHandle, selectedArticleHandle: this.selectedArticleHandle,
        selectedTag: this.selectedTag, searchTerm: this.searchTerm, searchFilter: this.searchFilter,
        showProfileView: this.showProfileView, showMessagesView: this.showMessagesView,
        showGuidelinesView: this.showGuidelinesView, guidelinesDocument: this.guidelinesDocument,
        profileViewContext: JSON.parse(JSON.stringify(this.profileViewContext)),
        commentSort: this.commentSort,
        position: { scrollY: window.scrollY, id: anchor?.id || '', offset: anchor?.getBoundingClientRect().top || 0 },
      };
    },
    saveForumVisit() {
      if (this._cancelReadingScroll || this.getMainViewContainer()?.getAttribute('aria-busy') === 'true') return;
      window.history.replaceState({ ...window.history.state, modlForumVisit: this.captureForumVisit() }, '', window.location.href);
    },
    stopReadingScroll() {
      this._cancelReadingScroll?.();
      this._cancelReadingScroll = null;
    },
    restoreReadingPosition(position, ready = () => true) {
      this.stopReadingScroll();
      const started = Date.now();
      let timer, settledAt = 0, lastTop = null;
      const cancel = () => {
        clearTimeout(timer);
        for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.removeEventListener(event, cancel, true);
        if (this._cancelReadingScroll === cancel) {
          this._cancelReadingScroll = null;
          this._readingTargetId = '';
        }
      };
      this._cancelReadingScroll = cancel;
      this._readingTargetId = position.id || '';
      for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.addEventListener(event, cancel, { capture: true, passive: true });
      const tick = () => {
        if (Date.now() - started > 60000) { cancel(); return; }
        if (ready()) {
          const element = position.id ? document.getElementById(position.id) : null;
          const comments = UTILS.getCommentsListComponent();
          const loading = !!comments?.loadingAll;
          const rect = element?.getBoundingClientRect();
          // If a remembered comment was deleted, restore the old offset after loading.
          if (!position.id || rect || (!loading && !position.center)) {
            const top = rect ? window.scrollY + rect.top - (position.center ? Math.max(24, (window.innerHeight - Math.min(rect.height, window.innerHeight - 48)) / 2) : position.offset) : position.scrollY || 0;
            if (Math.abs(window.scrollY - top) > 1) window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
            const imagesPending = [...(this.getMainViewContainer()?.querySelectorAll('img') || [])].some(img => !img.complete && img.getBoundingClientRect().top < window.innerHeight);
            if (loading || imagesPending || lastTop === null || Math.abs(lastTop - top) > 1) settledAt = Date.now();
            else if (Date.now() - settledAt >= 1000) { cancel(); this.saveForumVisit(); return; }
            lastTop = top;
          }
        }
        timer = setTimeout(tick, 100);
      };
      timer = setTimeout(tick, 0);
    },
  };

  // Sort copies only: the cached default order and reply arrays remain untouched.
  const forumSorting = (threads = false) => ({
    sortOrder: threads ? 'newest' : 'default', activeSort: threads ? 'newest' : 'default', sortBusy: false, sortError: '',
    async changeSort() {
      const requested = this.sortOrder;
      if (!['default', 'oldest', 'newest', 'likes'].includes(requested)) return;
      if (threads) UTILS.getCommunityForum()?.stopReadingScroll?.();
      this.sortError = '';
      this.sortBusy = true;
      try {
        if (requested !== 'default' && this.pageInfo?.hasNextPage) {
          await this.loadNextPage();
          if (this.pageInfo?.hasNextPage) throw new Error('Topics are still loading. Please try again.');
        }
        if (requested === 'likes') {
          const items = this.sortSourceItems();
          const posts = threads ? items.flatMap(item => [item, ...(item.replies || [])]) : items;
          await this.$store.likes.loadCounts(posts, threads ? 'cs-comments' : this.blogHandle);
        }
        if (this.sortOrder === requested) {
          this.activeSort = requested;
          if (threads) {
            const forum = UTILS.getCommunityForum();
            const listElement = this.rootEl || this.$el;
            if (forum && listElement?.isConnected && !forum.showProfileView && !forum.showMessagesView) {
              forum.commentSort = requested;
              this.$nextTick(() => { if (listElement.isConnected) forum.saveForumVisit(); });
            }
          }
        }
      } catch (error) {
        this.sortError = error.message || 'Could not sort this list. Please try again.';
        this.sortOrder = this.activeSort;
      } finally { this.sortBusy = false; }
    },
    sortedItems(items) {
      if (this.activeSort === 'default') return items;
      const date = item => Date.parse(item.publishedAt || item.published_at || item.created_at || '') || 0;
      const count = item => {
        const blog = threads ? 'cs-comments' : this.blogHandle;
        if (this.$store.moderation?.hidden(blog, item.handle)) return 0;
        const key = this.$store.likes.key?.(blog, item.handle) || `${blog}/${item.handle}`;
        return this.$store.likes.entries[key]?.count || 0;
      };
      const score = item => threads ? Math.max(count(item), ...(item.replies || []).map(count)) : count(item);
      return items.map((item, index) => ({ item, index })).sort((a, b) => {
        const difference = this.activeSort === 'likes' ? score(b.item) - score(a.item) :
          this.activeSort === 'oldest' ? date(a.item) - date(b.item) : date(b.item) - date(a.item);
        return difference || a.index - b.index;
      }).map(row => row.item);
    }
  });

  const UTILS = {
    _tinyMceLoadPromise: null,
    _csrfToken: '',
    _csrfPromise: null,
    _articleApiCache: new Map(),
    _customerProfileNameCache: new Map(),
    _customerProfileNameValues: new Map(),
    _customerProfileImageValues: new Map(),
    _customerProfileBioValues: new Map(),

    // --- Cloudinary ---
    getCloudinaryCloudName() {
      return (
        window.CLOUDINARY_CLOUD_NAME ||
        document.documentElement?.dataset?.cloudinaryCloudName ||
        document.querySelector('[data-cloudinary-cloud-name]')?.dataset?.cloudinaryCloudName ||
        'dv5n6l7cs'
      ).trim();
    },

    getCloudinaryUploadPreset() {
      return (
        window.CLOUDINARY_UPLOAD_PRESET ||
        document.documentElement?.dataset?.cloudinaryUploadPreset ||
        document.querySelector('[data-cloudinary-upload-preset]')?.dataset?.cloudinaryUploadPreset ||
        ''
      ).trim();
    },

    async uploadToCloudinary(file, resourceType = 'auto') {
      if (window.ModlCommunityTerms && !await window.ModlCommunityTerms.ensure()) throw new Error('Complete your forum profile setup before uploading.');
      if (!file) {
        throw new Error('No file selected for upload');
      }

      const cloudName = this.getCloudinaryCloudName();
      const uploadPreset = this.getCloudinaryUploadPreset();
      if (!uploadPreset) {
        throw new Error('Cloudinary upload preset is not configured');
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', uploadPreset);
      formData.append('resource_type', resourceType);

      try {
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${cloudName}/upload`,
          {
            method: 'POST',
            body: formData,
          }
        );
        const data = await response.json();
        if (!response.ok || !data?.secure_url) {
          throw new Error(data?.error?.message || 'Cloudinary upload failed');
        }
        return data.secure_url;
      } catch (error) {
        console.error('Cloudinary upload failed:', error);
        throw error;
      }
    },

    // --- Editor loading ---
    ensureTinyMce() {
      if (typeof window.tinymce !== 'undefined') {
        return Promise.resolve();
      }
      if (this._tinyMceLoadPromise) {
        return this._tinyMceLoadPromise;
      }
      this._tinyMceLoadPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = `${TINYMCE_CDN}tinymce.min.js`;
        s.referrerPolicy = 'origin';
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('TinyMCE failed to load'));
        document.head.appendChild(s);
      });
      return this._tinyMceLoadPromise;
    },

    normalizeEditorHeight(height, fallback = 360) {
      const raw = Number(height);
      if (!Number.isFinite(raw)) return fallback;
      return Math.max(90, Math.round(raw));
    },

    // --- Profile / customer ---
    validateProfileImageFile(file) {
      if (!file) return 'No file selected.';
      if (!PROFILE_IMAGE_TYPES.has(file.type)) {
        return 'Please choose a JPG, PNG, GIF, or WebP image.';
      }
      if (file.size > PROFILE_IMAGE_MAX_BYTES) {
        return 'Image must be 5 MB or smaller.';
      }
      return null;
    },

    shopName() {
      const el = document.querySelector('section.container[data-cf-auth]');
      const fromDataset = (el?.dataset?.shopDomain || '').trim();
      if (fromDataset) return fromDataset;
      return (
        (typeof window.Shopify !== 'undefined' && window.Shopify.shop) ||
        (typeof Shopify !== 'undefined' && Shopify.shop) ||
        'modl-design.myshopify.com'
      );
    },

    getForumRoot() {
      return document.querySelector('section.container[data-cf-auth]');
    },

    communityApiBase() {
      const raw = String(this.getForumRoot()?.dataset?.cfApiBase || '').trim();
      if (!raw || /^https?:\/\//i.test(raw)) return DEFAULT_API_BASE;
      return raw.replace(/\/+$/, '') || DEFAULT_API_BASE;
    },

    communityApiUrl(path) {
      const p = String(path || '');
      const suffix = p.startsWith('/') ? p : `/${p}`;
      return `${this.communityApiBase()}${suffix}`;
    },

    async communityCsrfToken() {
      if (this._csrfToken) return this._csrfToken;
      if (!this._csrfPromise) {
        this._csrfPromise = (async () => {
          try {
            const res = await fetch(this.communityApiUrl('/api/session'), {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            const data = await res.json().catch(() => null);
            const token = String(data?.csrf || data?.csrfToken || data?.csrf_token || '').trim();
            if (!res.ok || !token) {
              throw new Error(data?.error || data?.message || 'Could not start a community session.');
            }
            this._csrfToken = token;
            return token;
          } catch (err) {
            this._csrfPromise = null;
            throw err;
          }
        })();
      }
      return this._csrfPromise;
    },

    async communityFetch(path, options = {}) {
      const method = String(options.method || 'GET').toUpperCase();
      const headers = { Accept: 'application/json', ...(options.headers || {}) };
      if (method !== 'GET' && method !== 'HEAD' && window.ModlCommunityAPI?.request) {
        return window.ModlCommunityAPI.request(path, { ...options, method, headers });
      }
      if (method !== 'GET' && method !== 'HEAD') {
        headers['X-Modl-CSRF'] = await this.communityCsrfToken();
      }
      return fetch(this.communityApiUrl(path), {
        ...options,
        method,
        headers,
        credentials: 'same-origin',
      });
    },

    getArticlesPageSize() {
      const raw = parseInt(this.getForumRoot()?.dataset?.articlesPageSize, 10);
      return raw === 100 || raw === 250 ? raw : 250;
    },

    getCommentsInitialBatch() {
      return 10;
    },

    getCommentsBatchSize() {
      const raw = parseInt(this.getForumRoot()?.dataset?.commentsBatchSize, 10);
      return raw > 0 ? raw : 15;
    },

    isStorefrontDebug() {
      return Boolean(
        window.debug || /[?&]debug(?:=1)?(?:&|$)/.test(window.location.search)
      );
    },

    logStorefrontConsumed(consumer, data) {
      if (!this.isStorefrontDebug()) return;
      const payload = { consumer, ...(data && typeof data === 'object' ? data : { data }) };
      console.log('[Storefront]', payload);
    },

    customerNumericId(customerId) {
      if (customerId == null || customerId === '') return '';
      const id = String(customerId).trim();
      if (!id) return '';
      return id.replace(/^gid:\/\/shopify\/Customer\//i, '');
    },

    withCreateTopicAuthor(payload, customerId, profileImageUrl) {
      const numericCustomerId = this.customerNumericId(customerId);
      if (!numericCustomerId) return payload;

      const authorMetafield = {
        namespace: 'custom',
        key: 'author_id',
        type: 'single_line_text_field',
        value: numericCustomerId,
      };
      const existing = Array.isArray(payload.metafields) ? payload.metafields : [];
      const metadata = {
        ...(payload.metadata && typeof payload.metadata === 'object' ? payload.metadata : {}),
        author_id: numericCustomerId,
        customer_id: numericCustomerId,
      };
      const metafields = [...existing, authorMetafield];
      const imageUrl = profileImageUrl ? String(profileImageUrl).trim() : '';
      if (imageUrl) {
        metafields.push({
          namespace: 'custom',
          key: 'author_profile_image',
          type: 'single_line_text_field',
          value: imageUrl,
        });
        metadata.author_profile_image = imageUrl;
      }

      return {
        ...payload,
        customer_id: numericCustomerId,
        author_id: numericCustomerId,
        metafields,
        metadata,
      };
    },

    getSavedProfileImageUrl() {
      const el = document.getElementById('modl-cf-profile-initial-profile-image-url');
      const raw = el?.textContent?.trim();
      if (!raw) return '';
      try {
        const v = JSON.parse(raw);
        return typeof v === 'string' && v ? v : '';
      } catch {
        return '';
      }
    },

    textFieldValue(field) {
      if (field == null) return '';
      if (typeof field === 'string') return field.trim();
      if (typeof field === 'object' && field.value != null) {
        return String(field.value).trim();
      }
      return '';
    },

    apiArticleHandle(handle) {
      const h = String(handle || '').trim();
      if (!h) return '';
      const parts = h.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : h;
    },

    findCachedArticle(articlesByBlog, blogHandle, articleHandle) {
      if (!articlesByBlog || !blogHandle || !articleHandle) return null;
      const articles = articlesByBlog[blogHandle];
      if (!Array.isArray(articles)) return null;
      const slug = String(articleHandle).trim();
      const apiHandle = this.apiArticleHandle(slug);
      return (
        articles.find(
          (a) =>
            a?.handle === slug ||
            a?.article_handle === slug ||
            a?.handle === apiHandle ||
            a?.article_handle === apiHandle
        ) || null
      );
    },

    resolveAuthorProfile(article) {
      if (!article || typeof article !== 'object') return null;
      const profile = typeof article.author === 'object' ? article.author?.profile : null;
      return profile && typeof profile === 'object' ? profile : null;
    },

    hasAuthorProfile(article) {
      if (!article || typeof article !== 'object') return false;
      if (this.resolveAuthorProfile(article)) return true;
      if (this.textFieldValue(article.author_id)) return true;
      const mf = article.metafields || {};
      return Boolean(
        this.textFieldValue(mf.author_id ?? mf.custom?.author_id?.value ?? mf.custom?.author_id)
      );
    },

    isPseudoCommentAuthorName(name) {
      const raw = String(name || '').trim();
      return !raw || /^comment\s+\d+/i.test(raw);
    },

    resolveAuthorDisplayName(article) {
      if (!article || typeof article !== 'object') return '';
      const profile = this.resolveAuthorProfile(article);
      const hasProfile = this.hasAuthorProfile(article);

      const stamped = String(article.profile_name || '').trim();
      if (stamped) return stamped;

      if (profile) {
        const fromProfile = String(profile.profile_name ?? profile.profileName ?? '').trim();
        if (fromProfile) return fromProfile;
      }

      const topLevel = String(article.author_name || article.authorName || '').trim();
      if (topLevel && (!this.isPseudoCommentAuthorName(topLevel) || hasProfile)) {
        return topLevel;
      }

      if (typeof article.author === 'object' && article.author?.name) {
        const name = String(article.author.name).trim();
        if (name && (!this.isPseudoCommentAuthorName(name) || hasProfile)) return name;
      }

      const strAuthor = typeof article.author === 'string' ? article.author.trim() : '';
      if (strAuthor && (!this.isPseudoCommentAuthorName(strAuthor) || hasProfile)) return strAuthor;

      if (profile) {
        const shopifyDisplay = String(profile.displayName || '').trim();
        if (shopifyDisplay) return shopifyDisplay;
      }

      return '';
    },

    async fetchCustomerProfileName(customerId) {
      const id = this.customerNumericId(customerId);
      if (!id) return '';
      if (this._customerProfileNameValues.has(id)) {
        return this._customerProfileNameValues.get(id) || '';
      }
      if (this._customerProfileNameCache.has(id)) {
        return this._customerProfileNameCache.get(id);
      }

      const promise = (async () => {
        try {
          const params = new URLSearchParams({
            shop_name: this.shopName(),
            customer_id: id,
            keys: 'profile_name,profile_image,profile_bio',
          });
          const res = await this.communityFetch(`/api/customer-metafields?${params}`);
          const data = await res.json().catch(() => null);
          if (!res.ok) return '';
          const fields = Array.isArray(data?.metafields) ? data.metafields : [];
          const fieldValue = (key) => {
            const row = fields.find((m) => m && m.key === key);
            return row?.value != null ? String(row.value).trim() : '';
          };
          const value = fieldValue('profile_name');
          this._customerProfileNameValues.set(id, value);
          this._customerProfileImageValues.set(id, fieldValue('profile_image'));
          this._customerProfileBioValues.set(id, fieldValue('profile_bio'));
          return value;
        } catch (err) {
          console.warn('fetchCustomerProfileName failed:', id, err);
          this._customerProfileNameValues.set(id, '');
          this._customerProfileImageValues.set(id, '');
          this._customerProfileBioValues.set(id, '');
          return '';
        }
      })();

      this._customerProfileNameCache.set(id, promise);
      return promise;
    },

    stampAuthorProfileName(article, name) {
      if (!article || typeof article !== 'object') return;
      const trimmed = String(name || '').trim();
      if (!trimmed) return;
      article.profile_name = trimmed;
      if (article.author && typeof article.author === 'object') {
        if (!article.author.profile || typeof article.author.profile !== 'object') {
          article.author.profile = {};
        }
        article.author.profile.profile_name = trimmed;
      } else if (typeof article.author === 'string' || article.author_name != null) {
        article.author = trimmed;
        article.author_name = trimmed;
      }
    },

    async fetchCustomerProfileFields(customerId) {
      const id = this.customerNumericId(customerId);
      if (!id) return { name: '', image: '', bio: '' };
      await this.fetchCustomerProfileName(id);
      return {
        name: this._customerProfileNameValues.get(id) || '',
        image: this._customerProfileImageValues.get(id) || '',
        bio: this._customerProfileBioValues.get(id) || '',
      };
    },

    getSavedProfileBio() {
      const el = document.getElementById('modl-cf-profile-initial-bio');
      const raw = el?.textContent?.trim();
      if (!raw) return '';
      try {
        const v = JSON.parse(raw);
        return typeof v === 'string' && v ? v : '';
      } catch {
        return '';
      }
    },

    stampAuthorProfileImage(article, imageUrl) {
      if (!article || typeof article !== 'object') return;
      const trimmed = String(imageUrl || '').trim();
      if (!trimmed) return;
      article.author_profile_image = trimmed;
      if (article.author && typeof article.author === 'object') {
        if (!article.author.profile || typeof article.author.profile !== 'object') {
          article.author.profile = {};
        }
        article.author.profile.profile_image = trimmed;
      }
    },

    async hydrateArticlesAuthorProfileNames(articles) {
      const list = (articles || []).filter(Boolean);
      if (!list.length) return list;
      const ids = [
        ...new Set(list.map((article) => this.resolveAuthorId(article)).filter(Boolean)),
      ];
      await Promise.all(ids.map((id) => this.fetchCustomerProfileName(id)));
      list.forEach((article) => {
        const id = this.resolveAuthorId(article);
        if (!id) return;
        const name = this._customerProfileNameValues.get(id) || '';
        if (name) this.stampAuthorProfileName(article, name);
        const imageUrl = this._customerProfileImageValues.get(id) || '';
        if (imageUrl) this.stampAuthorProfileImage(article, imageUrl);
      });
      return list;
    },

    resolveAuthorProfileImageUrl(article) {
      if (!article || typeof article !== 'object') return '';
      const profile = this.resolveAuthorProfile(article);
      const fromProfile =
        this.textFieldValue(profile?.profile_image) || this.textFieldValue(profile?.profileImage);
      if (fromProfile) return fromProfile;
      const direct =
        article.author_profile_image ??
        article.authorProfileImage ??
        article.metafields?.author_profile_image ??
        article.metafields?.custom?.author_profile_image?.value ??
        article.metafields?.custom?.author_profile_image;
      return this.textFieldValue(direct);
    },

    resolveAuthorId(article) {
      if (!article || typeof article !== 'object') return '';
      const mf = article.metafields || {};
      const meta = article.metadata && typeof article.metadata === 'object' ? article.metadata : {};
      const fromMeta = this.textFieldValue(meta.author_id ?? meta.customer_id);
      if (fromMeta) return this.customerNumericId(fromMeta);
      const fromMf = this.textFieldValue(
        mf.author_id ?? mf.custom?.author_id?.value ?? mf.custom?.author_id
      );
      if (fromMf) return this.customerNumericId(fromMf);
      if (article.author_id != null && article.author_id !== '') {
        return this.customerNumericId(article.author_id);
      }
      return '';
    },

    resolveAuthorProfileBio(article) {
      if (!article || typeof article !== 'object') return '';
      const profile = this.resolveAuthorProfile(article);
      const fromProfile = this.textFieldValue(profile?.profile_bio ?? profile?.profileBio);
      if (fromProfile) return fromProfile;

      const mf = article.metafields || {};
      const candidates = [
        article.author_profile_bio,
        article.authorProfileBio,
        mf.author_profile_bio,
        mf.custom?.author_profile_bio?.value,
        mf.custom?.author_profile_bio,
        mf.profile_bio,
        mf.custom?.profile_bio?.value,
        mf.custom?.profile_bio,
      ];

      for (let i = 0; i < candidates.length; i++) {
        const value = this.textFieldValue(candidates[i]);
        if (value) return value;
      }
      return '';
    },

    createTopicErrorMessage(error) {
      const code = error?.code;
      const status = error?.status;
      if (code === 'TITLE_TAKEN' || status === 409) {
        return 'A topic with this title already exists. Choose a different title.';
      }
      const raw = String(error?.message || error || '').trim();
      if (!raw || /failed to fetch|networkerror|load failed/i.test(raw)) {
        return 'Could not reach the community service. Check your connection and try again.';
      }
      return raw;
    },

    getCommunityForum() {
      const el = document.querySelector('section.container[data-cf-auth]');
      if (!el || !window.Alpine) return null;
      return Alpine.$data(el);
    },

    restampCachedAuthor(customerId) {
      const id = this.customerNumericId(customerId);
      if (!id) return;
      const name = this._customerProfileNameValues.get(id) || '';
      const imageUrl = this._customerProfileImageValues.get(id) || '';
      const forum = this.getCommunityForum();
      const buckets = forum?.articlesByBlog || {};
      Object.keys(buckets).forEach((blogHandle) => {
        (buckets[blogHandle] || []).forEach((article) => {
          if (this.resolveAuthorId(article) !== id) return;
          if (name) this.stampAuthorProfileName(article, name);
          if (imageUrl) this.stampAuthorProfileImage(article, imageUrl);
        });
      });
      forum?.rebuildFlatArticlesFromCache?.();
    },

    primeCustomerProfile(customerId, { name, image, bio } = {}) {
      const id = this.customerNumericId(customerId);
      if (!id) return;
      if (typeof name === 'string') {
        const trimmed = name.trim();
        this._customerProfileNameValues.set(id, trimmed);
        this._customerProfileNameCache.set(id, Promise.resolve(trimmed));
      }
      if (typeof image === 'string') {
        this._customerProfileImageValues.set(id, image.trim());
      }
      if (typeof bio === 'string') {
        this._customerProfileBioValues.set(id, bio.trim());
      }
      this.restampCachedAuthor(id);
      void this.refreshServerRenderedAvatars(document);
    },

    async saveCustomerProfileImage(url, customerId) {
      if (!customerId) throw new Error('Not signed in.');
      if (!url) throw new Error('No image URL to save.');

      const res = await this.communityFetch('/api/customer-metafields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_name: this.shopName(),
          customer_id: String(customerId),
          namespace: 'custom',
          key: 'profile_image',
          value: url,
        }),
      });
      const data = (await res.json().catch(() => ({}))) || {};
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Could not save profile image.');
      }
      this.primeCustomerProfile(customerId, { image: url });
      return data;
    },

    async saveCustomerBioMetafield(bio, customerId) {
      if (!customerId) throw new Error('Not signed in.');

      const res = await this.communityFetch('/api/customer-metafields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_name: this.shopName(),
          customer_id: String(customerId),
          namespace: 'custom',
          key: 'profile_bio',
          type: 'multi_line_text_field',
          value: bio || '',
        }),
      });
      const data = (await res.json().catch(() => ({}))) || {};
      if (!res.ok) {
        console.error('saveCustomerBioMetafield failed:', res.status, data);
        throw new Error(data.error || data.message || 'Could not save bio.');
      }
      this.primeCustomerProfile(customerId, { bio: bio || '' });
      return data;
    },

    async saveCustomerDisplayNameMetafield(name, customerId) {
      if (!customerId) throw new Error('Not signed in.');
      const trimmed = String(name || '').trim();
      if (!trimmed) throw new Error('Username is required.');

      const res = await this.communityFetch('/api/customer-metafields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop_name: this.shopName(),
          customer_id: String(customerId),
          namespace: 'custom',
          key: 'profile_name',
          type: 'single_line_text_field',
          value: trimmed,
        }),
      });
      const data = (await res.json().catch(() => ({}))) || {};
      if (!res.ok) {
        console.error('saveCustomerDisplayNameMetafield failed:', res.status, data);
        if (data.code === 'NAME_TAKEN') {
          throw new Error('That username is taken.');
        }
        throw new Error(data.error || data.message || 'Could not save username.');
      }
      return data;
    },

    updateCommunityDisplayName(name, customerId) {
      const trimmed = String(name || '').trim();
      if (!trimmed) return;
      const id = this.customerNumericId(
        customerId ||
          document.querySelector('section.container[data-customer-id]')?.dataset?.customerId ||
          ''
      );
      if (id) {
        this._customerProfileNameValues.set(id, trimmed);
        this._customerProfileNameCache.set(id, Promise.resolve(trimmed));
        if (!this._customerProfileImageValues.has(id)) {
          this._customerProfileImageValues.set(id, this.getSavedProfileImageUrl() || '');
        }
        if (!this._customerProfileBioValues.has(id)) {
          this._customerProfileBioValues.set(id, this.getSavedProfileBio() || '');
        }
        this.restampCachedAuthor(id);
      }
      const primary = document.querySelector('.modl-cf__account-identity-primary');
      if (primary) primary.textContent = trimmed;
      const letter = this.authorAvatarLetters(trimmed);
      document.querySelectorAll('[data-cf-avatar-letter]').forEach((el) => {
        el.textContent = letter;
      });
      document.querySelectorAll('[data-customer-name]').forEach((el) => {
        el.dataset.customerName = trimmed;
      });
      document.querySelectorAll('[name="comment[author]"]').forEach((el) => {
        el.value = trimmed;
      });
      document.querySelectorAll('[data-customer]').forEach((el) => {
        try {
          const parsed = JSON.parse(el.dataset.customer || '{}');
          if (!parsed || typeof parsed !== 'object') return;
          parsed.name = trimmed;
          el.dataset.customer = JSON.stringify(parsed);
        } catch {
          /* noop */
        }
      });
      if (id) {
        const stamp = (article) => {
          if (!article) return;
          if (this.resolveAuthorId(article) !== id) return;
          this.stampAuthorProfileName(article, trimmed);
        };
        const forum = this.getCommunityForum();
        (forum?.flatArticles || []).forEach(stamp);
        (forum?.searchArticles || []).forEach(stamp);
        (forum?.searchComments || []).forEach(stamp);
        Object.values(forum?.articlesByBlog || {}).forEach((arr) => {
          (arr || []).forEach(stamp);
        });
        const commentsList = this.getCommentsListComponent();
        (commentsList?.comments || []).forEach((comment) => {
          stamp(comment);
          (comment.replies || []).forEach(stamp);
        });
      }
      document.dispatchEvent(
        new CustomEvent('cf-display-name-updated', { detail: { name: trimmed } })
      );
    },

    // --- Article slug & paths ---
    normalizeArticleSlug(blogHandle, articleHandle) {
      if (articleHandle == null || articleHandle === '') return '';
      let h = String(articleHandle).trim();
      if (!h) return '';
      const bh = blogHandle ? String(blogHandle).trim() : '';

      try {
        if (/^https?:\/\//i.test(h) || h.startsWith('//')) {
          h = new URL(h, window.location.origin).pathname;
        }
      } catch {
        /* keep h */
      }

      let parts = h.split('/').filter(Boolean);

      if (parts.length >= 3 && parts[0] === 'blogs' && bh && parts[1] === bh) {
        return parts.slice(2).join('/') || '';
      }

      while (bh && parts.length && parts[0] === bh) {
        parts.shift();
      }

      return parts.join('/') || h;
    },

    encodeArticlePathSegments(slug) {
      if (!slug) return '';
      return slug
        .split('/')
        .filter(Boolean)
        .map((s) => encodeURIComponent(s))
        .join('/');
    },

    articlePath(blogPaths, blogHandle, articleHandle) {
      if (!blogPaths || !blogHandle || !articleHandle) return '';
      const base = blogPaths[blogHandle];
      if (!base) return '';
      try {
        const slug = this.normalizeArticleSlug(blogHandle, articleHandle);
        if (!slug) return '';
        const baseU = new URL(base, window.location.origin);
        const encoded = this.encodeArticlePathSegments(slug);
        return `${baseU.pathname.replace(/\/$/, '')}/${encoded}`;
      } catch {
        return '';
      }
    },

    // --- Comment formatting & HTML ---
    formatAuthorDisplay(name, options = {}) {
      const raw = String(name || '').trim();
      if (!raw) return 'Member';
      if (!options.allowPseudo && /^comment\s+\d+/i.test(raw)) return 'Member';
      const parts = raw.split(/\s+/).filter(Boolean);
      if (parts.length <= 1) return parts[0] || 'Member';
      const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${parts[0]} ${lastInitial}.`;
    },

    formatCommentAuthorDisplay(name, article) {
      const raw = String(name || '').trim();
      if (!raw) return 'Member';
      const profile = this.resolveAuthorProfile(article);
      const profileName = String(
        article?.profile_name || profile?.profile_name || profile?.profileName || ''
      ).trim();
      if (profileName) return profileName;
      const allowPseudo = this.hasAuthorProfile(article);
      if (!allowPseudo && this.isPseudoCommentAuthorName(raw)) return 'Member';
      return this.formatAuthorDisplay(raw, { allowPseudo });
    },

    authorAvatarLetters(name) {
      const raw = String(name || '').trim();
      if (!raw || /^comment\s+\d+/i.test(raw)) return 'M';
      const parts = raw.split(/\s+/).filter(Boolean);
      if (!parts.length) return 'M';
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    formatCommentDateBadge(date = new Date()) {
      return window.ModlForumPresentation.timestamp(date);
    },

    escapeText(value) {
      const el = document.createElement('div');
      el.textContent = value || '';
      return el.innerHTML;
    },

    commentIndexFromHandle(handle) {
      const m = String(handle || '').match(/^comment-(\d+)-/i);
      return m ? m[1] : '';
    },

    parseCommentHandles(listEl) {
      const script = listEl?.querySelector('script[data-modl-comment-handles]');
      const raw = script?.textContent?.trim();
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.map((item) => this.normalizeCommentHandleItem(item)).filter((item) => item.handle);
      } catch {
        return [];
      }
    },

    normalizeCommentHandleItem(item) {
      if (!item || typeof item !== 'object') return { handle: '', href: '', index: undefined };
      return {
        handle: String(item.handle || '').trim(),
        href: item.href || '',
        index: item.index,
      };
    },

    resolveParentCommentHandle(article) {
      if (!article || typeof article !== 'object') return '';
      const mf = article.metafields?.custom ?? article.metafields ?? {};
      return String(
        article.parent_comment_handle ??
          mf.parent_comment_handle?.value ??
          mf.parent_comment_handle ??
          ''
      ).trim();
    },

    isNestedReplyArticle(article) {
      if (!article || typeof article !== 'object') return false;
      if (this.resolveParentCommentHandle(article)) return true;
      const handle = String(article.handle || '');
      if (/^reply-\d+-on-comment-/i.test(handle)) return true;
      const mf = article.metafields?.custom ?? article.metafields ?? {};
      const parentBlog = String(
        article.parent_blog_handle ??
          mf.parent_blog_handle?.value ??
          mf.parent_blog_handle ??
          ''
      ).trim();
      const parentArticle = String(
        article.parent_article_handle ??
          mf.parent_article_handle?.value ??
          mf.parent_article_handle ??
          ''
      ).trim();
      return parentBlog === this.COMMENTS_BLOG && /^comment-/i.test(parentArticle);
    },

    nestedReplySlotFromHandle(handle) {
      const match = String(handle || '').match(/^reply-(\d+)-/i);
      return match ? Number(match[1]) : 0;
    },

    dedupeSearchCommentItems(items) {
      const map = new Map();
      (items || []).forEach((item) => {
        if (!item) return;
        const key =
          item.comment_handle ||
          `${item.parent_blog_handle}-${item.parent_article_handle}-${item.comment_index}-${Boolean(item.is_nested_reply)}-${item.nested_reply_slot || ''}`;
        const prev = map.get(key);
        if (
          !prev ||
          new Date(item.publishedAt || 0).getTime() > new Date(prev.publishedAt || 0).getTime()
        ) {
          map.set(key, item);
        }
      });
      return [...map.values()];
    },

    async loadStorefrontNestedSearchComments(flatArticles = []) {
      if (!this.getStorefrontToken()) return [];
      const limit = Math.max(this.getArticlesPageSize(), 50);
      const data = await this.getArticles(this.COMMENTS_BLOG, { limit });
      const articles = Array.isArray(data?.articles) ? data.articles : [];
      this.buildTopicCommentCounts(articles);
      const nested = articles.filter((article) => this.isNestedReplyArticle(article));
      if (!nested.length) return [];

      const commentByHandle = new Map();
      articles.forEach((article) => {
        if (article?.handle) commentByHandle.set(article.handle, article);
      });

      const missingParentHandles = [
        ...new Set(
          nested
            .map((reply) => {
              const mf = reply.metafields?.custom ?? reply.metafields ?? {};
              return (
                this.resolveParentCommentHandle(reply) ||
                String(
                  reply.parent_article_handle ??
                    mf.parent_article_handle?.value ??
                    mf.parent_article_handle ??
                    ''
                ).trim()
              );
            })
            .filter((handle) => handle && !commentByHandle.has(handle))
        ),
      ];

      if (missingParentHandles.length) {
        const { map } = await this.fetchArticlesByHandles(this.COMMENTS_BLOG, missingParentHandles);
        map.forEach((article, handle) => {
          if (article) commentByHandle.set(handle, article);
        });
      }

      await this.hydrateArticlesAuthorProfileNames(nested);
      return nested
        .map((article) => this.normalizeSearchCommentItem(article, flatArticles, commentByHandle))
        .filter(Boolean);
    },

    extractCommentUrlHandles(article) {
      return this.extractCommentHandlesFromUrls(this.resolveCommentsMetafieldList(article));
    },

    parseCommentScrollTarget(raw) {
      const value = String(raw || '').trim();
      if (!value) return null;
      const normalized = value.startsWith('#') ? value : `#modl-cf-comment-${value}`;
      const match = normalized.match(/^#modl-cf-comment-(\d+)(?:-reply(?:-(\d+))?)?$/);
      if (!match) return null;
      const replySlot = match[2] ? Number(match[2]) : normalized.includes('-reply') ? 1 : 0;
      return {
        index: match[1],
        isReply: replySlot > 0,
        replySlot,
      };
    },

    commentScrollElementId(parsed) {
      if (!parsed?.index) return '';
      if (parsed.isReply) {
        const slot = parsed.replySlot > 0 ? parsed.replySlot : 1;
        return `modl-cf-comment-${parsed.index}-reply-${slot}`;
      }
      return `modl-cf-comment-${parsed.index}`;
    },

    formatCommentScrollToken(parsed) {
      if (!parsed?.index) return '';
      if (parsed.isReply && parsed.replySlot > 1) {
        return `${parsed.index}-reply-${parsed.replySlot}`;
      }
      if (parsed.isReply) {
        return `${parsed.index}-reply`;
      }
      return parsed.index;
    },

    getCommentsListComponent() {
      const el = document.querySelector('.modl-cf__comments-list');
      if (!el || !window.Alpine) return null;
      return Alpine.$data(el);
    },

    getStorefrontToken() {
      const el = this.getForumRoot();
      return (el?.dataset?.storefrontToken || '').trim();
    },

    requireStorefrontToken() {
      const token = this.getStorefrontToken();
      if (!token) {
        throw new Error('Storefront API token not configured');
      }
      return token;
    },

    STOREFRONT_API_VERSION: '2024-10',
    STOREFRONT_ARTICLE_CHUNK: 15,
    COMMENTS_BLOG: 'cs-comments',

    STOREFRONT_ARTICLE_METAFIELD_IDENTIFIERS: `
      metafields(identifiers: [
        {namespace: "custom", key: "comments"},
        {namespace: "custom", key: "thread_replies_count"},
        {namespace: "custom", key: "thread_posts_count"},
        {namespace: "custom", key: "author_id"},
        {namespace: "custom", key: "author_profile_image"},
        {namespace: "custom", key: "parent_blog_handle"},
        {namespace: "custom", key: "parent_article_handle"},
        {namespace: "custom", key: "parent_comment_handle"},
        {namespace: "custom", key: "comment_index"},
        {namespace: "custom", key: "nested_reply_slot"},
        {namespace: "custom", key: "original_comment_url"}
      ]) {
        namespace
        key
        value
        type
      }
    `,

    STOREFRONT_TAGS_FIELD: `
      tagsMeta: metafield(namespace: "custom", key: "tags") {
        value
        references(first: 10) {
          nodes {
            ... on Metaobject {
              id
              handle
              fields { key value }
            }
          }
        }
      }
    `,

    get STOREFRONT_ARTICLE_FIELDS() {
      return `
        id
        handle
        title
        publishedAt
        contentHtml
        excerptHtml
        author { name }
        ${this.STOREFRONT_ARTICLE_METAFIELD_IDENTIFIERS}
        ${this.STOREFRONT_TAGS_FIELD}
      `;
    },

    resolveArticleNumericId(article) {
      if (!article || typeof article !== 'object') return '';
      const raw =
        article.id ?? article.article_id ?? article.admin_graphql_api_id ?? '';
      if (raw == null || raw === '') return '';
      const match = String(raw).match(/(\d+)(?!.*\d)/);
      return match ? match[1] : '';
    },

    resolveArticleGid(article) {
      const numericId = this.resolveArticleNumericId(article);
      if (!numericId) return '';
      const raw = article?.admin_graphql_api_id ?? article?.id ?? '';
      if (typeof raw === 'string' && raw.includes('gid://')) return raw;
      return `gid://shopify/Article/${numericId}`;
    },

    parseStorefrontMetafieldValue(value, type) {
      if (value == null) return value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (
          type === 'json' ||
          (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
          (trimmed.startsWith('{') && trimmed.endsWith('}'))
        ) {
          try {
            return JSON.parse(trimmed);
          } catch {
            return value;
          }
        }
      }
      return value;
    },

    resolveCommentsMetafieldList(article) {
      if (!article || typeof article !== 'object') return [];
      const mf = article.metafields || {};
      let raw =
        mf.comments ??
        mf.custom?.comments?.value ??
        mf.custom?.comments ??
        article.comments ??
        null;
      if (raw && typeof raw === 'object' && !Array.isArray(raw) && raw.value != null) {
        raw = raw.value;
      }
      if (typeof raw === 'string') {
        raw = this.parseStorefrontMetafieldValue(raw, 'json');
      }
      if (!Array.isArray(raw)) return [];
      return raw;
    },

    countCommentUrlsInList(list) {
      return this.extractCommentHandlesFromUrls(list).length;
    },

    parseCommentsMetafield(value) {
      if (value == null || value === '') return [];
      let raw = value;
      if (typeof raw === 'object' && !Array.isArray(raw) && raw.value != null) {
        raw = raw.value;
      }
      if (typeof raw === 'string') {
        try {
          raw = JSON.parse(raw);
        } catch {
          return [];
        }
      }
      if (!Array.isArray(raw)) return [];
      return raw.map((e) => (e && e.url ? e.url : e)).filter(Boolean);
    },

    parseThreadCountMetafield(article, key) {
      if (!article || typeof article !== 'object') return null;
      const mf = article.metafields || {};
      const raw =
        article[key] ??
        mf[key] ??
        mf.custom?.[key]?.value ??
        mf.custom?.[key];
      if (raw == null || raw === '') return null;
      const n = parseInt(raw, 10);
      return Number.isNaN(n) ? null : n;
    },

    topicRepliesCount(article) {
      const n = this.parseThreadCountMetafield(article, 'thread_replies_count');
      if (n != null) return n;
      const commentsRaw =
        article?.comments?.value ??
        article?.metafields?.comments ??
        article?.metafields?.custom?.comments?.value ??
        article?.metafields?.custom?.comments;
      return this.parseCommentsMetafield(commentsRaw).length;
    },

    topicPostsCount(article) {
      const saved = window.Alpine?.store('forumCounts');
      if (saved?.enabled) return saved.topic(article?.blog_handle || article?.blogHandle, article?.article_handle || article?.handle)?.posts ?? '…';
      // A post is the opening message plus every comment on it.
      if (this._topicCommentCountsReady) {
        return this.topicCommentCount(article) + 1;
      }
      const n = this.parseThreadCountMetafield(article, 'thread_posts_count');
      if (n != null) return n;
      return this.topicRepliesCount(article) + 1;
    },

    mapStorefrontMetafields(metafieldsArray) {
      const custom = {};
      const root = { custom };
      (metafieldsArray || []).filter(Boolean).forEach((field) => {
        const parsed = this.parseStorefrontMetafieldValue(field.value, field.type);
        if (field.namespace === 'custom') {
          custom[field.key] = { value: parsed };
          root[field.key] = parsed;
        }
      });
      return root;
    },

    mapStorefrontTagsFromReferences(referencesNodes) {
      const nodes = Array.isArray(referencesNodes) ? referencesNodes : [];
      return nodes
        .map((node) => {
          if (!node || !Array.isArray(node.fields)) return null;
          const fieldMap = {};
          node.fields.forEach((f) => {
            if (f?.key) fieldMap[f.key] = f.value;
          });
          const name = String(fieldMap.name || node.handle || '').trim();
          if (!name) return null;
          return {
            name,
            handle: node.handle || '',
            tag_background: fieldMap.tag_background || '#22c55e',
            tag_color: fieldMap.tag_color || '#fff',
          };
        })
        .filter(Boolean);
    },

    mapStorefrontArticle(node) {
      if (!node) return null;
      const metafields = this.mapStorefrontMetafields(node.metafields);
      const custom = metafields.custom || {};
      const tagRefs = node.tagsMeta?.references?.nodes;
      const tagsFromRefs = this.mapStorefrontTagsFromReferences(tagRefs);
      if (tagsFromRefs.length) {
        custom.tags = { value: tagsFromRefs };
        metafields.tags = tagsFromRefs;
      }
      const parentCommentHandle = this.textFieldValue(custom.parent_comment_handle);
      const parentBlogHandle = this.textFieldValue(custom.parent_blog_handle);
      const parentArticleHandle = this.textFieldValue(custom.parent_article_handle);
      const commentIndex = this.textFieldValue(custom.comment_index);
      const nestedReplySlot = this.textFieldValue(custom.nested_reply_slot);
      const authorId = this.textFieldValue(custom.author_id);
      const authorProfileImage = this.textFieldValue(custom.author_profile_image);
      const authorName = node.author?.name ? String(node.author.name).trim() : '';
      const articleGid = node.id ? String(node.id) : '';
      const articleId = articleGid.match(/(\d+)(?!.*\d)/)?.[1] || '';
      return {
        id: articleId || articleGid,
        article_id: articleId,
        admin_graphql_api_id: articleGid || (articleId ? `gid://shopify/Article/${articleId}` : ''),
        handle: node.handle,
        title: node.title || '',
        publishedAt: node.publishedAt,
        published_at: node.publishedAt,
        content: node.contentHtml || '',
        excerpt: node.excerptHtml || '',
        summary: node.excerptHtml || '',
        author: authorName ? { name: authorName } : node.author,
        author_name: authorName,
        author_id: authorId,
        author_profile_image: authorProfileImage,
        metafields,
        parent_comment_handle: parentCommentHandle,
        parent_blog_handle: parentBlogHandle,
        parent_article_handle: parentArticleHandle,
        comment_index: commentIndex,
        nested_reply_slot: nestedReplySlot,
        tags: tagsFromRefs,
      };
    },

    extractCommentHandlesFromUrls(urlList) {
      const list = Array.isArray(urlList) ? urlList : [];
      return list
        .map((entry) => {
          const url = entry?.url ?? entry?.value ?? entry;
          return url ? String(url).split('/').pop() : '';
        })
        .filter(Boolean);
    },

    async shopifyStorefrontQuery(query, variables, options = {}) {
      this.requireStorefrontToken();
      const shop = this.shopName();
      const token = this.getStorefrontToken();
      const res = await fetch(`https://${shop}/api/${this.STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables }),
        ...(options.signal ? {signal: options.signal, cache: 'no-store'} : {}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
      }
      if (Array.isArray(json.errors) && json.errors.length) {
        throw new Error(json.errors.map((e) => e.message).join('; '));
      }
      return json.data;
    },

    commentLinksEnabled() {
      return this.getForumRoot()?.dataset?.commentLinksEnabled === 'true';
    },

    _commentLinks: new Map(),
    _commentRequests: new WeakMap(),
    commentRequest(form, parent, body) {
      const prior = this._commentRequests.get(form);
      if (prior?.parent === parent && prior.body === body) return prior.id;
      const id = crypto.randomUUID();
      this._commentRequests.set(form, {parent, body, id});
      return id;
    },

    async fetchDatabaseCommentReferences(blog, handles, signal) {
      const result = new Map();
      const read = async params => {
        const response = await this.communityFetch('/api/comment-links?' + new URLSearchParams(params), {signal, cache:'no-store'});
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Comments are temporarily unavailable.');
        return data;
      };
      for (let offset = 0; offset < handles.length; offset += 50) {
        const chunk = handles.slice(offset, offset + 50);
        const data = await read({blog, handles:chunk.join(',')});
        if (!Array.isArray(data.parents) || data.parents.length !== chunk.length) throw new Error('Incomplete comment references.');
        await Promise.all(data.parents.map(async page => {
          const parent = page.handle, items = [], seen = new Set();
          while (true) {
            if (!Array.isArray(page.items)) throw new Error('Invalid comment page.');
            items.push(...page.items);
            if (page.next == null) break;
            if (!Number.isSafeInteger(page.next) || page.next <= 0 || seen.has(page.next)) throw new Error('Repeated comment page.');
            seen.add(page.next);
            page = await read({blog, handle:parent, after:page.next});
          }
          items.forEach(item => this._commentLinks.set(item.handle, {...item, parentBlog:blog}));
          result.set(parent, items.map(item => item.handle));
        }));
      }
      return result;
    },

    // Poll references only; cached article bodies cannot reveal newly attached replies.
    async fetchCommentReferences(blogHandle, handles, signal) {
      const unique = [...new Set(handles.map(handle => this.apiArticleHandle(handle)).filter(Boolean))];
      if (this.commentLinksEnabled()) return this.fetchDatabaseCommentReferences(blogHandle, unique, signal);
      const result = new Map();
      for (let offset = 0; offset < unique.length; offset += 50) {
        const chunk = unique.slice(offset, offset + 50);
        const fields = chunk.map((handle, i) => `a${i}: articleByHandle(handle: ${JSON.stringify(handle)}) { metafield(namespace:"custom",key:"comments") { value } }`).join('\n');
        const data = await this.shopifyStorefrontQuery(`query CommentReferences($blog:String!) { blog(handle:$blog) { ${fields} } }`, {blog:blogHandle}, {signal});
        if (!data?.blog) throw new Error('Comment references unavailable.');
        chunk.forEach((handle, i) => {
          const article = data.blog[`a${i}`];
          if (!article) return;
          const urls = JSON.parse(article.metafield?.value || '[]');
          result.set(handle, this.extractCommentHandlesFromUrls(urls));
        });
      }
      return result;
    },

    async fetchArticleByHandle(blogHandle, articleHandle) {
      const handle = this.apiArticleHandle(articleHandle);
      const query = `query ArticleByHandle($blog: String!, $handle: String!) {
        blog(handle: $blog) {
          articleByHandle(handle: $handle) {
            ${this.STOREFRONT_ARTICLE_FIELDS}
          }
        }
      }`;
      const data = await this.shopifyStorefrontQuery(query, { blog: blogHandle, handle });
      return this.mapStorefrontArticle(data?.blog?.articleByHandle);
    },

    async fetchArticlesByHandles(blogHandle, handles, options = {}) {
      const unique = [
        ...new Set((handles || []).map((h) => this.apiArticleHandle(h)).filter(Boolean)),
      ];
      const resultMap = new Map();
      if (!unique.length) return { map: resultMap, source: 'cache' };

      this.requireStorefrontToken();

      const pending = [];
      unique.forEach((handle) => {
        const key = `${blogHandle}:${handle}`;
        if (this._articleApiCache.has(key)) {
          pending.push(
            this._articleApiCache.get(key).then((data) => {
              const article = data?.article ?? data;
              if (article && typeof article === 'object') {
                resultMap.set(handle, article);
              }
            })
          );
        }
      });
      await Promise.allSettled(pending);

      const toFetch = unique.filter((handle) => !resultMap.has(handle));
      if (!toFetch.length) return { map: resultMap, source: 'cache' };

      const chunkSize = this.STOREFRONT_ARTICLE_CHUNK;
      for (let i = 0; i < toFetch.length; i += chunkSize) {
        const chunk = toFetch.slice(i, i + chunkSize);
        const aliases = chunk
          .map(
            (handle, idx) =>
              `a${idx}: articleByHandle(handle: ${JSON.stringify(handle)}) { ${this.STOREFRONT_ARTICLE_FIELDS} }`
          )
          .join('\n');
        const query = `query StorefrontArticlesByHandle($blog: String!) {
          blog(handle: $blog) {
            ${aliases}
          }
        }`;

        const data = await this.shopifyStorefrontQuery(query, { blog: blogHandle }, options);
        const blog = data?.blog;
        chunk.forEach((handle, idx) => {
          const article = this.mapStorefrontArticle(blog?.[`a${idx}`]);
          const key = `${blogHandle}:${handle}`;
          if (article) {
            this._articleApiCache.set(key, Promise.resolve({ article }));
            resultMap.set(handle, article);
          } else if (typeof window.debugLog === 'function') {
            window.debugLog('[Storefront]', { handle, missing: true });
          }
        });
      }

      return { map: resultMap, source: 'storefront' };
    },

    // --- Community API ---
    async fetchArticle(blogHandle, articleHandle) {
      const apiHandle = this.apiArticleHandle(articleHandle);
      const key = `${blogHandle}:${apiHandle}`;
      if (this._articleApiCache.has(key)) {
        return this._articleApiCache.get(key);
      }
      const promise = (async () => {
        const { map } = await this.fetchArticlesByHandles(blogHandle, [apiHandle]);
        const article = map.get(apiHandle);
        if (article) return { article };
        throw new Error(`Article not found: ${blogHandle}/${apiHandle}`);
      })();
      this._articleApiCache.set(key, promise);
      return promise;
    },

    invalidateArticleCache(blogHandle, handle) {
      this._articleApiCache.delete(`${blogHandle}:${this.apiArticleHandle(handle)}`);
    },

    async fetchCommentArticle(blogHandle, articleHandle) {
      try {
        const apiHandle = this.apiArticleHandle(articleHandle);
        const { map } = await this.fetchArticlesByHandles(blogHandle, [apiHandle]);
        return map.get(apiHandle) || null;
      } catch (err) {
        console.warn('fetchCommentArticle:', articleHandle, err);
        return null;
      }
    },

    async fetchCommentsByParent(blogHandle, articleHandle, options = {}) {
      const onBatch = typeof options.onBatch === 'function' ? options.onBatch : null;
      const commentsBlog = options.commentsBlog || this.COMMENTS_BLOG;
      let handleItems = [];

      try {
        const parent = await this.fetchArticleByHandle(blogHandle, articleHandle);
        const mf = parent?.metafields?.custom ?? parent?.metafields ?? {};
        const commentsList = mf.comments?.value ?? mf.comments ?? [];
        const urls = Array.isArray(commentsList) ? commentsList : [];
        handleItems = this.extractCommentHandlesFromUrls(urls).map((handle) => ({
          handle,
          index: this.commentIndexFromHandle(handle),
        }));
      } catch (err) {
        if (typeof window.debugLog === 'function') {
          window.debugLog('[Storefront] fetchCommentsByParent parent', err);
        }
      }

      if (!handleItems.length) {
        const pageSize = this.STOREFRONT_ARTICLE_CHUNK;
        let after = null;
        let hasNext = true;
        const matched = [];
        while (hasNext) {
          const data = await this.getArticles(commentsBlog, { limit: pageSize, after });
          const articles = Array.isArray(data?.articles) ? data.articles : [];
          articles.forEach((article) => {
            const pb = String(article.parent_blog_handle || '').trim();
            const pa = String(article.parent_article_handle || '').trim();
            const pch = this.resolveParentCommentHandle(article);
            if (pb === blogHandle && pa === articleHandle && !pch) {
              matched.push({
                handle: article.handle,
                index: article.comment_index || this.commentIndexFromHandle(article.handle),
                article,
              });
            }
          });
          hasNext = Boolean(data?.pageInfo?.hasNextPage && data?.pageInfo?.endCursor);
          after = hasNext ? data.pageInfo.endCursor : null;
        }
        handleItems = matched.map((m) => ({ handle: m.handle, index: m.index, article: m.article }));
        if (onBatch && matched.length) {
          onBatch(matched.map((m) => m.article).filter(Boolean));
        }
        return { handleItems, articles: matched.map((m) => m.article).filter(Boolean) };
      }

      return { handleItems, articles: [] };
    },

    async fetchCurrentCustomerProfile(customerAccessToken) {
      if (!customerAccessToken) return null;
      const query = `query CustomerMetafields($customerAccessToken: String!) {
        customer(customerAccessToken: $customerAccessToken) {
          id
          firstName
          lastName
          displayName
          profileBio: metafield(namespace: "custom", key: "profile_bio") { value }
          profileImage: metafield(namespace: "custom", key: "profile_image") { value }
          bio: metafield(namespace: "custom", key: "bio") { value }
          avatar: metafield(namespace: "custom", key: "avatar") {
            value
            reference {
              ... on MediaImage {
                image { url }
              }
            }
          }
          profileName: metafield(namespace: "custom", key: "profile_name") { value }
        }
      }`;
      const data = await this.shopifyStorefrontQuery(query, { customerAccessToken });
      return data?.customer || null;
    },

    sortCommentsByIndex(comments) {
      return [...(comments || [])].sort((a, b) => {
        const ia = Number(a._commentIndex || a.comment_index || this.commentIndexFromHandle(a.handle) || 0);
        const ib = Number(b._commentIndex || b.comment_index || this.commentIndexFromHandle(b.handle) || 0);
        return ia - ib;
      });
    },

    buildCommentsFromBatch(handleItems, topMap, legacyReplies, replyMap) {
      const fetched = (handleItems || []).map((item) => {
        const handle = item?.handle;
        if (!handle) return null;
        const apiHandle = this.apiArticleHandle(handle);
        const article = topMap.get(apiHandle) || topMap.get(handle) || item.article;
        if (!article) return null;
        return {
          ...article,
          _commentIndex: (this.commentLinksEnabled() && this._commentLinks.get(apiHandle)?.comment_index) || item.index || this.commentIndexFromHandle(handle),
        };
      });
      const topRaw = fetched.filter(Boolean);
      const legacyList =
        legacyReplies != null
          ? legacyReplies
          : topRaw.filter((a) => this.resolveParentCommentHandle(a));
      const topLevel = topRaw.filter((a) => !this.resolveParentCommentHandle(a));

      return topLevel.map((article) => {
        const handles = this.extractCommentUrlHandles(article);
        const fetchedReplies = handles
          .map((h) => {
            const apiHandle = this.apiArticleHandle(h);
            const reply = replyMap.get(apiHandle) || replyMap.get(h);
            const link = this.commentLinksEnabled() && this._commentLinks.get(apiHandle);
            return reply && link ? {...reply, _nestedReplySlot:link.position} : reply;
          })
          .filter(Boolean);
        const legacyForParent = legacyList.filter(
          (r) => this.resolveParentCommentHandle(r) === article.handle
        );
        const replies = [...fetchedReplies, ...legacyForParent]
          .filter((r, i, arr) => arr.findIndex((x) => x.handle === r.handle) === i)
          .sort((a, b) => {
            const ta = Date.parse(a.publishedAt ?? a.published_at ?? '') || 0;
            const tb = Date.parse(b.publishedAt ?? b.published_at ?? '') || 0;
            return ta - tb;
          });
        return { ...article, replies };
      });
    },

    mergeCommentLists(existing, incoming) {
      const map = new Map();
      (existing || []).forEach((c) => {
        if (c?.handle) map.set(c.handle, c);
      });
      (incoming || []).forEach((c) => {
        if (!c?.handle) return;
        const prev = map.get(c.handle);
        map.set(c.handle, prev ? { ...prev, ...c, replies: c.replies?.length ? c.replies : prev.replies } : c);
      });
      return this.sortCommentsByIndex([...map.values()]);
    },

    prioritizeCommentHandleItems(handleItems, scrollIndex, sort = 'oldest') {
      const items = [...(handleItems || [])];
      if (sort === 'newest') items.sort((a, b) =>
        Number(b.index || this.commentIndexFromHandle(b.handle)) - Number(a.index || this.commentIndexFromHandle(a.handle)));
      if (!scrollIndex) return items;
      const target = String(this.parseCommentScrollTarget(scrollIndex)?.index || scrollIndex);
      const idx = items.findIndex(
        (item) =>
          String(item?.index || this.commentIndexFromHandle(item?.handle)) === target
      );
      if (idx <= 0) return items;
      const [promoted] = items.splice(idx, 1);
      items.unshift(promoted);
      return items;
    },

    async resolveArticleForProfile(blogHandle, articleHandle, articlesByBlog = null, cachedArticle = null) {
      const hasImage = (a) => Boolean(this.resolveAuthorProfileImageUrl(a));
      const hasName = (a) => {
        const name = this.resolveAuthorDisplayName(a);
        return Boolean(name) && (!this.isPseudoCommentAuthorName(name) || this.hasAuthorProfile(a));
      };
      const cached =
        cachedArticle ||
        (articlesByBlog ? this.findCachedArticle(articlesByBlog, blogHandle, articleHandle) : null);

      if (cached && this.resolveAuthorId(cached) && (hasImage(cached) || hasName(cached))) {
        return cached;
      }

      try {
        const data = await this.fetchArticle(blogHandle, articleHandle);
        const fetched = data?.article ?? data;
        if (fetched && typeof fetched === 'object') {
          return fetched;
        }
      } catch (err) {
        console.warn('resolveArticleForProfile:', articleHandle, err);
      }

      return cached || null;
    },

    async refreshServerRenderedAvatars(container) {
      const root = container || document;
      const nodes = Array.from(root.querySelectorAll('[data-cf-avatar-author]'));
      if (!nodes.length) return;
      nodes.forEach(el => {
        if (el.tagName !== 'IMG') Object.assign(el.style, window.ModlForumPresentation.avatarStyle(el.dataset.cfAvatarAuthor, el.textContent));
      });
      const ids = [
        ...new Set(
          nodes.map((el) => this.customerNumericId(el.dataset.cfAvatarAuthor)).filter(Boolean)
        ),
      ];
      if (!ids.length) return;
      await Promise.all(ids.map((id) => this.fetchCustomerProfileFields(id)));
      nodes.forEach((el) => {
        const id = this.customerNumericId(el.dataset.cfAvatarAuthor);
        if (!id) return;
        const url = this._customerProfileImageValues.get(id) || '';
        if (!url) return;
        if (el.tagName === 'IMG') {
          if (el.getAttribute('src') !== url) el.setAttribute('src', url);
          return;
        }
        const img = document.createElement('img');
        img.className = el.className;
        img.setAttribute('src', url);
        img.setAttribute('alt', '');
        img.setAttribute('width', '36');
        img.setAttribute('height', '36');
        img.setAttribute('loading', 'lazy');
        img.dataset.cfAvatarAuthor = el.dataset.cfAvatarAuthor;
        el.replaceWith(img);
      });
    },

    async hydrateArticleAuthorProfile(container, blogHandle, articleHandle, cachedArticle = null) {
      if (!container || !blogHandle || !articleHandle) return;

      const chipHost = container.querySelector('[data-modl-author-chip-host]');
      if (!chipHost || !window.Alpine) return;

      const chip = Alpine.$data(chipHost);
      if (chip?.loadTopicAuthor) {
        await chip.loadTopicAuthor(blogHandle, articleHandle, cachedArticle);
      }
    },

    _mapStorefrontArticlesConnection(connection) {
      const edges = Array.isArray(connection?.edges) ? connection.edges : [];
      const articles = edges
        .map((edge) => this.mapStorefrontArticle(edge?.node))
        .filter(Boolean);
      const pageInfo = connection?.pageInfo && typeof connection.pageInfo === 'object'
        ? connection.pageInfo
        : {};
      return { articles, pageInfo };
    },

    async _getArticlesStorefront(blogHandle, options = {}) {
      const limit = Number(options.limit) || this.getArticlesPageSize();
      const fields = this.STOREFRONT_ARTICLE_FIELDS;
      let query;
      let variables;

      if (options.before) {
        query = `query ArticlesByBlogBefore($blog: String!, $last: Int!, $before: String) {
          blog(handle: $blog) {
            articles(last: $last, before: $before, sortKey: PUBLISHED_AT, reverse: true) {
              pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
              edges { node { ${fields} } }
            }
          }
        }`;
        variables = { blog: blogHandle, last: limit, before: options.before };
      } else {
        query = `query ArticlesByBlog($blog: String!, $first: Int!, $after: String) {
          blog(handle: $blog) {
            articles(first: $first, after: $after, sortKey: PUBLISHED_AT, reverse: true) {
              pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
              edges { node { ${fields} } }
            }
          }
        }`;
        variables = { blog: blogHandle, first: limit, after: options.after || null };
      }

      const data = await this.shopifyStorefrontQuery(query, variables);
      return this._mapStorefrontArticlesConnection(data?.blog?.articles);
    },

    async getArticles(blogHandle, options = {}) {
      this.requireStorefrontToken();
      const data = await this._getArticlesStorefront(blogHandle, options);
      return { ...data, source: 'storefront' };
    },

    async getCommunitySearchIndex(parentBlogHandles) {
      const params = new URLSearchParams({
        shop_name: this.shopName(),
      });
      if (Array.isArray(parentBlogHandles) && parentBlogHandles.length) {
        params.set('blogs', parentBlogHandles.join(','));
      }
      const res = await this.communityFetch(`/api/community-search-index?${params}`);
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      return data;
    },

    parseCommentHash() {
      return this.parseCommentScrollTarget(String(window.location.hash || ''));
    },

    _topicActivityAt: new Map(),

    topicActivityKey(blogHandle, articleHandle) {
      const blog = String(blogHandle || '').trim();
      const article = this.bareArticleHandle(articleHandle);
      return blog && article ? `${blog}::${article}` : '';
    },

    recordTopicActivity(blogHandle, articleHandle, publishedAt) {
      const key = this.topicActivityKey(blogHandle, articleHandle);
      if (!key || !publishedAt) return;
      const ts = new Date(publishedAt).getTime();
      if (Number.isNaN(ts)) return;
      if (ts > (this._topicActivityAt.get(key) || 0)) {
        this._topicActivityAt.set(key, ts);
      }
    },

    buildTopicActivity(comments) {
      (comments || []).forEach((comment) => {
        if (!comment) return;
        this.recordTopicActivity(
          comment.parent_blog_handle,
          comment.parent_article_handle,
          comment.publishedAt ?? comment.published_at
        );
      });
    },

    _topicCommentCounts: new Map(),
    _topicCommentCountsReady: false,
    _countSourceComments: [],

    commentParentCommentHandle(comment) {
      if (!comment) return '';
      const explicit = this.bareArticleHandle(comment.parent_comment_handle);
      if (explicit) return explicit;
      // Some replies record their parent comment in the parent_article field.
      const parentBlog = String(comment.parent_blog_handle || '').trim();
      const parentArticle = this.bareArticleHandle(comment.parent_article_handle);
      if (parentBlog === this.COMMENTS_BLOG && /^(comment|reply)-/i.test(parentArticle)) {
        return parentArticle;
      }
      return '';
    },

    buildTopicCommentCounts(comments) {
      const list = (comments || []).filter(Boolean);
      this._countSourceComments = list;
      const moderation = window.Alpine?.store('moderation');
      this._countModerationStates = moderation?.states;
      const byHandle = new Map();
      list.forEach((comment) => {
        const handle = comment.comment_handle || comment.handle;
        if (handle) byHandle.set(handle, comment);
      });

      const counts = new Map();
      list.forEach((comment) => {
        if (window.Alpine?.store('moderation')?.hidden('cs-comments',comment.comment_handle || comment.handle)) return;
        // Replies hang off other comments; walk up to the one sitting on a topic.
        let node = comment;
        let guard = 0;
        while (node && guard < 25) {
          const parentHandle = this.commentParentCommentHandle(node);
          if (!parentHandle) break;
          if (!byHandle.has(parentHandle)) {
            const removedParent = moderation?.status('cs-comments', parentHandle);
            if (removedParent) node = {parent_blog_handle:removedParent.topic_blog,parent_article_handle:removedParent.topic_handle};
            break;
          }
          node = byHandle.get(parentHandle);
          guard += 1;
        }
        const key = this.topicActivityKey(node?.parent_blog_handle, node?.parent_article_handle);
        if (!key) return;
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      this._topicCommentCounts = counts;
      this._topicCommentCountsReady = true;
    },

    bumpTopicCommentCount(blogHandle, articleHandle, delta = 1) {
      if (!this._topicCommentCountsReady) return;
      const key = this.topicActivityKey(blogHandle, articleHandle);
      if (!key) return;
      const next = (this._topicCommentCounts.get(key) || 0) + delta;
      this._topicCommentCounts.set(key, next < 0 ? 0 : next);
    },

    topicCommentCount(article) {
      if (!article) return 0;
      const saved = window.Alpine?.store('forumCounts');
      if (saved?.enabled) return saved.topic(article.blog_handle || article.blogHandle, article.article_handle || article.handle)?.comments ?? '…';
      const key = this.topicActivityKey(
        article.blog_handle,
        article.article_handle || article.handle
      );
      if (key && this._topicCommentCounts.has(key)) {
        const store = window.Alpine?.store('moderation');
        if (store && this._countModerationStates !== store.states) { this.buildTopicCommentCounts(this._countSourceComments); }
        return this._topicCommentCounts.get(key) || 0;
      }
      return this.topicRepliesCount(article);
    },

    topicLastActivityTime(article, blogHandle) {
      if (!article) return 0;
      const own = new Date(article.publishedAt ?? article.published_at ?? 0).getTime();
      const key = this.topicActivityKey(
        blogHandle || article.blog_handle,
        article.article_handle || article.handle
      );
      const fromComments = key ? this._topicActivityAt.get(key) || 0 : 0;
      return Math.max(Number.isNaN(own) ? 0 : own, fromComments);
    },

    sortTopicsByActivity(list, blogHandle) {
      return [...(list || [])].sort(
        (a, b) =>
          this.topicLastActivityTime(b, blogHandle) - this.topicLastActivityTime(a, blogHandle)
      );
    },

    bareArticleHandle(value) {
      const raw = String(value || '').trim();
      if (!raw) return '';
      const parts = raw.split('/').filter(Boolean);
      return parts.length ? parts[parts.length - 1] : '';
    },

    normalizeSearchCommentItem(item, flatArticles = [], commentByHandle = null) {
      if (!item || typeof item !== 'object') return null;
      const mfRoot = item.metafields || {};
      const mf = mfRoot.custom ?? mfRoot;
      const meta = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
      const parentBlog = String(
        item.parent_blog_handle ??
          item.parentBlogHandle ??
          meta.parent_blog_handle ??
          mf.parent_blog_handle?.value ??
          mf.parent_blog_handle ??
          mfRoot.custom?.parent_blog_handle?.value ??
          mfRoot.custom?.parent_blog_handle ??
          ''
      ).trim();
      const parentArticle = this.bareArticleHandle(
        item.parent_article_handle ??
          item.parentArticleHandle ??
          meta.parent_article_handle ??
          mf.parent_article_handle?.value ??
          mf.parent_article_handle ??
          mfRoot.custom?.parent_article_handle?.value ??
          mfRoot.custom?.parent_article_handle ??
          ''
      );

      let commentIndex = item.comment_index ?? item._commentIndex ?? meta.comment_index;
      if (commentIndex == null || commentIndex === '') {
        commentIndex = mf.comment_index?.value ?? mf.comment_index ?? meta.comment_index;
      }
      if (commentIndex == null || commentIndex === '') {
        commentIndex = this.commentIndexFromHandle(item.handle);
      }
      commentIndex = commentIndex != null && commentIndex !== '' ? String(commentIndex) : '';

      let parentCommentHandle = String(
        item.parent_comment_handle ??
          item.parentCommentHandle ??
          meta.parent_comment_handle ??
          mf.parent_comment_handle?.value ??
          mf.parent_comment_handle ??
          mfRoot.custom?.parent_comment_handle?.value ??
          mfRoot.custom?.parent_comment_handle ??
          ''
      ).trim();
      if (
        !parentCommentHandle &&
        parentBlog === this.COMMENTS_BLOG &&
        /^comment-/i.test(parentArticle)
      ) {
        parentCommentHandle = parentArticle;
      }
      const isNestedReply =
        Boolean(parentCommentHandle) ||
        Boolean(item.nested_comment) ||
        /^reply-\d+-on-comment-/i.test(String(item.handle || ''));
      let nestedReplySlot = item.nested_reply_slot;
      if (nestedReplySlot == null || nestedReplySlot === '') {
        nestedReplySlot =
          meta.nested_reply_slot ??
          mf.nested_reply_slot?.value ??
          mf.nested_reply_slot ??
          mfRoot.custom?.nested_reply_slot?.value ??
          mfRoot.custom?.nested_reply_slot ??
          '';
      }
      nestedReplySlot =
        nestedReplySlot != null && nestedReplySlot !== ''
          ? Number(nestedReplySlot)
          : isNestedReply
            ? this.nestedReplySlotFromHandle(item.handle) || 1
            : 0;

      let topicBlog = String(
        item.topic_blog_handle ??
          item.topicBlogHandle ??
          meta.topic_blog_handle ??
          mf.topic_blog_handle?.value ??
          mf.topic_blog_handle ??
          mfRoot.custom?.topic_blog_handle?.value ??
          mfRoot.custom?.topic_blog_handle ??
          ''
      ).trim();
      let topicArticle = String(
        item.topic_article_handle ??
          item.topicArticleHandle ??
          meta.topic_article_handle ??
          mf.topic_article_handle?.value ??
          mf.topic_article_handle ??
          mfRoot.custom?.topic_article_handle?.value ??
          mfRoot.custom?.topic_article_handle ??
          ''
      ).trim();
      if (isNestedReply && parentCommentHandle && commentByHandle) {
        const parentComment = commentByHandle.get(parentCommentHandle);
        if (parentComment) {
          const parentMf = parentComment.metafields?.custom ?? parentComment.metafields ?? {};
          if (!topicBlog) {
            topicBlog = String(
              parentComment.parent_blog_handle ??
                parentMf.parent_blog_handle?.value ??
                parentMf.parent_blog_handle ??
                ''
            ).trim();
          }
          if (!topicArticle) {
            topicArticle = String(
              parentComment.parent_article_handle ??
                parentMf.parent_article_handle?.value ??
                parentMf.parent_article_handle ??
                ''
            ).trim();
          }
        }
      }
      const topicParentBlog = isNestedReply && topicBlog ? topicBlog : parentBlog;
      const topicParentArticle = isNestedReply && topicArticle ? topicArticle : parentArticle;
      if (!topicParentBlog || !topicParentArticle) return null;

      const rawBody =
        item.body_text ?? item.description ?? item.content ?? item.body ?? '';
      const bodyText = this.stripHtmlForSearch(rawBody).trim();
      const authorName = this.resolveAuthorDisplayName(item);
      let parentTitle = String(item.parent_title ?? item.parentTitle ?? meta.parent_title ?? '').trim();
      if (!parentTitle) {
        const cached = (flatArticles || []).find(
          (a) =>
            a.blog_handle === topicParentBlog &&
            (a.article_handle === topicParentArticle || a.handle === topicParentArticle)
        );
        parentTitle = cached?.title || topicParentArticle;
      }

      const authorId = this.resolveAuthorId(item);

      return {
        result_type: 'comment',
        comment_handle: String(item.handle || '').trim(),
        comment_index: commentIndex,
        parent_blog_handle: topicParentBlog,
        parent_article_handle: topicParentArticle,
        parent_comment_handle: parentCommentHandle,
        is_nested_reply: isNestedReply,
        nested_reply_slot: nestedReplySlot > 0 ? nestedReplySlot : undefined,
        parent_title: parentTitle,
        searchable_text: bodyText,
        author: authorName,
        author_name: authorName,
        author_id: authorId,
        profile_name: String(item.profile_name || '').trim() || authorName,
        author_profile_image: item.author_profile_image || null,
        published_at: this.formatArticleDate(item.published_at ?? item.publishedAt),
        publishedAt: item.published_at ?? item.publishedAt,
      };
    },

    stripHtmlForSearch(value) {
      return String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },

    formatArticleDate(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
    },

    normalizeArticleTags(rawTags) {
      if (!Array.isArray(rawTags)) return [];
      return rawTags
        .filter((t) => t && (t.name || t.displayName))
        .map((t) => ({
          name: String(t.name || t.displayName),
          tag_background: t.tag_background?.value ?? t.tag_background ?? '#22c55e',
          tag_color: t.tag_color?.value ?? t.tag_color ?? '#fff',
        }));
    },

    articleRepliesCount(article) {
      return this.topicCommentCount(article);
    },

    articlePostsCount(article) {
      return this.topicPostsCount(article);
    },

    normalizeApiArticle(article, blogHandle) {
      if (!article || typeof article !== 'object') return null;
      const mf = article.metafields || {};
      const authorName = this.resolveAuthorDisplayName(article);
      const rawTags = mf.tags ?? mf.custom?.tags?.value ?? article.tags ?? [];
      const tags = this.normalizeArticleTags(rawTags);
      const repliesCount = this.topicRepliesCount(article);
      const publishedRaw = article.publishedAt ?? article.published_at;
      const body = article.excerpt || article.content || article.summary || '';
      const searchable_text = `${article.title || ''} ${this.stripHtmlForSearch(body)}`.trim();
      const first_tag = tags.length
        ? {
            name: tags[0].name,
            tag_background: tags[0].tag_background,
            tag_color: tags[0].tag_color,
          }
        : null;
      const postsCount = this.topicPostsCount(article);

      return {
        id: article.id || article.article_id || article.admin_graphql_api_id || '',
        handle: article.handle,
        title: article.title || '',
        author: authorName,
        author_name: authorName,
        profile_name: String(article.profile_name || '').trim() || authorName,
        published_at: this.formatArticleDate(publishedRaw),
        publishedAt: publishedRaw,
        blog_handle: blogHandle,
        article_handle: article.handle,
        searchable_text,
        excerpt: this.stripHtmlForSearch(body).trim(),
        comments_count: repliesCount,
        author_id: mf.author_id ?? mf.custom?.author_id?.value ?? '',
        author_profile_image: this.resolveAuthorProfileImageUrl(article),
        tags,
        first_tag,
        repliesCount,
        postsCount,
        metafields: mf,
      };
    },

    createRichTextEditorController(options = {}) {
    const utils = this;
    const cfg = options || {};
    let editor = null;
    let isMounted = false;
    let inputListener = null;
    let currentValue = '';
    let uploadNotice = null;
    const activeUploads = new Set();
    const filePickers = new Map();

    const getTextarea = () => cfg.textarea || null;

    const getFilePicker = (kind, accept) => {
      let input = filePickers.get(kind);
      if (!input) {
        input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.hidden = true;
        // Keep the input attached and referenced while Safari's native picker is open.
        document.body.appendChild(input);
        filePickers.set(kind, input);
      }
      // Selecting the same file again must still fire a change event.
      input.value = '';
      return input;
    };

    const withUploadNotice = async (upload) => {
      const attempt = {};
      activeUploads.add(attempt);
      if (!uploadNotice) {
        uploadNotice = document.createElement('div');
        uploadNotice.className = 'modl-cf-upload-notice';
        uploadNotice.setAttribute('role', 'status');
        uploadNotice.setAttribute('aria-live', 'polite');
        uploadNotice.textContent = 'Uploading…';
        const host = getTextarea()?.parentElement;
        // Overlay the editing area without putting status text into the draft itself.
        (host?.querySelector('.tox-edit-area') || host)?.appendChild(uploadNotice);
      }
      try {
        return await upload();
      } finally {
        activeUploads.delete(attempt);
        if (!activeUploads.size) {
          uploadNotice?.remove();
          uploadNotice = null;
        }
      }
    };

    const getEditorId = () => {
      const ta = getTextarea();
      if (!ta) return '';
      if (ta.id && ta.id.trim()) return ta.id.trim();
      const autoId = `rte-${Math.random().toString(36).slice(2, 10)}`;
      ta.id = autoId;
      return autoId;
    };

    const readExternalValue = () => {
      if (typeof cfg.getValue === 'function') {
        const v = cfg.getValue();
        return v == null ? '' : String(v);
      }
      const ta = getTextarea();
      return ta?.value || '';
    };

    const writeExternalValue = (next, meta = {}) => {
      const value = next == null ? '' : String(next);
      const ta = getTextarea();
      if (ta && ta.value !== value) ta.value = value;

      if (typeof cfg.setValue === 'function') {
        cfg.setValue(value);
      }

      if (!meta.skipOnChange && typeof cfg.onChange === 'function') {
        cfg.onChange(value);
      }

      currentValue = value;
    };

    const setFallbackVisible = (enabled) => {
      const ta = getTextarea();
      if (!ta) return;
      ta.classList.toggle('modl-rich-text-editor__textarea--fallback', !!enabled);
      ta.classList.toggle('modl-rich-text-editor__textarea--hidden', !enabled);
    };

    const setReadonly = (readonly) => {
      const ta = getTextarea();
      if (ta) ta.readOnly = !!readonly;
      if (!editor) return;
      editor.mode.set(readonly ? 'readonly' : 'design');
    };

    const fallbackImageUpload = async (blobInfo, progress) => {
      const mode = cfg.imageUploadFallback || 'block';
      if (mode === 'base64' && blobInfo && typeof blobInfo.base64 === 'function') {
        const file = typeof blobInfo.blob === 'function' ? blobInfo.blob() : null;
        const mime = file?.type || 'image/png';
        if (typeof progress === 'function') progress(100);
        return `data:${mime};base64,${blobInfo.base64()}`;
      }
      throw new Error('Image upload is not configured for this editor.');
    };

    const uploadImage = async (blobInfo, progress) => {
      if (typeof cfg.onImageUpload === 'function') {
        const file = typeof blobInfo?.blob === 'function' ? blobInfo.blob() : null;
        if (!file) {
          throw new Error('Unable to read image file.');
        }
        const url = await withUploadNotice(() => cfg.onImageUpload(file));
        if (!url || typeof url !== 'string') {
          throw new Error('Image upload did not return a valid URL.');
        }
        if (typeof progress === 'function') progress(100);
        return url;
      }
      return fallbackImageUpload(blobInfo, progress);
    };

    return {
      async mount() {
        const ta = getTextarea();
        if (!ta || isMounted) return;

        isMounted = true;
        const initial = cfg.modelValue != null ? String(cfg.modelValue) : readExternalValue();
        writeExternalValue(initial, { skipOnChange: true });
        setReadonly(cfg.readonly);

        if (cfg.placeholder != null && String(cfg.placeholder).trim()) {
          ta.setAttribute('placeholder', String(cfg.placeholder));
        }

        inputListener = () => {
          writeExternalValue(ta.value || '');
        };
        ta.addEventListener('input', inputListener);

        try {
          await utils.ensureTinyMce();
        } catch (error) {
          console.error(error);
          setFallbackVisible(true);
          return;
        }

        if (typeof window.tinymce === 'undefined') {
          setFallbackVisible(true);
          return;
        }

        const editorId = getEditorId();
        if (!editorId) {
          setFallbackVisible(true);
          return;
        }

        if (ta.dataset.rteMounted === '1') {
          editor = window.tinymce.get(editorId) || null;
          if (!editor) setFallbackVisible(true);
          return;
        }

        ta.dataset.rteMounted = '1';

        const baseConfig = {
          selector: `#${editorId}`,
          license_key: 'gpl',
          base_url: TINYMCE_CDN,
          suffix: '.min',
          menubar: false,
          toolbar_sticky: true,
          toolbar_mode: 'floating',
          branding: false,
          promotion: false,
          statusbar: false,
          resize: false,
          height: utils.normalizeEditorHeight(cfg.height, 360),
          min_height: utils.normalizeEditorHeight(cfg.height, 360),
          max_height: 1200,
          readonly: cfg.readonly ? 1 : 0,
          placeholder: cfg.placeholder || '',
          icons: 'default',
          icons_url: `${TINYMCE_CDN}icons/default/icons.min.js`,
          skin_url: `${TINYMCE_CDN}skins/ui/oxide`,
          content_css: [
            `${TINYMCE_CDN}skins/content/default/content.min.css`,
            window.COMMUNITY_EDITOR_CONTENT_CSS,
          ].filter(Boolean),
          font_css: [
            'https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,600;0,700;1,400&display=swap',
          ],
          content_style: "body{font-family:'Nunito',sans-serif;}img{max-width:100%;height:auto;}",
          plugins: [
            'lists', 'link', 'image', 'media', 'table', 'code',
            'autoresize', 'paste'
          ],
          toolbar: [
            window.matchMedia('(max-width: 749px)').matches
              ? `${typeof cfg.onImageUpload === 'function' ? 'uploadimage' : 'image'} link | bold italic underline | bullist | blocks | media | undo redo | numlist blockquote removeformat`
              : `undo redo | bold italic underline | blocks | bullist numlist | blockquote | link ${typeof cfg.onImageUpload === 'function' ? 'uploadimage' : 'image'} media | removeformat`
          ],
          autoresize_bottom_margin: 40,
          media_live_embeds: true,
          paste_data_images: true,
          automatic_uploads: true,
          relative_urls: false,
          convert_urls: false,
          file_picker_callback: (cb, value, meta) => {
            if (meta.filetype !== 'media') return;
            if (typeof cfg.onMediaUpload !== 'function') return;
            const input = getFilePicker('video', 'video/*');
            input.onchange = async () => {
              const file = input.files?.[0];
              input.value = '';
              if (!file) return;
              try {
                const url = await withUploadNotice(() => cfg.onMediaUpload(file));
                if (url) cb(url, { source2: url });
              } catch (error) {
                console.error('TinyMCE video upload failed:', error);
              }
            };
            input.click();
          },
          setup: (ed) => {
            if (typeof cfg.onImageUpload === 'function') {
              ed.ui.registry.addButton('uploadimage', {
                icon: 'image',
                tooltip: 'Insert image',
                onAction: () => {
                  const input = getFilePicker('image', 'image/jpeg,image/png,image/gif,image/webp');
                  input.onchange = async () => {
                    const file = input.files?.[0];
                    input.value = '';
                    if (!file) return;
                    try {
                      const url = await withUploadNotice(() => cfg.onImageUpload(file));
                      if (url) {
                        ed.insertContent(`<img src="${url}" alt="" style="max-width:100%;" />`);
                      }
                    } catch (err) {
                      console.error('Image upload failed:', err);
                    }
                  };
                  input.click();
                },
              });
            }

            ed.on('change input Undo Redo SetContent', () => {
              writeExternalValue(ed.getContent());
            });
            if (typeof cfg.onSetup === 'function') {
              cfg.onSetup(ed);
            }
          },
          init_instance_callback: (ed) => {
            editor = ed;
            setFallbackVisible(false);
            writeExternalValue(ed.getContent(), { skipOnChange: true });
            if (typeof cfg.onInit === 'function') {
              cfg.onInit(ed);
            }
          },
        };

        const initConfig = Object.assign({}, baseConfig, cfg.tinyConfig || {});
        initConfig.images_upload_handler = async (blobInfo, progress) => {
          return uploadImage(blobInfo, progress);
        };

        try {
          await window.tinymce.init(initConfig);
          editor = window.tinymce.get(editorId) || editor;
          if (!editor) {
            setFallbackVisible(true);
          }
        } catch (error) {
          console.error('TinyMCE initialization failed:', error);
          setFallbackVisible(true);
          ta.dataset.rteMounted = '0';
        }
      },

      destroy() {
        filePickers.forEach((input) => {
          input.onchange = null;
          input.remove();
        });
        filePickers.clear();
        activeUploads.clear();
        uploadNotice?.remove();
        uploadNotice = null;
        const ta = getTextarea();
        if (inputListener && ta) {
          ta.removeEventListener('input', inputListener);
        }
        inputListener = null;
        isMounted = false;

        if (editor) {
          try {
            editor.remove();
          } catch {
            /* noop */
          }
        } else {
          try {
            const existing = ta && typeof window.tinymce !== 'undefined' ? window.tinymce.get(ta.id) : null;
            if (existing) existing.remove();
          } catch {
            /* noop */
          }
        }
        editor = null;

        if (ta) {
          ta.dataset.rteMounted = '0';
          setFallbackVisible(true);
        }
      },

      syncFromEditor() {
        if (editor) {
          writeExternalValue(editor.getContent(), { skipOnChange: true });
          return;
        }
        const ta = getTextarea();
        writeExternalValue(ta?.value || '', { skipOnChange: true });
      },

      syncExternalToEditor(nextValue) {
        const value = nextValue == null ? readExternalValue() : String(nextValue);
        if (value === currentValue) return;

        const ta = getTextarea();
        if (ta && ta.value !== value) ta.value = value;

        if (editor && editor.getContent() !== value) {
          editor.setContent(value || '');
        }

        currentValue = value;
      },

      setReadonly,
    };
    },
  };

  window.ModlContentEditEditorProps = () => ({
    getValue: () => Alpine.store('edits').body,
    setValue: value => { Alpine.store('edits').body = value || ''; },
    onImageUpload: file => UTILS.uploadToCloudinary(file, 'image'),
    onMediaUpload: file => UTILS.uploadToCloudinary(file, 'video'),
    height: 240, placeholder: 'Update your post…',
    tinyConfig: { toolbar_mode: 'wrap', toolbar_sticky: false },
    // TinyMCE 7 attaches iframe-editor popups to body. Move this editor's new
    // popup containers into the native dialog so they are not behind its backdrop.
    onSetup: ed => { ed._modlExistingPopups = new Set(document.querySelectorAll('.tox-tinymce-aux')); },
    onInit: ed => {
      const dialog = document.getElementById('modl-edit-dialog');
      if (dialog) document.querySelectorAll('body > .tox-tinymce-aux').forEach(node => {
        if (!ed._modlExistingPopups.has(node)) dialog.appendChild(node);
      });
      delete ed._modlExistingPopups;
    },
  });

  Alpine.data('RichTextEditor', (props = {}) => ({
    modelValue: props.modelValue ?? '',
    variant: props.variant || 'topic',
    onChange: typeof props.onChange === 'function' ? props.onChange : null,
    onImageUpload: typeof props.onImageUpload === 'function' ? props.onImageUpload : null,
    onMediaUpload: typeof props.onMediaUpload === 'function' ? props.onMediaUpload : null,
    imageUploadFallback: props.imageUploadFallback || 'block',
    getValue: typeof props.getValue === 'function' ? props.getValue : null,
    setValue: typeof props.setValue === 'function' ? props.setValue : null,
    placeholder: props.placeholder || '',
    height: props.height,
    readonly: !!props.readonly,
    tinyConfig: props.tinyConfig || null,
    onSetup: typeof props.onSetup === 'function' ? props.onSetup : null,
    onInit: typeof props.onInit === 'function' ? props.onInit : null,
    _editorCtrl: null,

    async init() {
      const textarea = this.$el?.querySelector?.('textarea');
      if (!textarea) return;

      this.$el.classList.add('modl-rich-text-editor');

      const resolveExternalValue = () => {
        if (this.getValue) {
          const v = this.getValue();
          return v == null ? '' : String(v);
        }
        return this.modelValue == null ? '' : String(this.modelValue);
      };

      this._editorCtrl = UTILS.createRichTextEditorController({
        textarea,
        modelValue: resolveExternalValue(),
        variant: this.variant,
        placeholder: this.placeholder,
        height: this.height,
        readonly: this.readonly,
        onImageUpload: this.onImageUpload,
        onMediaUpload: this.onMediaUpload,
        imageUploadFallback: this.imageUploadFallback,
        getValue: resolveExternalValue,
        setValue: (next) => {
          if (this.setValue) {
            this.setValue(next);
          }
          this.modelValue = next;
        },
        onChange: (next) => {
          if (this.setValue) {
            this.setValue(next);
          }
          this.modelValue = next;
          if (this.onChange) this.onChange(next);
        },
        tinyConfig: this.tinyConfig,
        onSetup: this.onSetup,
        onInit: this.onInit,
      });

      await this._editorCtrl.mount();

      if (this.getValue) {
        Alpine.effect(() => {
          this._editorCtrl?.syncExternalToEditor(resolveExternalValue());
        });
      } else {
        this.$watch('modelValue', (next) => {
          this._editorCtrl?.syncExternalToEditor(next);
        });
      }

      this.$watch('readonly', (next) => {
        this._editorCtrl?.setReadonly(next);
      });
    },

    async mountEditor() {
      await this._editorCtrl?.mount();
      if (this.getValue) {
        this._editorCtrl?.syncExternalToEditor(this.getValue());
      }
    },

    destroyEditor() {
      this._editorCtrl?.destroy();
    },

    syncFromEditor() {
      this._editorCtrl?.syncFromEditor();
    },

    destroy() {
      this.destroyEditor();
      this._editorCtrl = null;
    },
  }));

  Alpine.data('CommunityForum', (rootEl) => ({
    ...forumNavigation,
    currentView: 'landing',
    selectedBlogHandle: '',
    selectedArticleHandle: '',
    searchTerm: '',
    commentSubmitting: false,
    commentContent: '',
    commentSuccessMessage: '',
    commentErrorMessage: '',
    _commentSuccessClearTimer: null,
    validBlogHandles: [],
    blogArticleHandles: {},
    articlesByBlog: {},
    pageInfoByBlog: {},
    flatArticles: [],
    articlesLoading: true,
    articlesPaginating: false,
    storefrontError: '',
    blogTitles: {},
    latestTopicsLimit: 5,
    latestPostsLimit: 5,
    selectedTag: '',
    searchArticles: [],
    searchComments: [],
    searchFilter: '',
    pendingCommentScroll: '',
    _searchDebounce: null,
    _mainViewLoadId: 0,
    showCreateTopicView: false,
    showProfileView: false,
    showMessagesView: false,
    showGuidelinesView: false,
    guidelinesDocument: 'guidelines',
    guidelinesScrollY: 0,
    guidelinesReturnMessages: false,
    forumTrail: [],
    messageUnread: 0,
    messagesPreview: false,
    profileViewContext: { readOnly: false, member: null },
    accountProfileImageUrl: '',
    accountAvatarLoading: true,
    accountAvatarLoaded: false,
    _cfAuth: false,
    _sidebarRefreshTimer: null,
    _sidebarRefreshInFlight: false,
    sidebarIncoming: {},
    _sidebarHighlightTimers: new Set(),
    _onVisibilityChange: null,
    needsProfileSetup: false,
    townSquareWelcomePending: false,
    townSquarePulse: false,
    setupNameContent: '',
    setupBioContent: '',
    setupPhotoFile: null,
    setupPhotoPreview: '',
    setupPhotoUrl: '',
    setupSubmitting: false,
    setupMessage: { error: '' },

    init() {
      this._cfAuth = rootEl?.dataset?.cfAuth === '1';
      this.messagesPreview = rootEl?.dataset?.dmPreviewAllowed === 'true' && new URLSearchParams(location.search).get('dm_preview') === '1';
      this.needsProfileSetup = rootEl?.dataset?.needsProfileSetup === '1';
      if (this.needsProfileSetup) {
        this.setupNameContent = '';
        const initialBio = document.getElementById('modl-cf-profile-initial-bio');
        try {
          const v = JSON.parse(initialBio?.textContent?.trim() || '""');
          if (typeof v === 'string') this.setupBioContent = v;
        } catch {
          /* noop */
        }
      }
      if (this._cfAuth) {
        const el = document.getElementById('modl-cf-profile-initial-profile-image-url');
        const raw = el?.textContent?.trim();
        if (raw) {
          try {
            const v = JSON.parse(raw);
            if (typeof v === 'string' && v) this.accountProfileImageUrl = v;
          } catch {
            /* noop */
          }
        }
      }
      this._onTermsSetupRequired = () => this.openTermsProfileSetup();
      window.addEventListener('modl-terms-setup-required', this._onTermsSetupRequired);
      if (this.needsProfileSetup) void this.$store.communityTerms.check().catch(() => {});
      this.resolveAccountAvatarLoading();
      this.validBlogHandles = this.getValidBlogHandles(rootEl);
      this.blogArticleHandles = {};
      this.blogTitles = this.getBlogTitles(rootEl);
      this.latestTopicsLimit = parseInt(rootEl?.dataset?.latestTopicsLimit, 10) || 5;
      this.latestPostsLimit = parseInt(rootEl?.dataset?.latestPostsLimit, 10) || 5;
      this.blogPaths = this.getBlogPaths(rootEl);
      this.landingFetchPath = rootEl?.dataset?.landingFetchPath || window.location.pathname;

      UTILS.logStorefrontConsumed('boot', {
        storefrontToken: Boolean(UTILS.getStorefrontToken()),
        shop: UTILS.shopName(),
        blogs: this.validBlogHandles,
      });

      this.syncFromUrl();
      this.initTownSquareWelcome();
      this._onTopicTag = (e) => {
        const tag = e.detail?.tag;
        this.selectedTag = tag == null || tag === '' ? '' : String(tag);
        this.updateUrl();
      };
      document.addEventListener('cf-topic-tag', this._onTopicTag);

      this._onProfileImageUpdated = (e) => {
        const url = e.detail?.url ? String(e.detail.url) : '';
        this.accountProfileImageUrl = url;
        if (url) {
          this.accountAvatarLoading = true;
          this.accountAvatarLoaded = false;
        } else {
          this.resolveAccountAvatarLoading();
        }
      };
      document.addEventListener('cf-profile-image-updated', this._onProfileImageUpdated);

      this.$watch('showProfileView', (v) => {
        if (!v) {
          this.profileViewContext = { readOnly: false, member: null };
          document.dispatchEvent(new CustomEvent('cf-profile-close'));
        }
      });

      this.InitWatchers();

      if (!this.needsProfileSetup && !this.showProfileView) {
        const container = this.getMainViewContainer();
        if (container) {
          container.innerHTML = this.getViewSkeletonHtml();
          container.setAttribute('data-modl-view-loading', 'true');
          container.setAttribute('aria-busy', 'true');
        }
        void this.loadMainView();
      }

      if (!this.needsProfileSetup) {
        if (!UTILS.getStorefrontToken()) {
          this.storefrontError =
            'Community topics could not load. Configure the Storefront API access token in theme settings.';
          this.articlesLoading = false;
          void this.loadSearchComments();
        } else {
          Promise.allSettled([this.loadCommunityArticles(), this.loadSearchComments()]).finally(() => {
            this.articlesLoading = false;
            this.startSidebarRefresh();
            this.$nextTick(() => {
              if (this.showProfileView) {
                this.emitProfileOpen();
              }
            });
          });
        }
      } else {
        this.articlesLoading = false;
      }
    },

    get isForumHome() {
      return !this.needsProfileSetup && !this.selectedBlogHandle && !this.selectedArticleHandle &&
        !this.showProfileView && !this.showMessagesView && !this.showGuidelinesView && !this.showCreateTopicView;
    },

    townSquareWelcomeKey() {
      return 'modl-town-square-welcome:' + rootEl.dataset.customerId;
    },

    initTownSquareWelcome() {
      if (!this._cfAuth || !rootEl.dataset.customerId) return;
      try { this.townSquareWelcomePending = localStorage.getItem(this.townSquareWelcomeKey()) === 'pending'; } catch {}
      this.$watch('isForumHome', home => {
        if (home) this.beginTownSquareWelcome();
        else this.townSquarePulse = false;
      });
      this._onTownSquarePageHide = () => { this.townSquarePulse = false; };
      window.addEventListener('pagehide', this._onTownSquarePageHide);
      this.beginTownSquareWelcome();
    },

    queueTownSquareWelcome() {
      // Only a newly created forum profile gets the hint, not a later terms prompt.
      if (rootEl.dataset.needsProfileSetup !== '1') return;
      this.townSquareWelcomePending = true;
      try { localStorage.setItem(this.townSquareWelcomeKey(), 'pending'); } catch {}
    },

    beginTownSquareWelcome() {
      if (!this.isForumHome || !this.townSquareWelcomePending) return;
      this.townSquareWelcomePending = false;
      this.townSquarePulse = true;
      // Consume on arrival so refreshing or returning in browser history cannot replay it.
      try { localStorage.removeItem(this.townSquareWelcomeKey()); } catch {}
    },

    openTermsProfileSetup() {
      if (!this._cfAuth) return;
      if (!this.needsProfileSetup) {
        const name = document.getElementById('modl-cf-profile-initial-display-name');
        try { this.setupNameContent = JSON.parse(name?.textContent || '""'); } catch {}
        this.setupBioContent = UTILS.getSavedProfileBio();
      }
      this.needsProfileSetup = true;
      this.$nextTick(() => rootEl.querySelector('.modl-cf__profile-setup')?.scrollIntoView({block:'start'}));
    },

    clearSetupPhoto() {
      if (this.setupPhotoPreview) URL.revokeObjectURL(this.setupPhotoPreview);
      this.setupPhotoFile = null;
      this.setupPhotoPreview = '';
      this.setupPhotoUrl = '';
      if (this.$refs?.setupPhotoInput) this.$refs.setupPhotoInput.value = '';
    },

    onSetupPhotoChange(event) {
      if (this.setupSubmitting) return;
      const file = event.target?.files?.[0];
      if (!file) return;
      const error = UTILS.validateProfileImageFile(file);
      if (error) {
        this.setupMessage = { error };
        event.target.value = '';
        return;
      }
      this.clearSetupPhoto();
      this.setupMessage = { error: '' };
      this.setupPhotoFile = file;
      this.setupPhotoPreview = URL.createObjectURL(file);
    },

    async submitProfileSetup() {
      if (this.setupSubmitting) return;
      const name = String(this.setupNameContent || '').trim();
      if (!name) {
        this.setupMessage = { error: 'Username is required.' };
        return;
      }
      const customerId = rootEl?.dataset?.customerId;
      if (!customerId) {
        this.setupMessage = { error: 'Not signed in.' };
        return;
      }
      this.setupSubmitting = true;
      this.setupMessage = { error: '' };
      try {
        if (window.ModlCommunityTerms && !await window.ModlCommunityTerms.completeSetup()) return;
        if (this.setupPhotoFile) {
          // Keep the uploaded URL if saving fails, so retrying does not upload twice.
          this.setupPhotoUrl ||= await UTILS.uploadToCloudinary(this.setupPhotoFile, 'image');
          await UTILS.saveCustomerProfileImage(this.setupPhotoUrl, customerId);
          this.accountProfileImageUrl = this.setupPhotoUrl;
          const imageScript = document.getElementById('modl-cf-profile-initial-profile-image-url');
          if (imageScript) imageScript.textContent = JSON.stringify(this.setupPhotoUrl);
          document.dispatchEvent(new CustomEvent('cf-profile-image-updated', { detail: { url: this.setupPhotoUrl } }));
          this.clearSetupPhoto();
        }
        await UTILS.saveCustomerDisplayNameMetafield(name, customerId);
        await UTILS.saveCustomerBioMetafield(this.setupBioContent || '', customerId);
        UTILS.updateCommunityDisplayName(name, customerId);
        const bioScript = document.getElementById('modl-cf-profile-initial-bio');
        if (bioScript) bioScript.textContent = JSON.stringify(this.setupBioContent || '');
        const nameScript = document.getElementById('modl-cf-profile-initial-display-name');
        if (nameScript) nameScript.textContent = JSON.stringify(name);
        this.queueTownSquareWelcome();
        delete rootEl.dataset.needsProfileSetup;
        this.needsProfileSetup = false;
        window.dispatchEvent(new CustomEvent('modl-profile-created'));
        const container = this.getMainViewContainer();
        if (container && !this.showProfileView) {
          container.innerHTML = this.getViewSkeletonHtml();
          container.setAttribute('data-modl-view-loading', 'true');
          container.setAttribute('aria-busy', 'true');
          const loading = this.loadMainView();
          const loadId = this._mainViewLoadId;
          void loading.then(() => this.$nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => {
            if (loadId === this._mainViewLoadId && this.isForumHome)
              window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          }))));
        }
        this.articlesLoading = true;
        Promise.allSettled([this.loadCommunityArticles(), this.loadSearchComments()]).finally(() => {
          this.articlesLoading = false;
          this.startSidebarRefresh();
        });
      } catch (err) {
        console.error('Profile setup failed:', err);
        this.setupMessage = { error: err?.message || 'Could not save profile.' };
      } finally {
        this.setupSubmitting = false;
      }
    },

    destroy() {
      this.stopReadingScroll();
      clearTimeout(this._visitSaveTimer);
      window.removeEventListener('scroll', this._onVisitScroll);
      window.removeEventListener('popstate', this._onForumPopState);
      window.history.scrollRestoration = this._previousScrollRestoration || 'auto';
      this.clearSetupPhoto();
      window.removeEventListener('pagehide', this._onTownSquarePageHide);
      window.removeEventListener('modl-terms-setup-required', this._onTermsSetupRequired);
      this.stopSidebarRefresh();
    },

    startSidebarRefresh() {
      this.stopSidebarRefresh();
      this._onVisibilityChange = () => {
        if (!document.hidden) {
          void this.refreshLatestLists();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);
      this._sidebarRefreshTimer = setInterval(() => {
        void this.refreshLatestLists();
      }, 30000);
    },

    stopSidebarRefresh() {
      this._sidebarHighlightTimers.forEach(timer => clearTimeout(timer));
      this._sidebarHighlightTimers.clear();
      this.sidebarIncoming = {};
      if (this._sidebarRefreshTimer) {
        clearInterval(this._sidebarRefreshTimer);
        this._sidebarRefreshTimer = null;
      }
      if (this._onVisibilityChange) {
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        this._onVisibilityChange = null;
      }
    },

    async refreshLatestLists() {
      if (document.hidden || this._sidebarRefreshInFlight) return;
      this._sidebarRefreshInFlight = true;
      // Compare identities, not positions: reordering or editing an old item is not new activity.
      const known = new Set([
        ...this.flatArticles.map(item => this.sidebarItemKey(item, 'topic')),
        ...this.searchComments.map(item => this.sidebarItemKey(item, 'comment')),
      ]);
      try {
        await Promise.allSettled([
          this.loadCommunityArticles({ silent: true }),
          this.loadSearchComments(),
        ]);
        if (this._sidebarRefreshTimer && !document.hidden) {
          for (const [kind, items] of [['topic', this.latestTopics()], ['comment', this.latestComments()]]) {
            for (const item of items) {
              const key = this.sidebarItemKey(item, kind);
              if (!known.has(key)) this.highlightSidebarItem(key);
            }
          }
        }
      } finally {
        this._sidebarRefreshInFlight = false;
      }
    },

    sidebarItemKey(item, kind) {
      return kind === 'topic'
        ? `topic:${item.blog_handle}:${item.article_handle || item.handle}`
        : `comment:${item.comment_handle || item.article_handle || item.handle}`;
    },

    highlightSidebarItem(key) {
      if (this.sidebarIncoming[key]) return;
      this.sidebarIncoming[key] = true;
      const timer = setTimeout(() => {
        delete this.sidebarIncoming[key];
        this._sidebarHighlightTimers.delete(timer);
      }, 10000);
      this._sidebarHighlightTimers.add(timer);
    },

    resolveAccountAvatarLoading() {
      if (!this._cfAuth || !this.accountProfileImageUrl) {
        this.accountAvatarLoading = false;
        this.accountAvatarLoaded = false;
      }
    },

    onAccountAvatarLoad() {
      this.accountAvatarLoading = false;
      this.accountAvatarLoaded = true;
    },

    onAccountAvatarError() {
      this.accountProfileImageUrl = '';
      this.accountAvatarLoading = false;
      this.accountAvatarLoaded = false;
    },

    getBlogTitles(root) {
      const raw = root?.dataset?.blogTitles;
      if (!raw) return {};
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    },

    rebuildFlatArticlesFromCache() {
      const flatArticles = [];
      Object.entries(this.articlesByBlog || {}).forEach(([blogHandle, articles]) => {
        (articles || []).forEach((a) => flatArticles.push(a));
      });
      this.flatArticles = flatArticles;
      this.searchArticles = flatArticles;
      this.rebuildArticleRegistry();
      this.enrichSearchComments();
    },

    dispatchArticlesCacheUpdated(blogHandle) {
      const totalCount = (this.articlesByBlog[blogHandle] || []).length;
      document.dispatchEvent(
        new CustomEvent('cf-articles-cache-updated', {
          detail: { blogHandle, totalCount },
        })
      );
      UTILS.logStorefrontConsumed('mergeArticlesPage', {
        blogHandle,
        totalCount,
        pageInfo: this.pageInfoByBlog[blogHandle],
      });
    },

    async mergeArticlesPage(blogHandle, rawArticles, pageInfo) {
      const incoming = Array.isArray(rawArticles) ? rawArticles : [];
      if (!incoming.length && !pageInfo) return;

      await UTILS.hydrateArticlesAuthorProfileNames(incoming);
      const normalized = incoming
        .map((a) => UTILS.normalizeApiArticle(a, blogHandle))
        .filter(Boolean);

      const existing = Array.isArray(this.articlesByBlog[blogHandle])
        ? this.articlesByBlog[blogHandle]
        : [];
      const byHandle = new Map();
      existing.forEach((a) => {
        if (a?.handle) byHandle.set(a.handle, a);
      });
      normalized.forEach((a) => {
        if (a?.handle) byHandle.set(a.handle, a);
      });
      this.articlesByBlog[blogHandle] = UTILS.sortTopicsByActivity(
        [...byHandle.values()],
        blogHandle
      );

      if (pageInfo && typeof pageInfo === 'object') {
        this.pageInfoByBlog[blogHandle] = pageInfo;
      }

      this.rebuildFlatArticlesFromCache();
      this.dispatchArticlesCacheUpdated(blogHandle);
    },

    async paginateBlogRemaining(blogHandle) {
      const pageSize = UTILS.getArticlesPageSize();
      let pageInfo = this.pageInfoByBlog[blogHandle];
      if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;

      while (pageInfo?.hasNextPage && pageInfo?.endCursor) {
        const data = await UTILS.getArticles(blogHandle, {
          limit: pageSize,
          after: pageInfo.endCursor,
        });
        const rawArticles = Array.isArray(data?.articles) ? data.articles : [];
        pageInfo = data.pageInfo && typeof data.pageInfo === 'object' ? data.pageInfo : {};
        await this.mergeArticlesPage(blogHandle, rawArticles, pageInfo);
        if (!pageInfo.hasNextPage) break;
      }
    },

    startBackgroundArticlePagination() {
      const handles = this.validBlogHandles || [];
      if (!handles.length) return;
      if (!this._paginatingBlogs) this._paginatingBlogs = new Set();
      this.articlesPaginating = true;
      const jobs = handles
        .filter((blogHandle) => !this._paginatingBlogs.has(blogHandle))
        .map((blogHandle) => {
          this._paginatingBlogs.add(blogHandle);
          return this.paginateBlogRemaining(blogHandle).finally(() => {
            this._paginatingBlogs.delete(blogHandle);
          });
        });
      if (!jobs.length) {
        this.articlesPaginating = false;
        return;
      }
      Promise.allSettled(jobs).finally(() => {
        this.articlesPaginating = false;
      });
    },

    async loadCommunityArticles({ silent = false } = {}) {
      if (!UTILS.getStorefrontToken()) {
        this.storefrontError =
          'Community topics could not load. Configure the Storefront API access token in theme settings.';
        return;
      }
      this.storefrontError = '';
      if (!silent) this.articlesLoading = true;

      const handles = this.validBlogHandles || [];
      const pageSize = UTILS.getArticlesPageSize();
      const results = await Promise.allSettled(
        handles.map((handle) => UTILS.getArticles(handle, { limit: pageSize }))
      );

      for (let index = 0; index < results.length; index += 1) {
        const blogHandle = handles[index];
        const result = results[index];
        if (result.status !== 'fulfilled') {
          console.error(`Failed to load articles for ${blogHandle}:`, result.reason);
          if (!this.articlesByBlog[blogHandle]) {
            this.articlesByBlog[blogHandle] = [];
            this.pageInfoByBlog[blogHandle] = {};
          }
          continue;
        }
        const data = result.value || {};
        UTILS.logStorefrontConsumed(`loadCommunityArticles:${blogHandle}`, {
          source: data.source || 'unknown',
          count: Array.isArray(data.articles) ? data.articles.length : 0,
          pageInfo: data.pageInfo,
          articles: data.articles,
        });
        const rawArticles = Array.isArray(data.articles) ? data.articles : [];
        const pageInfo = data.pageInfo && typeof data.pageInfo === 'object' ? data.pageInfo : {};
        await this.mergeArticlesPage(blogHandle, rawArticles, pageInfo);
      }

      if (!silent) this.articlesLoading = false;
      this.startBackgroundArticlePagination();
    },

    async waitForArticlesLoaded() {
      for (let attempt = 0; attempt < 50; attempt += 1) {
        if (!this.articlesLoading) return;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    },

    async loadSearchComments() {
      try {
        const prior = Array.isArray(this.searchComments) ? this.searchComments : [];
        const [indexData, storefrontNested] = await Promise.all([
          UTILS.getCommunitySearchIndex(this.validBlogHandles),
          UTILS.loadStorefrontNestedSearchComments(this.flatArticles).catch((err) => {
            console.warn('loadStorefrontNestedSearchComments failed:', err);
            return [];
          }),
        ]);
        const comments = Array.isArray(indexData?.comments) ? indexData.comments : [];
        await UTILS.hydrateArticlesAuthorProfileNames(comments);
        const serverComments = UTILS.dedupeSearchCommentItems(
          [
            ...comments
              .map((c) => UTILS.normalizeSearchCommentItem(c, this.flatArticles))
              .filter(Boolean),
            ...storefrontNested,
          ]
        );
        const pendingLocals = prior.filter((local) => {
          if (!local) return false;
          return !serverComments.some(
            (server) =>
              (local.comment_handle &&
                server.comment_handle &&
                local.comment_handle === server.comment_handle) ||
              (local.parent_blog_handle === server.parent_blog_handle &&
                local.parent_article_handle === server.parent_article_handle &&
                local.comment_index &&
                local.comment_index === server.comment_index &&
                Boolean(local.is_nested_reply) === Boolean(server.is_nested_reply) &&
                String(local.nested_reply_slot || '') === String(server.nested_reply_slot || ''))
          );
        });
        this.searchComments = UTILS.dedupeSearchCommentItems([...pendingLocals, ...serverComments]);
        this.enrichSearchComments();
        this.applyTopicActivityOrder();
      } catch (err) {
        console.warn('loadSearchComments failed:', err);
        if (!this.searchComments?.length) {
          this.searchComments = [];
        }
      }
    },

    applyTopicActivityOrder() {
      UTILS.buildTopicActivity(this.searchComments);
      Object.keys(this.articlesByBlog || {}).forEach((blogHandle) => {
        this.articlesByBlog[blogHandle] = UTILS.sortTopicsByActivity(
          this.articlesByBlog[blogHandle],
          blogHandle
        );
      });
      this.rebuildFlatArticlesFromCache();
    },

    enrichSearchComments() {
      if (!this.searchComments?.length || !this.flatArticles?.length) return;
      this.searchComments = this.searchComments.map((c) => {
        const parentTitle = this.resolveCommentParentTitle(c);
        return parentTitle !== c.parent_title ? { ...c, parent_title: parentTitle } : c;
      });
    },

    addSearchComment({
      blogHandle,
      articleHandle,
      articleTitle,
      commentIndex,
      body,
      authorName,
      profileImageUrl,
      commentHandle,
      authorId,
      parentCommentHandle,
      nestedComment,
      nestedReplySlot,
    }) {
      const authorNumericId = UTILS.customerNumericId(authorId);
      const entry = UTILS.normalizeSearchCommentItem(
        {
          handle: commentHandle || `comment-${commentIndex}-pending`,
          comment_index: commentIndex,
          parent_blog_handle: blogHandle,
          parent_article_handle: articleHandle,
          parent_title: articleTitle || articleHandle,
          body_text: UTILS.stripHtmlForSearch(body),
          author_name: authorName,
          author_id: authorNumericId || undefined,
          author_profile_image: profileImageUrl || null,
          published_at: new Date().toISOString(),
          parent_comment_handle: parentCommentHandle || '',
          nested_comment: Boolean(nestedComment),
          nested_reply_slot: nestedReplySlot,
          topic_blog_handle: nestedComment ? blogHandle : undefined,
          topic_article_handle: nestedComment ? articleHandle : undefined,
        },
        this.flatArticles
      );
      if (!entry) return;
      if (authorNumericId && !entry.author_id) {
        entry.author_id = authorNumericId;
      }
      const exists = this.searchComments.some(
        (c) =>
          (entry.comment_handle && c.comment_handle === entry.comment_handle) ||
          (c.parent_blog_handle === entry.parent_blog_handle &&
            c.parent_article_handle === entry.parent_article_handle &&
            entry.comment_index &&
            c.comment_index === entry.comment_index &&
            Boolean(c.is_nested_reply) === Boolean(entry.is_nested_reply) &&
            String(c.nested_reply_slot || '') === String(entry.nested_reply_slot || ''))
      );
      if (!exists) {
        this.searchComments = [entry, ...this.searchComments];
        UTILS._countSourceComments.push(entry);
        UTILS.bumpTopicCommentCount(blogHandle, articleHandle);
        window.dispatchEvent(new CustomEvent('modl-forum-counts-refresh'));
      }
      this.applyTopicActivityOrder();
    },

    rebuildArticleRegistry() {
      const registry = {};
      Object.entries(this.articlesByBlog || {}).forEach(([blogHandle, articles]) => {
        registry[blogHandle] = (articles || []).map((a) => a.handle).filter(Boolean);
      });
      this.blogArticleHandles = registry;
    },

    addCachedArticle(blogHandle, article) {
      const normalized =
        article && article.article_handle
          ? article
          : UTILS.normalizeApiArticle(article, blogHandle);
      if (!normalized || !blogHandle) return;

      if (!this.articlesByBlog[blogHandle]) {
        this.articlesByBlog[blogHandle] = [];
      }
      const blogList = this.articlesByBlog[blogHandle];
      if (!blogList.some((a) => a.handle === normalized.handle)) {
        blogList.unshift(normalized);
      }
      if (!this.flatArticles.some((a) => a.article_handle === normalized.article_handle && a.blog_handle === blogHandle)) {
        this.flatArticles.unshift(normalized);
      }
      if (!this.searchArticles.some((a) => a.article_handle === normalized.article_handle && a.blog_handle === blogHandle)) {
        this.searchArticles.unshift(normalized);
      }
      this.registerArticleHandle(blogHandle, normalized.handle);
      window.dispatchEvent(new CustomEvent('modl-forum-counts-refresh'));
    },

    categoryStats(blogHandle) {
      if (this.$store.forumCounts?.enabled) return this.$store.forumCounts.category(blogHandle);
      const articles = (this.articlesByBlog[blogHandle] || []).filter(a => !this.$store.moderation?.hidden(blogHandle,a.article_handle || a.handle));
      return {
        topics: articles.length,
        posts: articles.reduce((sum, a) => sum + UTILS.articlePostsCount(a), 0),
      };
    },

    blogDisplayTitle(blogHandle) {
      return this.blogTitles[blogHandle] || blogHandle;
    },

    latestTopics() {
      const limit = this.latestTopicsLimit || 5;
      return [...(this.flatArticles || [])].filter(a => !this.$store.moderation?.hidden(a.blog_handle, a.article_handle || a.handle))
        // Keep the Easter egg discoverable elsewhere, but out of this sidebar list.
        .filter(a => !(a.blog_handle === 'cs-maker-space' &&
          (a.article_handle || a.handle) === 'shhh-you-found-an-easter-egg'))
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0))
        .slice(0, limit);
    },

    latestComments() {
      const limit = this.latestPostsLimit || 5;
      return [...(this.searchComments || [])].filter(a => !this.$store.moderation?.hidden('cs-comments', a.comment_handle || a.article_handle || a.handle))
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0))
        .slice(0, limit)
        .map((c) => ({ ...c, parent_title: this.resolveCommentParentTitle(c) }));
    },

    commentPreview(text) {
      if (!text) return '';
      const words = String(text).trim().split(/\s+/);
      if (words.length <= 6) return words.join(' ');
      return words.slice(0, 6).join(' ') + '…';
    },

    latestPosts() {
      const limit = this.latestPostsLimit || 5;
      return [...(this.flatArticles || [])]
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0))
        .slice(0, limit);
    },

    getBlogPaths(root) {
      const raw = root?.dataset?.blogPaths;
      if (!raw) return {};
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch {
        return {};
      }
    },

    /** Storefront pathname for blog index or article (no query). */
    buildBlogResourcePath(articleHandle = null) {
      const blogHandle = this.selectedBlogHandle;
      const base = this.blogPaths[blogHandle];
      if (!base) return null;
      try {
        const baseU = new URL(base, window.location.origin);
        let path = baseU.pathname.replace(/\/$/, '');
        if (articleHandle) {
          const slug = UTILS.normalizeArticleSlug(blogHandle, articleHandle);
          if (!slug) return null;
          path += `/${UTILS.encodeArticlePathSegments(slug)}`;
        }
        return path;
      } catch {
        return null;
      }
    },

    sectionKeysForApi(filename) {
      const map = {
        'modl-api-view-article': ['modl-api-view-article'],
        'modl-api-view-blog': ['modl_api_view_blog', 'modl-api-view-blog'],
        'modl-api-view-landing': ['modl_api_view_landing', 'modl-api-view-landing'],
      };
      return map[filename] || [filename];
    },

    parseModlApiViewFromHtml(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const viewRoot =
        doc.querySelector('.modl-api-view') ||
        doc.querySelector('.shopify-section .modl-api-view');
      if (!viewRoot) return null;
      return {
        inner: viewRoot.innerHTML,
        isEmpty: viewRoot.classList.contains('modl-api-view--empty'),
      };
    },

    async fetchSectionInnerMarkup(pathWithSearch, sectionFilename) {
      const bust = String(Date.now());

      const tryJsonForKey = async (sectionKey) => {
        const u = new URL(pathWithSearch, window.location.origin);
        u.searchParams.set('sections', sectionKey);
        u.searchParams.set('_', bust);
        const res = await fetch(u.pathname + u.search, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const data = await res.json();
        const fragment = data[sectionKey];
        if (fragment == null || typeof fragment !== 'string') return null;
        return this.parseModlApiViewFromHtml(fragment);
      };

      for (const key of this.sectionKeysForApi(sectionFilename)) {
        try {
          const parsed = await tryJsonForKey(key);
          if (parsed?.inner) return parsed;
        } catch {
          /* try next */
        }
      }

      const u2 = new URL(pathWithSearch, window.location.origin);
      u2.searchParams.set('section_id', sectionFilename);
      u2.searchParams.set('_', bust);
      const res2 = await fetch(u2.pathname + u2.search, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
      });
      if (!res2.ok) throw new Error(`View fetch failed: ${res2.status}`);
      const parsed2 = this.parseModlApiViewFromHtml(await res2.text());
      if (!parsed2?.inner) {
        throw new Error('Section markup missing .modl-api-view');
      }
      return parsed2;
    },

    registerArticleHandle(blogHandle, articleHandle) {
      if (!blogHandle || !articleHandle) return;
      const list = this.blogArticleHandles[blogHandle] || [];
      if (!list.includes(articleHandle)) {
        this.blogArticleHandles[blogHandle] = [...list, articleHandle];
      }
    },

    InitWatchers() {
      this._previousScrollRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      this._onVisitScroll = () => {
        clearTimeout(this._visitSaveTimer);
        this._visitSaveTimer = setTimeout(() => this.saveForumVisit(), 150);
      };
      window.addEventListener('scroll', this._onVisitScroll, { passive: true });
      this._onForumPopState = () => {
        clearTimeout(this._visitSaveTimer);
        this.stopReadingScroll();
        this.forumTrail = [];
        const saved = window.history.state?.modlForumVisit;
        this.syncFromUrl();
        this.commentSort = 'newest';
        this._returnPosition = null;
        if (saved) {
          const { position, ...view } = saved;
          Object.assign(this, view);
          this._returnPosition = position;
          this.pendingCommentScroll = '';
          this.updateCurrentView();
        }
        if (this.showProfileView) {
          this.emitProfileOpen();
          this.$nextTick(() => this.restoreReadingPosition(this._returnPosition || { scrollY: 0 }));
        } else if (this.showMessagesView) {
          this.$nextTick(() => window.dispatchEvent(new CustomEvent('modl-dm-open')));
        } else void this.loadMainView();
      };
      window.addEventListener('popstate', this._onForumPopState);

      document.addEventListener('community:topic-created', async (event) => {
        this.closeCreateTopicView();
        const { blogHandle, articleHandle: rawArticleHandle, searchArticle } = event.detail || {};
        if (!blogHandle || !rawArticleHandle) return;
        const articleHandle = UTILS.normalizeArticleSlug(blogHandle, rawArticleHandle);
        if (!articleHandle) return;
        if (searchArticle && typeof searchArticle === 'object') {
          this.addCachedArticle(blogHandle, searchArticle);
        } else {
          this.registerArticleHandle(blogHandle, articleHandle);
        }
        this.selectedBlogHandle = blogHandle;
        this.selectedArticleHandle = articleHandle;
        this.commentSort = 'newest';
        this._returnPosition = null;
        this.updateCurrentView();
        this.updateUrl();
        window.scrollTo(0, 0);
        await this.loadMainView();
        this.$nextTick(() => {
          window.scrollTo(0, 0);
        });
      });
    },

    getMainViewContainer() {
      return this.$refs?.mainView || document.getElementById('modl-cf-main-view');
    },

    async loadMainView() {
      if (this.showProfileView) {
        return;
      }

      const loadId = ++this._mainViewLoadId;
      const container = this.getMainViewContainer();
      if (!container) return;

      const view = this.currentView;
      const blogHandle = this.selectedBlogHandle;
      const articleHandle = this.selectedArticleHandle;

      const isStale = () =>
        loadId !== this._mainViewLoadId ||
        this.showProfileView ||
        this.currentView !== view ||
        this.selectedBlogHandle !== blogHandle ||
        this.selectedArticleHandle !== articleHandle;

      const sectionId =
        view === 'article'
          ? 'modl-api-view-article'
          : view === 'blog'
            ? 'modl-api-view-blog'
            : 'modl-api-view-landing';

      let pathWithSearch;
      if (sectionId === 'modl-api-view-landing') {
        const u = new URL(this.landingFetchPath, window.location.origin);
        pathWithSearch = u.pathname + u.search;
      } 
      else if (sectionId === 'modl-api-view-blog') {
        const path = this.buildBlogResourcePath(null);
        if (!path) {
          container.innerHTML = '<p class="modl-cf__muted">Unable to load category.</p>';
          return;
        }
        pathWithSearch = path;
      } 
      else {
        const path = this.buildBlogResourcePath(articleHandle);
        if (!path) {
          container.innerHTML = '<p class="modl-cf__muted">Unable to load topic.</p>';
          return;
        }
        pathWithSearch = path;
      }

      if (isStale()) return;

      this.emitCommentEditorEvent('rte-destroy');
      container.innerHTML = this.getViewSkeletonHtml();
      container.setAttribute('data-modl-view-loading', 'true');
      container.setAttribute('aria-busy', 'true');
      const commentTarget = this.getCommentScrollTarget();
      const position = this._returnPosition || (commentTarget
        ? { id: UTILS.commentScrollElementId(commentTarget), center: true }
        : view === 'article' ? { scrollY: 0 } : null);
      this._returnPosition = null;
      this.stopReadingScroll();
      if (position) this.restoreReadingPosition(position, () => !isStale() && container.getAttribute('aria-busy') === 'false');

      try {
        const parsed = await this.fetchSectionInnerMarkup(pathWithSearch, sectionId);
        if (isStale()) return;

        container.innerHTML = parsed.inner;
        container.removeAttribute('data-modl-view-loading');
        container.setAttribute('aria-busy', 'false');

        if (sectionId === 'modl-api-view-article' && !parsed.isEmpty) {
          this.registerArticleHandle(this.selectedBlogHandle, this.selectedArticleHandle);
        }

        if (window.Alpine?.initTree) {
          window.Alpine.initTree(container);
        }

        void UTILS.refreshServerRenderedAvatars(container);

        if (isStale()) return;

        if (sectionId === 'modl-api-view-article' && !parsed.isEmpty) {
          const cached = UTILS.findCachedArticle(
            this.articlesByBlog,
            this.selectedBlogHandle,
            this.selectedArticleHandle
          );
          await UTILS.hydrateArticleAuthorProfile(
            container,
            this.selectedBlogHandle,
            this.selectedArticleHandle,
            cached
          );
          if (isStale()) return;
          this.pendingCommentScroll = '';
        }
      } catch (err) {
        if (isStale()) return;
        console.error(err);
        container.innerHTML = '<p class="modl-cf__muted">Could not load this view. Please refresh the page.</p>';
        container.removeAttribute('data-modl-view-loading');
        container.setAttribute('aria-busy', 'false');
      }
    },

    getViewSkeletonHtml() {
      const v = this.currentView;
      const skRow = () =>
        `<div class="modl-cf__skeleton-row">
          <div class="modl-cf__skeleton-icon" aria-hidden="true"></div>
          <div class="modl-cf__skeleton-row-main">
            <div class="modl-cf__skeleton-line modl-cf__skeleton-line--lg"></div>
            <div class="modl-cf__skeleton-line modl-cf__skeleton-line--sm"></div>
          </div>
          <div class="modl-cf__skeleton-cell" aria-hidden="true"></div>
          <div class="modl-cf__skeleton-cell" aria-hidden="true"></div>
        </div>`;

      if (v === 'landing') {
        return `<div class="modl-cf__skeleton modl-cf__skeleton--landing modl-cf__panel" aria-hidden="true">
          ${skRow()}${skRow()}${skRow()}${skRow()}${skRow()}
        </div>`;
      }

      if (v === 'blog') {
        return `<div class="modl-cf__skeleton modl-cf__skeleton--blog modl-cf__panel" aria-hidden="true">
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--breadcrumb"></div>
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--title"></div>
          <div class="modl-cf__skeleton-tags">
            <span class="modl-cf__skeleton-pill" aria-hidden="true"></span>
            <span class="modl-cf__skeleton-pill modl-cf__skeleton-pill--short" aria-hidden="true"></span>
            <span class="modl-cf__skeleton-pill" aria-hidden="true"></span>
          </div>
          <div class="modl-cf__skeleton-divider" aria-hidden="true"></div>
          ${skRow()}${skRow()}${skRow()}${skRow()}${skRow()}${skRow()}
        </div>`;
      }

      return `<div class="modl-cf__skeleton modl-cf__skeleton--article modl-cf__panel" aria-hidden="true">
        <div class="modl-cf__skeleton-line modl-cf__skeleton-line--breadcrumb"></div>
        <div class="modl-cf__skeleton-line modl-cf__skeleton-line--hero"></div>
        <div class="modl-cf__skeleton-meta">
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--xs"></div>
        </div>
        <div class="modl-cf__skeleton-block">
          <div class="modl-cf__skeleton-line"></div>
          <div class="modl-cf__skeleton-line"></div>
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--md"></div>
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--sm"></div>
        </div>
        <div class="modl-cf__skeleton-divider" aria-hidden="true"></div>
        <div class="modl-cf__skeleton-line modl-cf__skeleton-line--subtitle"></div>
        <div class="modl-cf__skeleton-comment">
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--lg"></div>
          <div class="modl-cf__skeleton-line modl-cf__skeleton-line--sm"></div>
        </div>
      </div>`;
    },

    searchResults() {
      const term = (this.searchFilter || '').trim().toLowerCase();
      if (!term) return [];
      const topicHits = (this.searchArticles || [])
        .filter((a) => {
          if (this.$store.moderation?.hidden(a.blog_handle,a.article_handle || a.handle)) return false;
          const text = (a.searchable_text || a.title || '').toLowerCase();
          return text.includes(term);
        })
        .map((a) => ({ ...a, result_type: 'topic' }));
      const commentHits = (this.searchComments || []).filter((c) =>
        !this.$store.moderation?.hidden('cs-comments',c.comment_handle || c.handle) && !this.$store.moderation?.hidden(c.parent_blog_handle,c.parent_article_handle) && (c.searchable_text || '').toLowerCase().includes(term)
      );
      return [...topicHits, ...commentHits].slice(0, 15);
    },

    searchResultKey(result) {
      if (result?.result_type === 'comment') {
        return `comment-${result.comment_handle || `${result.parent_blog_handle}-${result.parent_article_handle}-${result.comment_index}`}`;
      }
      return `topic-${result.blog_handle}-${result.article_handle || result.handle}`;
    },

    resolveCommentParentTitle(comment) {
      if (!comment) return '';
      const cached =
        this.findCachedTopic(comment.parent_blog_handle, comment.parent_article_handle) ||
        this.findCachedTopicAnyBlog(comment.parent_article_handle);
      if (cached?.title) return cached.title;
      const stored = String(comment.parent_title || '').trim();
      const handle = String(comment.parent_article_handle || '').trim();
      if (stored && stored !== handle) return stored;
      return stored || handle;
    },

    canonicalBlogHandle(handle) {
      const h = String(handle || '').trim();
      if (!h) return '';
      const match = (this.validBlogHandles || []).find(
        (b) => String(b).trim().toLowerCase() === h.toLowerCase()
      );
      return match ? String(match).trim() : '';
    },

    articleHandlesMatch(blogHandle, articleHandle, candidate) {
      if (!candidate) return false;
      const ah = String(articleHandle || '').trim();
      if (!ah) return false;
      const aBlog = String(candidate.blog_handle || '').trim();
      const bh = String(blogHandle || '').trim();
      if (bh && aBlog.toLowerCase() !== bh.toLowerCase()) {
        const canonicalBlog = this.canonicalBlogHandle(bh) || bh;
        if (aBlog.toLowerCase() !== canonicalBlog.toLowerCase()) return false;
      }
      const aHandle = String(candidate.article_handle || candidate.handle || '').trim();
      if (!aHandle) return false;
      const slug = UTILS.normalizeArticleSlug(bh || aBlog, ah);
      const aSlug = UTILS.normalizeArticleSlug(aBlog, aHandle);
      return aHandle === ah || aSlug === ah || aSlug === slug || aHandle === slug;
    },

    findCachedTopic(blogHandle, articleHandle) {
      const bh = String(blogHandle || '').trim();
      const ah = String(articleHandle || '').trim();
      if (!ah) return null;
      return (
        (this.flatArticles || []).find((a) => this.articleHandlesMatch(bh, ah, a)) || null
      );
    },

    findCachedTopicAnyBlog(articleHandle) {
      const ah = String(articleHandle || '').trim();
      if (!ah) return null;
      return (
        (this.flatArticles || []).find((a) =>
          this.articleHandlesMatch(a.blog_handle, ah, a)
        ) || null
      );
    },

    resolveTopicHandles(blogHandle, articleHandle) {
      const apiBlog = String(blogHandle || '').trim();
      const apiArticle = String(articleHandle || '').trim();
      const cached =
        this.findCachedTopic(apiBlog, apiArticle) ||
        this.findCachedTopic(this.canonicalBlogHandle(apiBlog), apiArticle) ||
        this.findCachedTopicAnyBlog(apiArticle);
      if (cached) {
        return {
          blogHandle: this.canonicalBlogHandle(cached.blog_handle) || cached.blog_handle,
          articleHandle: cached.article_handle || cached.handle,
        };
      }
      return {
        blogHandle: this.canonicalBlogHandle(apiBlog) || apiBlog,
        articleHandle: apiArticle,
      };
    },

    resolveCommentParentHandles(comment) {
      return this.resolveTopicHandles(
        comment?.parent_blog_handle,
        comment?.parent_article_handle
      );
    },

    openLatestComment(comment) {
      if (!comment) return;
      const resolved = this.resolveCommentParentHandles(comment);
      let commentIndex = comment.comment_index || undefined;
      if (!commentIndex && comment.comment_handle) {
        commentIndex = UTILS.commentIndexFromHandle(comment.comment_handle) || undefined;
      }
      if (commentIndex && comment.is_nested_reply) {
        const slot = comment.nested_reply_slot || 1;
        commentIndex = slot > 1 ? `${commentIndex}-reply-${slot}` : `${commentIndex}-reply`;
      }
      this.openArticle(resolved.blogHandle, resolved.articleHandle, commentIndex);
    },

    openSearchResultAt(index) {
      const results = this.searchResults();
      const result = results[index];
      if (!result) return;
      this.openSearchResult(result);
    },

    openSearchResult(result) {
      if (!result || typeof result !== 'object') return;

      this.searchTerm = '';
      this.searchFilter = '';

      let blogHandle;
      let articleHandle;
      let commentIndex;

      if (result.result_type === 'comment') {
        const resolved = this.resolveCommentParentHandles(result);
        blogHandle = resolved.blogHandle;
        articleHandle = resolved.articleHandle;
        commentIndex = result.comment_index || undefined;
        if (commentIndex && result.is_nested_reply) {
          const slot = result.nested_reply_slot || 1;
          commentIndex = slot > 1 ? `${commentIndex}-reply-${slot}` : `${commentIndex}-reply`;
        }
      } else {
        const resolved = this.resolveTopicHandles(
          result.blog_handle,
          result.article_handle || result.handle
        );
        blogHandle = resolved.blogHandle;
        articleHandle = resolved.articleHandle;
        commentIndex = undefined;
      }

      this.openArticle(blogHandle, articleHandle, commentIndex);
    },

    getCommentScrollTarget() {
      if (this.pendingCommentScroll) {
        return UTILS.parseCommentScrollTarget(this.pendingCommentScroll);
      }
      return UTILS.parseCommentHash();
    },

    async scrollToComment(commentTarget) {
      const parsed =
        commentTarget && typeof commentTarget === 'object' && commentTarget.index
          ? commentTarget
          : UTILS.parseCommentScrollTarget(commentTarget);
      if (!parsed?.index) return;
      const id = UTILS.commentScrollElementId(parsed);
      this.restoreReadingPosition({ id, center: true });
      this.pendingCommentScroll = '';
    },

    onSearchInput() {
      clearTimeout(this._searchDebounce);
      const term = (this.searchTerm || '').trim();
      if (!term) {
        this.searchFilter = '';
        return;
      }
      this._searchDebounce = setTimeout(() => {
        this.searchFilter = this.searchTerm;
      }, 250);
    },

    getValidBlogHandles(root) {
      const raw = root?.dataset?.validBlogHandles;

      if (!raw) {
        return [];
      }

      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },

    syncFromUrl() {
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      const params = new URLSearchParams(window.location.search);
      this.guidelinesDocument = ['terms','privacy','guidelines'].find(key => params.get(key) === '1') || 'guidelines';
      this.showGuidelinesView = ['terms','privacy','guidelines'].some(key => params.get(key) === '1');
      this.guidelinesReturnMessages = false;
      const nextHandle = (params.get('selected') || params.get('selectedBlogHandle') || '').trim();
      const nextArticle = (params.get('post') || '').trim();
      const nextTag = (params.get('tag') || '').trim();
      const accountParam = (params.get('account') || '').trim();

      if (nextTag) {
        this.selectedTag = nextTag;
      }

      if (nextHandle) {
        const canonicalHandle = this.canonicalBlogHandle(nextHandle);
        if (canonicalHandle) {
          this.selectedBlogHandle = canonicalHandle;
          if (nextArticle) {
            const slug = UTILS.normalizeArticleSlug(canonicalHandle, nextArticle);
            this.selectedArticleHandle = slug;
            if (!slug) {
              this.updateUrl({ replace: true });
            } else if (slug !== nextArticle) {
              this.updateUrl({ replace: true });
            }
          } else {
            this.selectedArticleHandle = '';
          }
          const hashTarget = UTILS.parseCommentHash();
          this.pendingCommentScroll =
            hashTarget && nextArticle ? UTILS.formatCommentScrollToken(hashTarget) : '';
          this.updateCurrentView();
          this.showProfileView = this._cfAuth && accountParam === '1';
          return;
        }
      }

      this.selectedBlogHandle = '';
      this.selectedArticleHandle = '';
      this.pendingCommentScroll = '';
      this.updateCurrentView();
      this.showProfileView = this._cfAuth && accountParam === '1';

      if (nextHandle || nextArticle) {
        this.updateUrl({ replace: true });
      }
    },

    updateCurrentView() {
      if (this.selectedBlogHandle && this.selectedArticleHandle) {
        this.currentView = 'article';
        return;
      }

      if (this.selectedBlogHandle) {
        this.currentView = 'blog';
        return;
      }

      this.currentView = 'landing';
    },

    clearCommentFeedback() {
      if (this._commentSuccessClearTimer) {
        clearTimeout(this._commentSuccessClearTimer);
        this._commentSuccessClearTimer = null;
      }
      this.commentSuccessMessage = '';
      this.commentErrorMessage = '';
    },

    scheduleCommentSuccessClear() {
      if (this._commentSuccessClearTimer) {
        clearTimeout(this._commentSuccessClearTimer);
      }
      this._commentSuccessClearTimer = setTimeout(() => {
        this.commentSuccessMessage = '';
        this._commentSuccessClearTimer = null;
      }, 3000);
    },

    getCommentHandlesFromList(listEl) {
      return UTILS.parseCommentHandles(listEl);
    },

    commentEditorProps() {
      return {
        modelValue: this.commentContent,
        onImageUpload: async (file) => UTILS.uploadToCloudinary(file, 'image'),
        onMediaUpload: async (file) => UTILS.uploadToCloudinary(file, 'video'),
        getValue: () => this.commentContent,
        setValue: (value) => {
          this.commentContent = value || '';
        },
        onChange: (value) => {
          this.commentContent = value || '';
        },
        placeholder: 'Write a reply...',
        height: 90,
        readonly: this.commentSubmitting,
      };
    },

    emitCommentEditorEvent(name, form) {
      const scope = form || document;
      scope.querySelectorAll('[data-topic-comment-editor]').forEach(host => {
        host.dispatchEvent(new CustomEvent(name));
      });
    },

    syncCommentFromEditor(form) {
      this.emitCommentEditorEvent('rte-sync', form);
      const ta = form?.querySelector('[name="comment[body]"]');
      if (ta) this.commentContent = ta.value || '';
    },

    resetCommentEditor() {
      this.commentContent = '';
      document.querySelectorAll('[data-topic-comment-form] [name="comment[body]"]').forEach(ta => {
        ta.value = '';
      });
      this.emitCommentEditorEvent('rte-mount');
    },

    nextCommentIndex(form) {
      const raw = Number(form?.dataset?.commentIndex);
      return Number.isFinite(raw) && raw > 0 ? Math.round(raw) : 1;
    },

    commentOriginalUrl(commentIndex) {
      const url = new URL(window.location.href);
      const parsed = UTILS.parseCommentScrollTarget(commentIndex);
      url.hash = parsed
        ? `#${UTILS.commentScrollElementId(parsed)}`
        : commentIndex
          ? `#modl-cf-comment-${commentIndex}`
          : '#modl-cf-comments';
      return url.toString();
    },

    updateUrl({ replace = false } = {}) {
      clearTimeout(this._visitSaveTimer);
      this.stopReadingScroll();
      const url = new URL(window.location.href);
      for (const key of ['terms','privacy','guidelines']) url.searchParams.delete(key);
      if (this.showGuidelinesView) url.searchParams.set(this.guidelinesDocument, '1');

      if (this.selectedBlogHandle) {
        url.searchParams.set('selected', this.selectedBlogHandle);
      } else {
        url.searchParams.delete('selected');
      }

      if (this.selectedBlogHandle && this.selectedArticleHandle) {
        url.searchParams.set('post', this.selectedArticleHandle);
      } else {
        url.searchParams.delete('post');
      }

      url.searchParams.delete('selectedBlogHandle');

      if (this.selectedTag) {
        url.searchParams.set('tag', this.selectedTag);
      } else {
        url.searchParams.delete('tag');
      }

      if (this.showProfileView && this._cfAuth) {
        const memberId = this.profileViewContext?.readOnly
          ? UTILS.customerNumericId(this.profileViewContext?.member?.id)
          : '';
        if (memberId) {
          url.searchParams.set('member', memberId);
          url.searchParams.delete('account');
        } else {
          url.searchParams.set('account', '1');
          url.searchParams.delete('member');
        }
      } else {
        url.searchParams.delete('account');
        url.searchParams.delete('member');
      }

      if (this.pendingCommentScroll) {
        const parsed = UTILS.parseCommentScrollTarget(this.pendingCommentScroll);
        url.hash = parsed ? `#${UTILS.commentScrollElementId(parsed)}` : '';
      } else {
        url.hash = '';
      }

      const nextUrl = url.toString();

      if (nextUrl === window.location.href) {
        return;
      }

      if (replace) {
        window.history.replaceState({}, '', nextUrl);
        return;
      }

      window.history.pushState({}, '', nextUrl);
    },

    rememberForumLocation() {
      this.saveForumVisit();
      this.stopReadingScroll();
      this._returnPosition = null;
      // Messages is an overlay, not another back destination.
      if (this.showMessagesView) return;
      this.forumTrail.push(this.captureForumVisit());
      if (this.forumTrail.length > 30) this.forumTrail.shift();
    },

    openGuidelinesView(document = 'guidelines') {
      if (!['guidelines','terms','privacy'].includes(document)) return;
      if (this.showGuidelinesView && this.guidelinesDocument === document) return;
      if (!this.showGuidelinesView) {
        this.guidelinesScrollY = window.scrollY;
        this.guidelinesReturnMessages = this.showMessagesView;
      }
      this.guidelinesDocument = document;
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = true;
      this.updateUrl();
      this.$nextTick(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        this.$refs[this.guidelinesDocument + 'Heading']?.focus({ preventScroll: true });
      });
    },

    closeGuidelinesView() {
      this.showGuidelinesView = false;
      if (this.guidelinesReturnMessages) {
        this.showMessagesView = true;
        this.guidelinesReturnMessages = false;
        window.dispatchEvent(new CustomEvent('modl-dm-open'));
      }
      this.updateUrl();
      this.$nextTick(() => window.scrollTo({ top: this.guidelinesScrollY, behavior: 'instant' }));
    },

    openMessagesView() {
      this.showGuidelinesView = false;
      if (!this.showMessagesView) {
        this.rememberForumLocation();
        this.closeCreateTopicView();
        this.showProfileView = false;
        this.showMessagesView = true;
        this.updateUrl();
      }
      // Wait until Alpine has shown Messages and the browser has painted the new layout.
      this.$nextTick(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (this.showMessagesView && !this.showGuidelinesView)
          window.scrollTo({top: 0, left: 0, behavior: 'instant'});
      })));
    },

    async backWithinForum() {
      const previous = this.forumTrail.pop();
      if (!previous) {
        if (this.selectedArticleHandle && !this.showMessagesView && !this.showProfileView) {
          this.backToBlog();
        } else if (this.showMessagesView || this.showProfileView) {
          // Direct links still return to their underlying forum topic/category.
          if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
          this.showMessagesView = false;
          this.showGuidelinesView = false;
          this.showProfileView = false;
          this.updateUrl({ replace: true });
          await this.loadMainView();
        } else {
          this.backToForum();
        }
        return;
      }
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.closeCreateTopicView();
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.saveForumVisit();
      const { position, ...view } = previous;
      Object.assign(this, view);
      this.pendingCommentScroll = '';
      this.clearCommentFeedback();
      this.updateCurrentView();
      this.updateUrl({ replace: true });
      this._returnPosition = position;
      if (this.showProfileView) {
        this.emitProfileOpen();
        this.$nextTick(() => this.restoreReadingPosition(position));
      } else await this.loadMainView();
    },

    openCreateTopicView() {
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.showProfileView = false;
      this.updateUrl();
      this.showCreateTopicView = true;
      const prefillBlog =
        this.selectedBlogHandle && this.validBlogHandles.includes(this.selectedBlogHandle)
          ? this.selectedBlogHandle
          : '';
      this.$nextTick(() => {
        document.dispatchEvent(
          new CustomEvent('cf-reset-create-topic', { detail: { blogHandle: prefillBlog } })
        );
        this.$nextTick(() => {
          const first = this.$el?.querySelector(
            prefillBlog ? '#modl-topic-heading' : '#modl-topic-blog, #modl-topic-heading'
          );
          first?.focus();
        });
      });
    },

    closeCreateTopicView() {
      this.showCreateTopicView = false;
      document.dispatchEvent(new CustomEvent('cf-destroy-create-topic-editor'));
    },

    openNotificationsView() {
      if (!this._cfAuth) return;
      this.openAccountProfile('notifications');
    },

    openNotification(item) {
      if (!this._cfAuth || !item) return;
      if (item.kind === 'welcome') return;
      if (item.preview) {
        const comment = this.latestComments()[0];
        if (comment) { this.openLatestComment(comment); return; }
        const topic = this.latestTopics()[0];
        if (topic) this.openArticle(topic.blog_handle, topic.article_handle);
        return;
      }
      this.openArticle(item.blog_handle, item.article_handle, item.comment_target);
    },

    openAccountProfile(tab = 'topics') {
      if (!this._cfAuth) {
        return;
      }
      if (!this.showProfileView || this.profileViewContext.readOnly) this.rememberForumLocation();
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      this.profileViewContext = { readOnly: false, member: null, tab };
      this.showProfileView = true;
      this.updateUrl();
      this.emitProfileOpen();
    },

    emitProfileOpen() {
      window.scrollTo({top: 0, behavior: 'instant'});
      this.$nextTick(() => {
        document.dispatchEvent(
          new CustomEvent('cf-profile-open', { detail: { ...this.profileViewContext } })
        );
      });
    },

    async openCommentAuthorProfile(article) {
      if (!article || !UTILS.hasAuthorProfile(article)) {
        return;
      }
      const authorId = UTILS.resolveAuthorId(article);
      if (!authorId) {
        return;
      }
      const currentId = UTILS.customerNumericId(rootEl?.dataset?.customerId || '');
      if (currentId && authorId === currentId) {
        this.openAccountProfile();
        return;
      }

      const originUrl = window.location.href;
      const originVisit = this.captureForumVisit();
      let enriched = article;
      const authorProfile = await UTILS.fetchCustomerProfileFields(authorId);
      const needsBio = !authorProfile.bio && !UTILS.resolveAuthorProfileBio(article);
      const needsImage = !authorProfile.image && !UTILS.resolveAuthorProfileImageUrl(article);

      if (needsBio || needsImage) {
        const handle = String(article.handle || '').trim();
        const blogHandle =
          String(article.blogHandle || article.blog_handle || '').trim() ||
          (UTILS.commentIndexFromHandle(handle) ? 'cs-comments' : String(this.selectedBlogHandle || '').trim());

        if (blogHandle && handle) {
          const resolved = await UTILS.resolveArticleForProfile(
            blogHandle,
            handle,
            this.articlesByBlog,
            article
          );
          if (resolved) enriched = resolved;
        }

        if (
          (needsBio && !UTILS.resolveAuthorProfileBio(enriched)) ||
          (needsImage && !UTILS.resolveAuthorProfileImageUrl(enriched))
        ) {
          if (handle) {
            const fetched = await UTILS.fetchCommentArticle(blogHandle || 'cs-comments', handle);
            if (fetched) enriched = fetched;
          }
        }
      }

      if (window.location.href !== originUrl) return;
      this.rememberForumLocation();
      if (!originVisit.showMessagesView) {
        this.forumTrail[this.forumTrail.length - 1] = originVisit;
        window.history.replaceState({ ...window.history.state, modlForumVisit: originVisit }, '', originUrl);
      }
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      this.profileViewContext = {
        readOnly: true,
        member: {
          id: authorId,
          name: authorProfile.name || UTILS.resolveAuthorDisplayName(enriched) || 'Member',
          profileImage: authorProfile.image || UTILS.resolveAuthorProfileImageUrl(enriched),
          bio: authorProfile.bio || UTILS.resolveAuthorProfileBio(enriched),
        },
      };
      this.showProfileView = true;
      this.updateUrl();
      this.emitProfileOpen();
    },

    closeAccountProfile() {
      void this.backWithinForum();
    },

    async openBlog(handle) {
      const canonicalBlog = this.canonicalBlogHandle(handle);
      if (!canonicalBlog) {
        return;
      }

      this.rememberForumLocation();
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      this.showProfileView = false;
      this.selectedBlogHandle = canonicalBlog;
      this.selectedArticleHandle = '';
      this.clearCommentFeedback();
      this.updateCurrentView();
      this.updateUrl();
      window.scrollTo({top: 0, left: 0, behavior: 'instant'});
      const loading = this.loadMainView();
      const loadId = this._mainViewLoadId;
      await loading;
      await this.$nextTick();
      if (loadId === this._mainViewLoadId && this.selectedBlogHandle === canonicalBlog &&
          !this.selectedArticleHandle && !this.showProfileView && !this.showMessagesView && !this.showGuidelinesView) {
        window.scrollTo({top: 0, left: 0, behavior: 'instant'});
      }
    },

    openArticle(blogHandle, articleHandle, commentIndex) {
      if (!articleHandle) {
        return;
      }

      const resolved = this.resolveTopicHandles(blogHandle, articleHandle);
      const canonicalBlog = this.canonicalBlogHandle(resolved.blogHandle) || resolved.blogHandle;
      if (!canonicalBlog) {
        return;
      }

      const articleSlug = UTILS.normalizeArticleSlug(
        canonicalBlog,
        resolved.articleHandle
      );
      if (!articleSlug) {
        return;
      }

      this.rememberForumLocation();
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      this.showProfileView = false;
      this.selectedBlogHandle = canonicalBlog;
      this.selectedArticleHandle = articleSlug;
      this.commentSort = 'newest';
      this._returnPosition = null;
      this.pendingCommentScroll =
        commentIndex != null && commentIndex !== '' ? String(commentIndex) : '';
      if (!this.pendingCommentScroll) {
        window.scrollTo(0, 0);
      }
      this.clearCommentFeedback();
      this.updateCurrentView();
      this.updateUrl();
      void this.loadMainView();
    },

    backToBlog() {
      this.saveForumVisit();
      // Level-up navigation starts at the top once the destination has loaded.
      this._returnPosition = { scrollY: 0 };
      const previous = this.forumTrail[this.forumTrail.length - 1];
      if (previous && previous.selectedBlogHandle === this.selectedBlogHandle &&
          !previous.selectedArticleHandle && !previous.showProfileView) this.forumTrail.pop();
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      this.showProfileView = false;
      this.selectedArticleHandle = '';
      this.pendingCommentScroll = '';
      this.clearCommentFeedback();
      this.updateCurrentView();
      this.updateUrl();
      void this.loadMainView();
    },

    backToForum() {
      this.saveForumVisit();
      // Level-up navigation starts at the top once the destination has loaded.
      this._returnPosition = { scrollY: 0 };
      this.forumTrail = [];
      if (this.showMessagesView) window.dispatchEvent(new CustomEvent('modl-dm-close'));
      this.showMessagesView = false;
      this.showGuidelinesView = false;
      this.closeCreateTopicView();
      this.showProfileView = false;
      this.selectedBlogHandle = '';
      this.selectedArticleHandle = '';
      this.pendingCommentScroll = '';
      this.searchTerm = '';
      this.searchFilter = '';
      this.selectedTag = '';
      this.clearCommentFeedback();
      this.updateCurrentView();
      this.updateUrl();
      void this.loadMainView();
    },

    async submitComment(event, blogHandle, articleHandle) {
      if (
        !this.validBlogHandles.includes(blogHandle) ||
        !articleHandle ||
        this.commentSubmitting
      ) {
        return;
      }

      const form = event?.target;

      if (!form) {
        return;
      }

      this.syncCommentFromEditor(form);
      const body = (this.commentContent || '').trim();
      if (!body) return;

      this.commentSubmitting = true;
      this.clearCommentFeedback();

      try {
        const parentTitle = (form.dataset.articleTitle || articleHandle).trim();
        const authorName = (form.querySelector('[name="comment[author]"]')?.value || '').trim();
        let commentIndex = this.nextCommentIndex(form);
        const originalCommentUrl = this.commentOriginalUrl(commentIndex);
        const parentArticleUrl = form.dataset.articleUrl || window.location.pathname;
        const parentArticleId = form.dataset.parentArticleId || '';
        const parentArticleGid =
          form.dataset.parentArticleGid ||
          (parentArticleId ? `gid://shopify/Article/${parentArticleId}` : '');
        const parentBlogHandle = form.dataset.parentBlogHandle || blogHandle;
        const customerId =
          form.dataset.customerId ||
          document.querySelector('section.container[data-customer-id]')?.dataset?.customerId ||
          '';
        const profileImageUrl = this.accountProfileImageUrl || UTILS.getSavedProfileImageUrl();
        const payload = UTILS.withCreateTopicAuthor({
          shop_name: Shopify.shop || 'modl-design.myshopify.com',
          blog_handle: 'cs-comments',
          title: `Comment ${commentIndex} on ${parentTitle}`,
          description: body,
          author_name: authorName,
          comment_index: commentIndex,
          parent_article_id: parentArticleId,
          parent_article_gid: parentArticleGid,
          parent_blog_handle: parentBlogHandle,
          parent_article_handle: articleHandle,
          parent_article_url: parentArticleUrl,
          original_comment_url: originalCommentUrl,
          append_to_parent_article_metafield: 'custom.comments',
          metadata: {
            comment_index: commentIndex,
            parent_article_id: parentArticleId,
            parent_article_gid: parentArticleGid,
            parent_blog_handle: parentBlogHandle,
            parent_article_handle: articleHandle,
            parent_article_url: parentArticleUrl,
            original_comment_url: originalCommentUrl,
            append_to_parent_article_metafield: 'custom.comments',
          },
        }, customerId, profileImageUrl);

        if (UTILS.commentLinksEnabled()) {
          payload.comment_links = true;
          payload.request_id = UTILS.commentRequest(form, payload.parent_article_gid || payload.parent_article_handle, body);
        }
        const response = await UTILS.communityFetch('/api/create-topic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.error || data?.message || 'Unable to submit comment.');
        }
        if (UTILS.commentLinksEnabled()) { commentIndex = Number(data.comment_index); UTILS._commentRequests.delete(form); }
        const commentArticleUrl = data?.article_url ?? data?.url ?? data?.article?.url ?? '';
        if (commentArticleUrl) {
          form.dataset.commentArticleUrl = commentArticleUrl;
        }
        form.dataset.commentIndex = String(commentIndex + 1);

        this.resetCommentEditor();

        this.commentSuccessMessage = 'Comment submitted successfully.';
        this.scheduleCommentSuccessClear();

        const commentsList = UTILS.getCommentsListComponent();
        if (commentsList?.prepend) {
          commentsList.prepend({
            id: data?.article?.id,
            handle: data?.article?.handle || `comment-${commentIndex}-pending`,
            body,
            publishedAt: new Date().toISOString(),
            author: {
              name: authorName,
              profile: profileImageUrl ? { profile_image: profileImageUrl } : null,
            },
            metafields: customerId ? { author_id: UTILS.customerNumericId(customerId) } : {},
            _commentIndex: commentIndex,
            replies: [],
          });
          commentsList.scrollToTop?.();
        }

        this.addSearchComment({
          blogHandle,
          articleHandle,
          articleTitle: parentTitle,
          commentIndex,
          body,
          authorName,
          profileImageUrl,
          commentHandle: data?.article?.handle ?? data?.handle,
          authorId: customerId,
        });

        await this.refreshCommentsList(blogHandle, articleHandle, { commentIndex });
      } 
      catch (error) {
        this.commentErrorMessage = error?.message || 'Could not submit your comment. Please try again.';
        console.error('Error submitting comment:', error);
      } 
      finally {
        this.commentSubmitting = false;
      }
    },

    async submitNestedComment(event, blogHandle, articleHandle, parentComment) {
      if (
        !this.validBlogHandles.includes(blogHandle) ||
        !articleHandle ||
        !parentComment
      ) {
        return false;
      }

      const form = event?.target;
      const listEl = form?.closest?.('.modl-cf__comments-list');
      const rowEl = form?.closest?.('.modl-cf__comment-thread');
      const row = rowEl && window.Alpine ? Alpine.$data(rowEl) : null;
      if (!row || row.replySubmitting) {
        return false;
      }

      const body = (row.replyContent || '').trim();
      if (!body) return false;

      row.replySubmitting = true;
      row.replySuccessMessage = '';
      row.replyErrorMessage = '';

      try {
        const parentTitle = (listEl?.dataset?.topicArticleTitle || articleHandle).trim();
        const authorName = (listEl?.dataset?.customerName || '').trim();
        const parentCommentIndex =
          parentComment._commentIndex || UTILS.commentIndexFromHandle(parentComment.handle);
        const parentCommentHandle = parentComment.handle || '';
        let parentCommentId = UTILS.resolveArticleNumericId(parentComment);
        let parentCommentGid = UTILS.resolveArticleGid(parentComment);
        if (!parentCommentId && parentCommentHandle) {
          try {
            const fetched = await UTILS.fetchArticleByHandle('cs-comments', parentCommentHandle);
            if (fetched) {
              parentCommentId = UTILS.resolveArticleNumericId(fetched);
              parentCommentGid = UTILS.resolveArticleGid(fetched);
            }
          } catch (err) {
            console.warn('submitNestedComment: could not resolve parent comment id', err);
          }
        }
        if (!parentCommentId && !parentCommentGid) {
          row.replyErrorMessage = 'Could not submit your reply. Please refresh the page and try again.';
          row.replySubmitting = false;
          return false;
        }
        if (!parentCommentGid && parentCommentId) {
          parentCommentGid = `gid://shopify/Article/${parentCommentId}`;
        }
        const commentsList = UTILS.getCommentsListComponent();
        const liveParent =
          commentsList?.comments?.find(
            (c) =>
              (parentCommentHandle && c.handle === parentCommentHandle) ||
              String(c._commentIndex || UTILS.commentIndexFromHandle(c.handle)) ===
                String(parentCommentIndex)
          ) || parentComment;
        const existingReplies = Array.isArray(liveParent?.replies) ? liveParent.replies : [];
        let nestedReplySlot = existingReplies.length + 1;
        const parentArticleUrl = listEl?.dataset?.topicArticleUrl || window.location.pathname;
        const parentArticleId = listEl?.dataset?.topicArticleId || '';
        const parentArticleGid =
          listEl?.dataset?.topicArticleGid ||
          (parentArticleId ? `gid://shopify/Article/${parentArticleId}` : '');
        const parentBlogHandle = listEl?.dataset?.topicBlogHandle || blogHandle;
        const customerId =
          listEl?.dataset?.customerId ||
          document.querySelector('section.container[data-customer-id]')?.dataset?.customerId ||
          '';
        const profileImageUrl = this.accountProfileImageUrl || UTILS.getSavedProfileImageUrl();
        const originalCommentUrl = this.commentOriginalUrl(
          nestedReplySlot > 1
            ? `${parentCommentIndex}-reply-${nestedReplySlot}`
            : `${parentCommentIndex}-reply`
        );
        const payload = UTILS.withCreateTopicAuthor({
          shop_name: Shopify.shop || 'modl-design.myshopify.com',
          blog_handle: 'cs-comments',
          title: `Reply ${nestedReplySlot} on Comment ${parentCommentIndex} on ${parentTitle}`,
          description: body,
          author_name: authorName,
          comment_index: parentCommentIndex,
          parent_article_id: parentCommentId,
          parent_article_gid: parentCommentGid,
          parent_blog_handle: 'cs-comments',
          parent_article_handle: parentCommentHandle,
          parent_article_url: parentArticleUrl,
          append_to_parent_article_metafield: 'custom.comments',
          topic_article_id: parentArticleId,
          topic_article_gid: parentArticleGid,
          topic_blog_handle: parentBlogHandle,
          topic_article_handle: articleHandle,
          topic_article_url: parentArticleUrl,
          parent_comment_handle: parentCommentHandle,
          parent_comment_index: parentCommentIndex,
          nested_comment: true,
          nested_reply_slot: nestedReplySlot,
          original_comment_url: originalCommentUrl,
          metadata: {
            comment_index: parentCommentIndex,
            parent_article_id: parentCommentId,
            parent_article_gid: parentCommentGid,
            parent_blog_handle: 'cs-comments',
            parent_article_handle: parentCommentHandle,
            parent_article_url: parentArticleUrl,
            append_to_parent_article_metafield: 'custom.comments',
            topic_article_id: parentArticleId,
            topic_article_gid: parentArticleGid,
            topic_blog_handle: parentBlogHandle,
            topic_article_handle: articleHandle,
            topic_article_url: parentArticleUrl,
            parent_comment_handle: parentCommentHandle,
            parent_comment_index: parentCommentIndex,
            nested_comment: true,
            nested_reply_slot: nestedReplySlot,
            original_comment_url: originalCommentUrl,
          },
        }, customerId, profileImageUrl);

        if (UTILS.commentLinksEnabled()) {
          payload.comment_links = true;
          payload.request_id = UTILS.commentRequest(form, payload.parent_article_gid || payload.parent_article_handle, body);
        }
        const response = await UTILS.communityFetch('/api/create-topic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(data?.error || data?.message || 'Unable to submit reply.');
        }

        if (UTILS.commentLinksEnabled()) { nestedReplySlot = Number(data.nested_reply_slot); UTILS._commentRequests.delete(form); }
        row.replyContent = '';
        row.replyOpen = false;
        row.replySuccessMessage = 'Reply submitted successfully.';
        row.scheduleReplySuccessClear?.();

        if (commentsList?.appendReply) {
          commentsList.appendReply(parentCommentHandle, parentCommentIndex, {
            id: data?.article?.id,
            handle: data?.article?.handle ?? data?.handle ?? `comment-${parentCommentIndex}-reply-${nestedReplySlot}-pending`,
            body,
            publishedAt: new Date().toISOString(),
            author: {
              name: authorName,
              profile: profileImageUrl ? { profile_image: profileImageUrl } : null,
            },
            metafields: customerId ? { author_id: UTILS.customerNumericId(customerId) } : {},
            _commentIndex: parentCommentIndex,
            _isNestedReply: true,
            _nestedReplySlot: nestedReplySlot,
          });
        }

        this.addSearchComment({
          blogHandle,
          articleHandle,
          articleTitle: parentTitle,
          commentIndex: parentCommentIndex,
          body,
          authorName,
          profileImageUrl,
          commentHandle: data?.article?.handle ?? data?.handle,
          authorId: customerId,
          parentCommentHandle,
          nestedComment: true,
          nestedReplySlot,
        });

        // The reply lives in the parent comment's custom.comments, not the topic list,
        // so drop the cached parent article and rely on the optimistic appendReply above.
        UTILS.invalidateArticleCache(this.commentsBlog || 'cs-comments', parentCommentHandle);
        this.$nextTick(() =>
          this.scrollToComment({
            index: parentCommentIndex,
            isReply: true,
            replySlot: nestedReplySlot,
          })
        );
        return true;
      } catch (error) {
        row.replyErrorMessage =
          error?.message || 'Could not submit your reply. Please try again.';
        console.error('Error submitting nested comment:', error);
        return false;
      } finally {
        row.replySubmitting = false;
      }
    },

    syncCommentFormIndex(comments) {
      const forms = document.querySelectorAll('[data-topic-comment-form]');
      if (!forms.length) return;
      const list = Array.isArray(comments) ? comments : UTILS.getCommentsListComponent()?.comments;
      if (!list?.length) return;
      const maxIndex = list.reduce((max, comment) => {
        const idx = Number(comment._commentIndex || UTILS.commentIndexFromHandle(comment.handle));
        return Number.isFinite(idx) && idx > max ? idx : max;
      }, 0);
      if (maxIndex > 0) {
        forms.forEach(form => { form.dataset.commentIndex = String(maxIndex + 1); });
      }
    },

    scrollCommentsListToTop(listEl) {
      const list = listEl || document.querySelector('.modl-cf__comments-list');
      if (!list) return;
      list.scrollTop = 0;
    },

    applyCommentsListHtml(currentList, nextList, commentIndex) {
      if (!currentList || !nextList) return;

      currentList.innerHTML = nextList.innerHTML;
      void this.refreshServerRenderedAvatars(currentList);

      if (commentIndex) {
        const parsed = UTILS.parseCommentScrollTarget(commentIndex);
        const selector = parsed ? `#${UTILS.commentScrollElementId(parsed)}` : '';
        const newest = selector ? currentList.querySelector(selector) : null;
        if (newest) currentList.prepend(newest.closest('.modl-cf__comment-thread') || newest);
      }

      this.scrollCommentsListToTop(currentList);
    },

    async refreshCommentsList(blogHandle, articleHandle, { commentIndex } = {}) {
      if (UTILS.commentLinksEnabled()) {
        await UTILS.getCommentsListComponent()?.pollComments();
        return;
      }

      const articlePath = UTILS.articlePath(this.blogPaths, blogHandle, articleHandle);
      if (!articlePath) {
        console.warn('refreshCommentsList: missing blog path');
        return;
      }

      const currentList = document.querySelector('.modl-cf__comments-list');
      if (!currentList) return;

      const commentsList = UTILS.getCommentsListComponent();
      const priorItems = this.getCommentHandlesFromList(currentList);
      const priorHandles = priorItems.map((i) => i.handle).filter(Boolean);
      const priorCount = commentsList?.comments?.length ?? currentList.querySelectorAll('.modl-cf__comment-thread, .modl-cf__comment').length;
      const u = new URL(articlePath, window.location.origin);
      u.searchParams.set('section_id', 'modl-api-article-comments');
      const scrollParsed = UTILS.parseCommentScrollTarget(commentIndex);
      const scrollSelector = scrollParsed ? `#${UTILS.commentScrollElementId(scrollParsed)}` : '';
      const isNestedRefresh = Boolean(scrollParsed?.isReply);
      const parentCommentIndex = scrollParsed?.index || '';
      const maxAttempts = commentIndex ? 30 : 1;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }

        u.searchParams.set('ts', String(Date.now()));
        const comments_api_url = u.pathname + u.search;

        let response;
        try {
          response = await fetch(comments_api_url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'text/html',
            },
          });
        } catch (error) {
          console.error('refreshCommentsList fetch failed:', error);
          return;
        }

        if (!response.ok) {
          console.warn('refreshCommentsList: unable to refresh comments.');
          return;
        }

        const html = await response.text();
        const parser = new DOMParser();
        const nextDocument = parser.parseFromString(html, 'text/html');
        const nextList = nextDocument.querySelector('.modl-cf__comments-list');
        if (!nextList) continue;

        const nextItems = this.getCommentHandlesFromList(nextList);
        const nextHandles = nextItems.map((i) => i.handle).filter(Boolean);
        const hasNewHandle = nextHandles.some((h) => h && !priorHandles.includes(h));
        const nextCount = nextHandles.length || nextList.querySelectorAll('.modl-cf__comment-thread, .modl-cf__comment').length;
        const hasIndexedComment = scrollSelector
          ? Boolean(nextList.querySelector(scrollSelector))
          : false;
        const hasNewComment = commentIndex
          ? hasIndexedComment || hasNewHandle
          : hasNewHandle || nextCount > priorCount || (priorCount === 0 && nextCount > 0);

        if (!hasNewComment) {
          if (attempt < maxAttempts - 1) continue;
          return;
        }

        if (nextList.querySelector('script[data-modl-comment-handles]')) {
          const nextScript = nextList.querySelector('script[data-modl-comment-handles]');
          const hasAlpine = currentList.hasAttribute('x-data');

          if (!hasAlpine) {
            currentList.replaceWith(nextList);
            if (window.Alpine?.initTree) {
              window.Alpine.initTree(nextList);
            }
            const newList = UTILS.getCommentsListComponent();
            if (commentIndex && newList?.comments?.length && !isNestedRefresh) {
              const idx = newList.comments.findIndex(
                (c) =>
                  String(c._commentIndex || UTILS.commentIndexFromHandle(c.handle)) ===
                  String(parentCommentIndex)
              );
              if (idx > 0) {
                const [item] = newList.comments.splice(idx, 1);
                newList.comments.unshift(item);
              }
            }
            newList?.scrollToTop?.();
            return;
          }

          const currentScript = currentList.querySelector('script[data-modl-comment-handles]');
          if (currentScript && nextScript) {
            currentScript.textContent = nextScript.textContent;
          }
            if (commentsList?.reload) {
            await commentsList.reload();
            if (commentIndex && !isNestedRefresh) {
              const idx = commentsList.comments.findIndex(
                (c) =>
                  String(c._commentIndex || UTILS.commentIndexFromHandle(c.handle)) ===
                  String(parentCommentIndex)
              );
              if (idx > 0) {
                const [item] = commentsList.comments.splice(idx, 1);
                commentsList.comments.unshift(item);
              }
            }
            commentsList.scrollToTop?.();
          }
          return;
        }

        this.applyCommentsListHtml(currentList, nextList, commentIndex);
        return;
      }
    },

    includesSearch(value) {
      if (!this.searchTerm) {
        return true;
      }

      return (value || '').toLowerCase().includes(this.searchTerm.trim().toLowerCase());
    },
    includesTag(value) {
      if (!this.selectedTag) {
        return true;
      }

      return (value || '').toLowerCase().includes(this.selectedTag.trim().toLowerCase());
    }
  }));

  Alpine.data('ArticlesFeed', (blogHandle) => ({
    ...forumSorting(),
    sortSourceItems() { return this.articles; },
    blogHandle: blogHandle || '',
    articles: [],
    loading: true,
    pageInfo: {},
    selectedTag: '',

    init() {
      const forum = this.getForum();
      if (forum?.selectedTag) {
        this.selectedTag = forum.selectedTag;
      }
      this._onTopicTag = (e) => {
        const tag = e.detail?.tag;
        this.selectedTag = tag == null || tag === '' ? '' : String(tag);
      };
      this._onCacheUpdated = (e) => {
        if (e.detail?.blogHandle === this.blogHandle) {
          this.syncFromForumCache();
        }
      };
      document.addEventListener('cf-topic-tag', this._onTopicTag);
      document.addEventListener('cf-articles-cache-updated', this._onCacheUpdated);
      this.loadArticles();
    },

    destroy() {
      if (this._onTopicTag) {
        document.removeEventListener('cf-topic-tag', this._onTopicTag);
      }
      if (this._onCacheUpdated) {
        document.removeEventListener('cf-articles-cache-updated', this._onCacheUpdated);
      }
    },

    getForum() {
      return UTILS.getCommunityForum();
    },

    syncFromForumCache() {
      const forum = this.getForum();
      const handle = this.blogHandle;
      if (!forum || !handle) return;
      if (forum.articlesByBlog && handle in forum.articlesByBlog) {
        this.articles = forum.articlesByBlog[handle];
        this.pageInfo = forum.pageInfoByBlog?.[handle] || {};
      }
    },

    async loadArticles() {
      const forum = this.getForum();
      const handle = this.blogHandle;
      if (!handle) {
        this.loading = false;
        return;
      }

      // Stay in the loading state while the initial forum-wide fetch finishes.
      this.loading = true;

      if (forum?.waitForArticlesLoaded) {
        await forum.waitForArticlesLoaded();
      }

      this.syncFromForumCache();
      if (this.articles.length) {
        this.loading = false;
        return;
      }

      if (!UTILS.getStorefrontToken()) {
        this.loading = false;
        return;
      }

      try {
        const data = await UTILS.getArticles(handle, { limit: UTILS.getArticlesPageSize() });
        if (forum?.mergeArticlesPage) {
          await forum.mergeArticlesPage(handle, data.articles, data.pageInfo);
          forum.startBackgroundArticlePagination?.();
          this.syncFromForumCache();
        } else {
          const rawArticles = Array.isArray(data.articles) ? data.articles : [];
          await UTILS.hydrateArticlesAuthorProfileNames(rawArticles);
          this.articles = rawArticles
            .map((a) => UTILS.normalizeApiArticle(a, handle))
            .filter(Boolean);
          this.pageInfo = data.pageInfo && typeof data.pageInfo === 'object' ? data.pageInfo : {};
        }
      } catch (err) {
        console.error('ArticlesFeed.loadArticles failed:', err);
        this.articles = [];
      } finally {
        this.loading = false;
      }
    },

    async loadNextPage() {
      const forum = this.getForum();
      if (forum?.paginateBlogRemaining) {
        await forum.paginateBlogRemaining(this.blogHandle);
        this.syncFromForumCache();
        return;
      }
      if (!this.pageInfo?.hasNextPage || !this.pageInfo?.endCursor) return;
      try {
        const data = await UTILS.getArticles(this.blogHandle, {
          limit: UTILS.getArticlesPageSize(),
          after: this.pageInfo.endCursor,
        });
        const rawNext = Array.isArray(data.articles) ? data.articles : [];
        await UTILS.hydrateArticlesAuthorProfileNames(rawNext);
        const next = rawNext
          .map((a) => UTILS.normalizeApiArticle(a, this.blogHandle))
          .filter(Boolean);
        this.articles.push(...next);
        this.pageInfo = data.pageInfo && typeof data.pageInfo === 'object' ? data.pageInfo : {};
      } catch (err) {
        console.error('ArticlesFeed.loadNextPage failed:', err);
      }
    },

    async loadPreviousPage() {
      if (!this.pageInfo?.hasPreviousPage || !this.pageInfo?.startCursor) return;
      try {
        const data = await UTILS.getArticles(this.blogHandle, {
          limit: UTILS.getArticlesPageSize(),
          before: this.pageInfo.startCursor,
        });
        const rawPrev = Array.isArray(data.articles) ? data.articles : [];
        await UTILS.hydrateArticlesAuthorProfileNames(rawPrev);
        const prev = rawPrev
          .map((a) => UTILS.normalizeApiArticle(a, this.blogHandle))
          .filter(Boolean);
        this.articles.unshift(...prev);
        this.pageInfo = data.pageInfo && typeof data.pageInfo === 'object' ? data.pageInfo : {};
        const forum = this.getForum();
        if (forum?.mergeArticlesPage) {
          await forum.mergeArticlesPage(this.blogHandle, rawPrev, data.pageInfo);
          this.syncFromForumCache();
        }
      } catch (err) {
        console.error('ArticlesFeed.loadPreviousPage failed:', err);
      }
    },

    filteredArticles() {
      const tag = (this.selectedTag || '').trim().toLowerCase();
      const visible = this.articles.filter(a => !this.$store.moderation?.hidden(this.blogHandle, a.handle));
      if (!tag) return this.sortedItems(visible);
      return this.sortedItems(visible.filter((article) => {
        const joined = (article.tags || []).map((t) => t.name).join('|');
        return joined.toLowerCase().includes(tag);
      }));
    },

    tagCounts() {
      const counts = {};
      this.articles.forEach((article) => {
        (article.tags || []).forEach((tag) => {
          if (!tag?.name) return;
          counts[tag.name] = (counts[tag.name] || 0) + 1;
        });
      });
      const seen = new Set();
      const tags = [];
      this.articles.forEach((article) => {
        (article.tags || []).forEach((tag) => {
          if (!tag?.name || seen.has(tag.name)) return;
          seen.add(tag.name);
          tags.push({ ...tag, count: counts[tag.name] || 0 });
        });
      });
      return tags;
    },

    e_FilterByTag(tag) {
      document.dispatchEvent(
        new CustomEvent('cf-topic-tag', { bubbles: true, detail: { tag } })
      );
    },

    clearTagFilter() {
      this.selectedTag = '';
      const forum = this.getForum();
      if (!forum) return;
      forum.selectedTag = '';
      forum.updateUrl();
    },

    hasSelectedTag() {
      return Boolean((this.selectedTag || '').trim());
    },
  }));

  Alpine.data('AuthorChip', (initialData = {}) => ({
    chipData: {
      name: '',
      avatarUrl: '',
      dateLabel: '',
      articlePayload: null,
      ...(initialData && typeof initialData === 'object' ? initialData : {}),
    },
    _topicAuthorPromise: null,

    init() {
      const host = this.$el.matches('[data-modl-author-chip-host]')
        ? this.$el
        : this.$el.closest('[data-modl-author-chip-host]');
      const blogHandle = host?.dataset?.topicBlogHandle;
      const articleHandle = host?.dataset?.topicArticleHandle;
      if (!blogHandle || !articleHandle) return;

      const forum = UTILS.getCommunityForum();
      const cached = UTILS.findCachedArticle(
        forum?.articlesByBlog,
        blogHandle,
        articleHandle
      );
      void this.loadTopicAuthor(blogHandle, articleHandle, cached);
    },

    async loadTopicAuthor(blogHandle, articleHandle, cachedArticle = null) {
      if (!blogHandle || !articleHandle) return;
      if (this._topicAuthorPromise) return this._topicAuthorPromise;

      this._topicAuthorPromise = (async () => {
        const article = await UTILS.resolveArticleForProfile(
          blogHandle,
          articleHandle,
          null,
          cachedArticle
        );
        if (!article) return;

        await UTILS.hydrateArticlesAuthorProfileNames([article]);

        const publishedRaw = article.publishedAt ?? article.published_at;
        this.update({
          name: UTILS.resolveAuthorDisplayName(article),
          avatarUrl: UTILS.resolveAuthorProfileImageUrl(article),
          dateLabel: UTILS.formatCommentDateBadge(publishedRaw),
          articlePayload: article,
        });
      })();

      try {
        await this._topicAuthorPromise;
      } finally {
        this._topicAuthorPromise = null;
      }
    },

    update(patch) {
      if (!patch || typeof patch !== 'object') return;
      this.chipData = { ...this.chipData, ...patch };
    },

    article() {
      return this.chipData.articlePayload;
    },

    name() {
      const article = this.article();
      if (article) {
        const resolved = UTILS.resolveAuthorDisplayName(article);
        if (resolved) {
          return UTILS.formatCommentAuthorDisplay(resolved, article) || 'Member';
        }
      }
      const raw = String(this.chipData.name || '').trim();
      if (!raw) return 'Member';
      return UTILS.formatAuthorDisplay(raw) || 'Member';
    },

    avatarUrl() {
      const direct = String(this.chipData.avatarUrl || '').trim();
      if (direct) return direct;
      const article = this.article();
      return article ? UTILS.resolveAuthorProfileImageUrl(article) : '';
    },

    editedAt() {
      const article=this.article();
      const blog=article?.blogHandle || article?.blog_handle || (this.$el.closest('.modl-cf__comments-list') ? 'cs-comments' : this.$el.closest('[data-topic-blog-handle]')?.dataset.topicBlogHandle);
      return this.$store.edits?.stamp(blog, article?.handle || this.$el.closest('[data-topic-article-handle]')?.dataset.topicArticleHandle) || '';
    },

    avatarStyle() {
      return window.ModlForumPresentation.avatarStyle(UTILS.resolveAuthorId(this.article()), this.name());
    },

    avatarLetter() {
      const article = this.article();
      const nameForLetter =
        String(this.chipData.name || '').trim() ||
        (article ? UTILS.resolveAuthorDisplayName(article) : '');
      return UTILS.authorAvatarLetters(nameForLetter);
    },

    dateLabel() {
      const direct = String(this.chipData.dateLabel || '').trim();
      if (direct) return direct;
      const article = this.article();
      if (!article) return '';
      const raw = article.publishedAt ?? article.published_at;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      return UTILS.formatCommentDateBadge(d);
    },

    canOpenProfile() {
      const article = this.article();
      return Boolean(article) && UTILS.hasAuthorProfile(article) && Boolean(UTILS.resolveAuthorId(article));
    },

    profileAriaLabel() {
      return `View profile for ${this.name()}`;
    },

    openProfile() {
      if (!this.canOpenProfile()) return;
      UTILS.getCommunityForum()?.openCommentAuthorProfile?.(this.article());
    },
  }));

  Alpine.data('CommentCard', (article) => ({
    article: article || {},

    authorName() {
      const raw = UTILS.resolveAuthorDisplayName(this.article);
      return UTILS.formatCommentAuthorDisplay(raw, this.article) || 'Member';
    },

    avatarUrl() {
      return UTILS.resolveAuthorProfileImageUrl(this.article);
    },

    avatarLetter() {
      return UTILS.authorAvatarLetters(UTILS.resolveAuthorDisplayName(this.article));
    },

    dateLabel() {
      const raw = this.article.publishedAt ?? this.article.published_at;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      return UTILS.formatCommentDateBadge(d);
    },

    metaLabel() {
      const author = this.authorName();
      const date = this.dateLabel();
      return date ? `${author} · ${date}` : author;
    },

    bodyHtml() {
      return this.article.body || this.article.content || '';
    },

    commentId() {
      const index = this.article._commentIndex || UTILS.commentIndexFromHandle(this.article.handle);
      return index ? `modl-cf-comment-${index}` : '';
    },

    canOpenProfile() {
      return UTILS.hasAuthorProfile(this.article) && Boolean(UTILS.resolveAuthorId(this.article));
    },

    openAuthorProfile() {
      if (!this.canOpenProfile()) {
        return;
      }
      const forum = UTILS.getCommunityForum();
      forum?.openCommentAuthorProfile?.(this.article);
    },
  }));

  Alpine.data('CommentRow', (comment) => ({
    comment: comment || {},
    replyOpen: false,
    replyAtEnd: false,
    replyContent: '',
    replySubmitting: false,
    replySuccessMessage: '',
    replyErrorMessage: '',
    _replySuccessClearTimer: null,

    clearReplySuccess() {
      if (this._replySuccessClearTimer) {
        clearTimeout(this._replySuccessClearTimer);
        this._replySuccessClearTimer = null;
      }
      this.replySuccessMessage = '';
    },

    scheduleReplySuccessClear() {
      if (this._replySuccessClearTimer) {
        clearTimeout(this._replySuccessClearTimer);
      }
      this._replySuccessClearTimer = setTimeout(() => {
        this.replySuccessMessage = '';
        this._replySuccessClearTimer = null;
      }, 3000);
    },

    emitReplyEditorEvent(name) {
      const host = this.$el.querySelector('.modl-cf__reply-rte-host');
      if (!host) return;
      host.dispatchEvent(new CustomEvent(name));
    },

    replyEditorProps() {
      return {
        getValue: () => this.replyContent,
        setValue: (val) => { this.replyContent = val || ''; },
        onChange: (val) => { this.replyContent = val || ''; },
        onImageUpload: async (file) => UTILS.uploadToCloudinary(file, 'image'),
        placeholder: 'Write a reply…',
        height: 90,
        readonly: this.replySubmitting,
        tinyConfig: { toolbar_mode: 'wrap' },
      };
    },

    syncReplyFromEditor() {
      this.emitReplyEditorEvent('rte-sync');
    },

    listEl() {
      return this.$el.closest('.modl-cf__comments-list');
    },

    listDataset() {
      return this.listEl()?.dataset || {};
    },

    isLoggedIn() {
      return this.listDataset().commentsLoggedIn === '1';
    },

    authorName() {
      const raw = UTILS.resolveAuthorDisplayName(this.comment);
      return UTILS.formatCommentAuthorDisplay(raw, this.comment) || 'Member';
    },

    avatarUrl() {
      return UTILS.resolveAuthorProfileImageUrl(this.comment);
    },

    avatarLetter() {
      return UTILS.authorAvatarLetters(UTILS.resolveAuthorDisplayName(this.comment));
    },

    dateLabel() {
      const raw = this.comment.publishedAt ?? this.comment.published_at;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      return UTILS.formatCommentDateBadge(d);
    },

    metaLabel() {
      const author = this.authorName();
      const date = this.dateLabel();
      return date ? `${author} · ${date}` : author;
    },

    bodyHtml() {
      if (this.$store.moderation?.hidden('cs-comments', this.comment.handle)) return '<p>' + this.$store.moderation.placeholder('cs-comments', this.comment.handle) + '</p>';
      return this.$store.edits?.current('cs-comments', this.comment.handle)?.body ?? (this.comment.body || this.comment.content || '');
    },

    commentId() {
      const index = this.comment._commentIndex || UTILS.commentIndexFromHandle(this.comment.handle);
      return index ? `modl-cf-comment-${index}` : '';
    },

    replyFieldId() {
      const index = this.comment._commentIndex || UTILS.commentIndexFromHandle(this.comment.handle);
      return index ? `modl-cf-comment-reply-${index}` : 'modl-cf-comment-reply';
    },

    replies() {
      return Array.isArray(this.comment?.replies) ? this.comment.replies : [];
    },

    hasReplies() {
      return this.replies().some(reply => this.$store.moderation?.commentVisible?.(reply) !== false);
    },

    canReply() {
      return this.isLoggedIn() && !this.$store.moderation?.hidden('cs-comments', this.comment.handle);
    },

    replyCommentId(replyIndex) {
      const index = this.comment._commentIndex || UTILS.commentIndexFromHandle(this.comment.handle);
      if (!index) return '';
      const slot = this.replies()[replyIndex]?._nestedReplySlot || replyIndex + 1;
      return `modl-cf-comment-${index}-reply-${slot}`;
    },

    authorChipData() {
      return {
        name: this.authorName(),
        avatarUrl: this.avatarUrl(),
        dateLabel: this.dateLabel(),
        articlePayload: this.comment,
      };
    },

    replyAuthorName(reply) {
      const raw = UTILS.resolveAuthorDisplayName(reply);
      return UTILS.formatCommentAuthorDisplay(raw, reply) || 'Member';
    },

    replyAvatarUrl(reply) {
      return UTILS.resolveAuthorProfileImageUrl(reply);
    },

    replyAvatarLetter(reply) {
      return UTILS.authorAvatarLetters(UTILS.resolveAuthorDisplayName(reply));
    },

    replyDateLabel(reply) {
      const raw = reply?.publishedAt ?? reply?.published_at;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return '';
      return UTILS.formatCommentDateBadge(d);
    },

    replyMetaLabel(reply) {
      const author = this.replyAuthorName(reply);
      const date = this.replyDateLabel(reply);
      return date ? `${author} · ${date}` : author;
    },

    replyBodyHtml(reply) {
      if (this.$store.moderation?.hidden('cs-comments', reply.handle)) return '<p>' + this.$store.moderation.placeholder('cs-comments', reply.handle) + '</p>';
      return this.$store.edits?.current('cs-comments', reply.handle)?.body ?? (reply?.body || reply?.content || '');
    },

    replyAuthorChipData(reply) {
      return {
        name: this.replyAuthorName(reply),
        avatarUrl: this.replyAvatarUrl(reply),
        dateLabel: this.replyDateLabel(reply),
        articlePayload: reply,
      };
    },

    async openReply(atEnd = false) {
      this.replyAtEnd = atEnd;
      this.replyOpen = true;
      this.replyErrorMessage = '';
      this.clearReplySuccess();
      await this.$nextTick();
      this.emitReplyEditorEvent('rte-mount');
    },

    cancelReply() {
      this.replyOpen = false;
      this.replyContent = '';
      this.replyErrorMessage = '';
      this.clearReplySuccess();
    },

    async submitReply(event) {
      this.syncReplyFromEditor();
      const ds = this.listDataset();
      const forum = UTILS.getCommunityForum();
      if (!forum?.submitNestedComment) return;
      await forum.submitNestedComment(
        event,
        ds.topicBlogHandle,
        ds.topicArticleHandle,
        this.comment
      );
    },
  }));

  Alpine.data('StaticCommentSorting', () => ({
    ...forumSorting(true),
    comments: [],
    init() {
      this.sortOrder = this.activeSort = UTILS.getCommunityForum()?.commentSort || 'newest';
      this.comments = [...this.$el.querySelectorAll('[data-sort-comment]')].map(node => ({
        id: node.dataset.articleId, handle: node.dataset.commentHandle,
        published_at: node.dataset.publishedAt, node
      }));
      const reorder = () => {
        const list = this.$el.querySelector('[data-static-comment-list]');
        this.sortedItems(this.comments).forEach(item => list.appendChild(item.node));
      };
      reorder();
      this.$watch(() => this.sortedItems(this.comments).map(item => item.handle).join('|'), reorder);
    },
    sortSourceItems() { return this.comments; }
  }));

  Alpine.data('CommentsList', (rootEl) => ({
    ...forumSorting(true),
    sortSourceItems() { return this.comments; },
    sortedComments() {
      const pinned = comment => !!this.justPosted[UTILS.apiArticleHandle(comment.handle)];
      const visible = this.comments.filter(comment => this.$store.moderation?.commentVisible?.(comment) !== false);
      return [...visible.filter(pinned), ...this.sortedItems(visible.filter(comment => !pinned(comment)))];
    },
    totalCommentCount() {
      return this.comments.reduce((total, comment) => total + 1 + (comment.replies?.length || 0), 0);
    },
    loadingAll: false,
    rootEl,
    commentsBlog: 'cs-comments',
    loading: false,
    comments: [],
    error: '',
    handleItems: [],
    _loadGeneration: 0,
    incoming: {},
    justPosted: {},
    _highlightTimers: new Set(),
    _pollTimer: null,
    _pollController: null,
    _polling: false,
    _pollDelay: 10000,
    _destroyed: false,

    init() {
      this.sortOrder = this.activeSort = UTILS.getCommunityForum()?.commentSort || 'newest';
      this.commentsBlog = rootEl?.dataset?.commentsBlogHandle || 'cs-comments';
      this.handleItems = UTILS.parseCommentHandles(rootEl);
      this._onModerationState = () => { if (this.loadingAll) this._moderationReloadNeeded=true; else void this.loadProgressive(); };
      window.addEventListener('modl-moderation-state',this._onModerationState);
      this._onPollVisibility = () => {
        clearTimeout(this._pollTimer);
        if (document.hidden) this._pollController?.abort();
        else void this.pollComments();
      };
      document.addEventListener('visibilitychange', this._onPollVisibility);
      void this.loadProgressive().finally(() => this.schedulePoll());
    },

    destroy() {
      this._destroyed = true; this._loadGeneration++;
      clearTimeout(this._pollTimer); this._pollController?.abort();
      this._highlightTimers.forEach(timer => clearTimeout(timer));
      document.removeEventListener('visibilitychange', this._onPollVisibility);
      window.removeEventListener('modl-moderation-state',this._onModerationState);
    },

    schedulePoll() {
      clearTimeout(this._pollTimer);
      if (!this._destroyed && !document.hidden)
        this._pollTimer = setTimeout(() => this.pollComments(), this._pollDelay);
    },

    highlightIncoming(article, posted = false) {
      const own = String(rootEl?.dataset?.customerId || '');
      if (!posted && own && String(UTILS.resolveAuthorId(article)) === own) return;
      const handle = UTILS.apiArticleHandle(article.handle);
      if (posted) this.justPosted[handle] = true;
      if (this.incoming[handle]) return;
      this.incoming[handle] = true;
      const timer = setTimeout(() => {
        delete this.incoming[handle];
        delete this.justPosted[handle];
        this._highlightTimers.delete(timer);
      }, 10000);
      this._highlightTimers.add(timer);
    },

    isIncoming(article) { return !!this.incoming[UTILS.apiArticleHandle(article.handle)]; },

    mergeIncoming(batch) {
      // Keep existing objects/keys so CommentRow's editor and draft survive.
      const existing = new Map(this.comments.map(comment => [UTILS.apiArticleHandle(comment.handle), comment]));
      for (const item of batch) {
        const previous = existing.get(UTILS.apiArticleHandle(item.handle));
        if (!previous) {
          this.comments.push(item); existing.set(UTILS.apiArticleHandle(item.handle), item);
          this.highlightIncoming(item);
          (item.replies || []).forEach(reply => this.highlightIncoming(reply));
        } else {
          const replies = new Set((previous.replies || []).map(reply => UTILS.apiArticleHandle(reply.handle)));
          for (const reply of item.replies || []) if (!replies.has(UTILS.apiArticleHandle(reply.handle))) {
            (previous.replies ||= []).push(reply); replies.add(UTILS.apiArticleHandle(reply.handle));
            this.highlightIncoming(reply);
          }
          previous.replies?.sort((a,b) => (Date.parse(a.publishedAt || a.published_at) || 0) - (Date.parse(b.publishedAt || b.published_at) || 0));
        }
      }
    },

    async pollComments() {
      if (this._destroyed || this._polling) return;
      if (document.hidden || !rootEl.isConnected || !rootEl.getClientRects().length || this.loadingAll) {
        this.schedulePoll(); return;
      }
      const topic = this.getTopicContext();
      if (!topic.blogHandle || !topic.articleHandle || !UTILS.getStorefrontToken()) { this.schedulePoll(); return; }
      this._polling = true;
      const generation = this._loadGeneration;
      const controller = new AbortController(); this._pollController = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      const valid = () => !this._destroyed && !document.hidden && rootEl.isConnected && rootEl.getClientRects().length > 0 && !controller.signal.aborted && generation === this._loadGeneration;
      try {
        const topicRefs = await UTILS.fetchCommentReferences(topic.blogHandle, [topic.articleHandle], controller.signal);
        if (!valid()) return;
        const handles = topicRefs.get(UTILS.apiArticleHandle(topic.articleHandle)) || [];
        const existing = new Map(this.comments.map(comment => [UTILS.apiArticleHandle(comment.handle), comment]));
        const newItems = handles.filter(handle => !existing.has(handle)).map(handle => ({handle,index:UTILS.commentIndexFromHandle(handle)}));
        const added = newItems.length ? await this.loadBatch(newItems, 'poll', {signal:controller.signal}) : [];
        if (!valid()) return;
        const references = await UTILS.fetchCommentReferences(this.commentsBlog, [...existing.keys()], controller.signal);
        const replyUpdates = [];
        for (const [handle, replyHandles] of references) {
          const comment = existing.get(handle);
          const known = new Set((comment.replies || []).map(reply => UTILS.apiArticleHandle(reply.handle)));
          const missing = replyHandles.filter(reply => !known.has(reply));
          if (missing.length) replyUpdates.push({handle,missing});
        }
        const missingReplies = replyUpdates.flatMap(item => item.missing);
        const {map} = await UTILS.fetchArticlesByHandles(this.commentsBlog, missingReplies, {signal:controller.signal});
        await UTILS.hydrateArticlesAuthorProfileNames([...map.values()]);
        if (!valid()) return;
        const updates = replyUpdates.map(item => ({handle:item.handle,replies:item.missing.map(handle=>{const article=map.get(handle),link=UTILS._commentLinks?.get(handle);return article && link ? {...article,_nestedReplySlot:link.position} : article;}).filter(Boolean)}));
        const anchor = [...rootEl.querySelectorAll('[data-live-comment]')].find(el => {const rect=el.getBoundingClientRect();return rect.bottom>0 && rect.top<window.innerHeight;});
        const before = anchor?.getBoundingClientRect().top, scrollY = window.scrollY;
        this.mergeIncoming([...added,...updates]);
        UTILS.getCommunityForum()?.syncCommentFormIndex?.(this.comments);
        await this.$nextTick();
        if (valid() && anchor?.isConnected && window.scrollY === scrollY) {
          const shift = anchor.getBoundingClientRect().top - before;
          if (Math.abs(shift)>1) window.scrollTo({top:scrollY+shift,behavior:'instant'});
        }
        this._pollDelay = 10000;
      } catch (error) {
        if (!this._destroyed && !document.hidden) this._pollDelay = Math.min(this._pollDelay * 2, 60000);
      } finally {
        clearTimeout(timeout); this._pollController = null; this._polling = false; this.schedulePoll();
      }
    },

    getTopicContext() {
      return {
        blogHandle: this.rootEl?.dataset?.topicBlogHandle || '',
        articleHandle: this.rootEl?.dataset?.topicArticleHandle || '',
      };
    },

    async loadBatch(handleItems, batchLabel, options = {}) {
      const { map: topMap, source: topSource } = await UTILS.fetchArticlesByHandles(
        this.commentsBlog,
        (handleItems || []).map((item) => item?.handle), options
      );
      for (const item of handleItems || []) {
        const stub=this.$store.moderation?.stub(this.commentsBlog,item.handle);
        if(stub)topMap.set(item.handle,stub);
      }
      if (UTILS.commentLinksEnabled()) {
        const refs = await UTILS.fetchCommentReferences(this.commentsBlog, [...topMap.keys()], options.signal);
        for (const [handle, article] of topMap) {
          topMap.set(handle, {...article, metafields:{...(article.metafields || {}), comments:(refs.get(handle) || []).map(h => '/blogs/cs-comments/' + h)}});
        }
      }
      UTILS.logStorefrontConsumed(`CommentsList.${batchLabel}`, {
        source: topSource,
        blog: this.commentsBlog,
        handles: (handleItems || []).map((item) => item?.handle).filter(Boolean),
        articles: [...topMap.values()],
      });

      const replyHandles = [
        ...new Set(
          UTILS.buildCommentsFromBatch(handleItems, topMap, null, new Map()).flatMap((article) =>
            UTILS.extractCommentUrlHandles(article)
          )
        ),
      ];
      let replyMap = new Map();
      if (replyHandles.length) {
        const { map, source: replySource } = await UTILS.fetchArticlesByHandles(
          this.commentsBlog,
          replyHandles, options
        );
        replyMap = map;
        for (const handle of replyHandles) { const stub=this.$store.moderation?.stub(this.commentsBlog,handle);if(stub)replyMap.set(handle,stub); }
        UTILS.logStorefrontConsumed(`CommentsList.${batchLabel}.replies`, {
          source: replySource,
          handles: replyHandles,
          articles: [...replyMap.values()],
        });
      }

      const batchComments = UTILS.buildCommentsFromBatch(handleItems, topMap, null, replyMap);
      await UTILS.hydrateArticlesAuthorProfileNames(
        batchComments.flatMap((comment) => [comment, ...(comment.replies || [])])
      );
      return batchComments;
    },

    async loadProgressive() {
      if (!UTILS.getStorefrontToken()) {
        this.error = 'Could not load comments. Storefront API token not configured.';
        return;
      }

      this.loading = true;
      this.loadingAll = true;
      this.error = '';
      this._loadGeneration += 1;
      const loadGen = this._loadGeneration;

      let handleItems = [...(this.handleItems || [])];
      const topic = this.getTopicContext();
      const forum = UTILS.getCommunityForum();
      const scrollTarget =
        forum?.pendingCommentScroll || forum?._readingTargetId || UTILS.parseCommentHash()?.index || '';

      if (UTILS.commentLinksEnabled()) {
        try {
          const refs = await UTILS.fetchCommentReferences(topic.blogHandle, [topic.articleHandle]);
          handleItems = (refs.get(UTILS.apiArticleHandle(topic.articleHandle)) || []).map(handle => ({handle, index:UTILS._commentLinks.get(handle)?.comment_index}));
          this.handleItems = handleItems;
        } catch (error) {
          this.error = 'Comments are temporarily unavailable. Please try again.';
          this.loading = false; this.loadingAll = false;
          return;
        }
      }
      if (!UTILS.commentLinksEnabled() && !handleItems.length && topic.blogHandle && topic.articleHandle) {
        try {
          const result = await UTILS.fetchCommentsByParent(
            topic.blogHandle,
            topic.articleHandle
          );
          handleItems = result.handleItems || [];
          this.handleItems = handleItems;
        } catch (err) {
          console.warn('CommentsList Option B failed:', err);
        }
      }

      if (!handleItems.length) {
        this.loading = false;
        this.loadingAll = false;
        return;
      }

      handleItems = UTILS.prioritizeCommentHandleItems(handleItems, scrollTarget, this.activeSort);

      const initialSize = UTILS.getCommentsInitialBatch();
      const batchSize = UTILS.getCommentsBatchSize();
      const firstBatch = handleItems.slice(0, initialSize);
      const remaining = handleItems.slice(initialSize);

      try {
        const first = await this.loadBatch(firstBatch, 'batch1');
        if (loadGen !== this._loadGeneration) return;

        const posted = this.comments.filter(comment => this.justPosted[UTILS.apiArticleHandle(comment.handle)] &&
          !first.some(item => UTILS.apiArticleHandle(item.handle) === UTILS.apiArticleHandle(comment.handle)));
        this.comments = UTILS.sortCommentsByIndex([...first, ...posted]);
        this.loading = false;
        UTILS.getCommunityForum()?.syncCommentFormIndex?.(this.comments);

        for (let offset = 0; offset < remaining.length; offset += batchSize) {
          if (loadGen !== this._loadGeneration) return;
          const chunk = remaining.slice(offset, offset + batchSize);
          const batchNum = Math.floor(offset / batchSize) + 2;
          const batchComments = await this.loadBatch(chunk, `batch${batchNum}`);
          if (loadGen !== this._loadGeneration) return;
          this.comments = UTILS.mergeCommentLists(this.comments, batchComments);
          UTILS.getCommunityForum()?.syncCommentFormIndex?.(this.comments);
        }

        if (!this.comments.length && handleItems.length) {
          this.error = 'Could not load comments. Please refresh the page.';
        }
      } catch (err) {
        console.error('CommentsList.loadProgressive failed:', err);
        this.error = 'Could not load comments. Please refresh the page.';
        this.loading = false;
      } finally {
        if (loadGen === this._loadGeneration) { this.loadingAll = false; if(this._moderationReloadNeeded){this._moderationReloadNeeded=false;void this.loadProgressive();} }
      }
    },

    async reload() {
      this.handleItems = UTILS.parseCommentHandles(this.rootEl);
      await this.loadProgressive();
    },

    prepend(article) {
      if (!article) return;
      const handle = article.handle;
      this.highlightIncoming(article, true);
      if (handle && this.comments.some((c) => c.handle === handle)) return;
      const index = article._commentIndex || UTILS.commentIndexFromHandle(handle);
      if (
        index &&
        this.comments.some(
          (c) => String(c._commentIndex || UTILS.commentIndexFromHandle(c.handle)) === String(index)
        )
      ) {
        return;
      }
      this.comments = [{ ...article, replies: article.replies ?? [] }, ...this.comments];
      this.error = '';
      UTILS.getCommunityForum()?.syncCommentFormIndex?.(this.comments);
    },

    appendReply(parentCommentHandle, parentCommentIndex, replyArticle) {
      if (!replyArticle) return;
      const idx = this.comments.findIndex(
        (c) =>
          (parentCommentHandle && c.handle === parentCommentHandle) ||
          String(c._commentIndex || UTILS.commentIndexFromHandle(c.handle)) ===
            String(parentCommentIndex)
      );
      if (idx < 0) return;
      const target = this.comments[idx];
      target.replies = [...(target.replies || []), replyArticle];
      this.comments = this.comments.slice();
      this.error = '';
    },

    async scrollToTop() {
      await this.$nextTick();
      if (this._destroyed || !this.rootEl?.isConnected) return;
      this.rootEl.scrollTop = 0;
      const comment = this.rootEl.querySelector('[data-live-comment]');
      if (!comment) return;
      const rect = comment.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        comment.scrollIntoView({block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
      }
    },
  }));

  Alpine.data('ArticleCard', (article, blogHandle) => ({
    article: article || {},
    blogHandle: blogHandle || '',
    init() { this.$store.edits?.register({blog:this.blogHandle,handle:this.article.handle}); },
    destroy() { this.$store.edits?.unregister({blog:this.blogHandle,handle:this.article.handle}); },
    displayTitle() { return this.$store.edits?.current(this.blogHandle,this.article.handle)?.title ?? this.article.title; },

    authorName() {
      return this.article.author || this.article.author_name || 'Member';
    },

    avatarUrl() {
      return UTILS.resolveAuthorProfileImageUrl(this.article);
    },

    avatarLetter() {
      return UTILS.authorAvatarLetters(this.authorName());
    },

    publishedLabel() {
      const raw = this.article.publishedAt ?? this.article.published_at;
      if (!raw) return '';
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      return UTILS.formatCommentDateBadge(d);
    },

    authorChipData() {
      const raw = UTILS.resolveAuthorDisplayName(this.article);
      return {
        name: UTILS.formatCommentAuthorDisplay(raw, this.article) || this.authorName(),
        avatarUrl: this.avatarUrl(),
        dateLabel: this.publishedLabel(),
        articlePayload: {
          ...this.article,
          blogHandle: this.blogHandle,
        },
      };
    },

    postsCount() {
      return UTILS.articlePostsCount({ ...this.article, blog_handle: this.blogHandle });
    },

    repliesCount() {
      return UTILS.articleRepliesCount({ ...this.article, blog_handle: this.blogHandle });
    },

    previewText() {
      const edited=this.$store.edits?.current(this.blogHandle,this.article.handle);
      const raw = edited ? (edited.linked_page ? edited.summary || '' : UTILS.stripHtmlForSearch(edited.body)) : String(this.article?.excerpt || '').trim();
      return raw.replace(/\s+/g, ' ').trim();
    },

    openArticle(event) {
      event?.preventDefault?.();
      const section = this.$el.closest('section.container');
      const forum = section && window.Alpine ? Alpine.$data(section) : null;
      if (forum?.openArticle) {
        forum.openArticle(this.blogHandle, this.article.handle);
      }
    },
  }));

  Alpine.data('CommunityCustomerArea', (rootEl) => ({
    customer: { name: '', id: null },
    flags: {
      submitting: false,
      formValid: false,
    },
    submitError: '',
    blogs: [],
    topicTags: [],
    topicType: 'standard',
    linkedPage: '',
    linkedSummary: '',
    linkedPageOptions: [],
    linkedUrlsAllowed: false,
    linkedPreviewId: '',
    linkedPreviewUrl: '',
    linkedPreviewError: '',
    linkedPreviewBusy: false,
    form: {
      blogHandle: '',
      title: '',
      description: '',
      notes: '',
      tagHandles: [],
    },
    response: null,

    init() {
      this.customer = this.getCustomerFromDataset(rootEl);
      this.blogs = this.getBlogs(rootEl);
      this.topicTags = this.getTopicTags(rootEl);
      this.InitWatchers();
      void this.loadLinkedPages();
      this._onResetCreateTopic = (e) => {
        void this.resetForm(e?.detail?.blogHandle);
      };
      this._onDestroyEditor = () => this.destroyContentEditor();
      this._onDisplayNameUpdated = (e) => {
        const name = e.detail?.name ? String(e.detail.name).trim() : '';
        if (name) this.customer.name = name;
      };
      document.addEventListener('cf-reset-create-topic', this._onResetCreateTopic);
      document.addEventListener('cf-destroy-create-topic-editor', this._onDestroyEditor);
      document.addEventListener('cf-display-name-updated', this._onDisplayNameUpdated);
    },

    getCustomerFromDataset(root) {
      const raw = root?.dataset?.customer;
      if (!raw) return { name: '', id: null };
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : { name: '', id: null };
      } catch {
        return { name: '', id: null };
      }
    },

    async loadLinkedPages() {
      try {
        const response = await window.ModlCommunityAPI.linkedPages('/options');
        if (!response.ok) return;
        const data = await response.json();
        this.linkedPageOptions = Array.isArray(data.pages) ? data.pages.filter(p => p.id === 'community-highlights') : [];
        this.linkedUrlsAllowed = data.supports_urls === true;
        this.linkedPage = this.linkedUrlsAllowed ? '' : this.linkedPageOptions[0]?.id || '';
      } catch { this.linkedPageOptions = []; }
      this.validateTopicForm();
    },

    async previewLinkedPage() {
      if(this.linkedPreviewBusy)return;
      const url = this.linkedPage.trim();
      this.linkedPreviewId = ''; this.linkedPreviewError = ''; this.linkedPreviewBusy = true;
      this.validateTopicForm();
      try {
        const response = await window.ModlCommunityAPI.linkedPages('/preview?url=' + encodeURIComponent(url));
        const data = await response.json();
        if(!response.ok)throw Error(data.error || 'This page could not be displayed.');
        if(this.linkedPage.trim() !== url)return;
        this.linkedPreviewUrl = url; this.linkedPreviewId = data.page.id;
      } catch(error) { if(this.linkedPage.trim() === url)this.linkedPreviewError = error.message; }
      finally { this.linkedPreviewBusy = false; this.validateTopicForm(); }
    },

    validateTopicForm() {
      const contentValid = this.topicType === 'linked'
        ? (this.linkedUrlsAllowed ? !!this.linkedPreviewId && this.linkedPreviewUrl === this.linkedPage.trim() : this.linkedPageOptions.some(p => p.id === this.linkedPage))
        : !!String(this.form.description || '').trim();
      this.flags.formValid = !!String(this.form.title || '').trim() && !!this.form.blogHandle && contentValid;
    },

    InitWatchers() {
      this.$watch('form', () => { this.validateTopicForm(); this.submitError = ''; }, {deep: true});
      this.$watch('topicType', () => {
        this.validateTopicForm(); this.submitError = '';
        if (this.topicType === 'standard') this.$nextTick(() => this.mountContentEditor());
      });
      this.$watch('linkedPage', () => { this.linkedPreviewId = ''; this.linkedPreviewError = ''; this.validateTopicForm(); });
    },

    getBlogs(root) {
      const raw = root?.dataset?.blogs;
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    },

    getTopicTags(root) {
      const forumRoot = root?.closest?.('[data-valid-blog-handles]');
      const fromScript =
        forumRoot?.querySelector?.('#modl-cf-topic-tags')?.textContent?.trim() ||
        document.getElementById('modl-cf-topic-tags')?.textContent?.trim();
      let raw = fromScript;
      if (!raw) {
        raw = forumRoot?.dataset?.tags ?? root?.dataset?.tags;
      }
      if (!raw) return [];
      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed
          .map((t) => {
            if (!t || typeof t !== 'object') return null;
            const h =
              t.handle != null && String(t.handle).trim() !== ''
                ? String(t.handle).trim()
                : t.id != null && String(t.id).trim() !== ''
                  ? String(t.id).trim()
                  : '';
            if (!h) return null;
            const name =
              t.name != null && String(t.name).trim() !== '' ? String(t.name).trim() : h;
            return { handle: h, name, id: t.id };
          })
          .filter(Boolean);
      } catch {
        return [];
      }
    },

    topicEditorProps() {
      return {
        modelValue: this.form.description,
        onImageUpload: async (file) => UTILS.uploadToCloudinary(file, 'image'),
        onMediaUpload: async (file) => UTILS.uploadToCloudinary(file, 'video'),
        getValue: () => this.form.description,
        setValue: (value) => {
          this.form.description = value || '';
        },
        onChange: (value) => {
          this.form.description = value || '';
        },
        placeholder: 'Start writing...',
        height: 260,
        readonly: this.flags.submitting,
      };
    },

    emitTopicEditorEvent(name) {
      const host = document.getElementById('modl-topic-content-rte');
      if (!host) return;
      host.dispatchEvent(new CustomEvent(name));
    },

    destroyContentEditor() {
      this.emitTopicEditorEvent('rte-destroy');
    },

    syncDescriptionFromEditor() {
      this.emitTopicEditorEvent('rte-sync');
      const ta = document.getElementById('modl-topic-content');
      if (ta) this.form.description = ta.value || '';
    },

    async mountContentEditor() {
      const ta = document.getElementById('modl-topic-content');
      if (!ta) return;
      ta.value = this.form.description || '';
      this.emitTopicEditorEvent('rte-mount');
    },

    resetFormPrefillBlogHandle(prefill) {
      const h = typeof prefill === 'string' ? prefill.trim() : '';
      if (!h) return '';
      return this.blogs.some((b) => b.handle === h) ? h : '';
    },

    async resetForm(prefillBlogHandle) {
      this.destroyContentEditor();
      this.submitError = '';
      const blogHandle = this.resetFormPrefillBlogHandle(prefillBlogHandle);
      this.topicType = 'standard';
      this.linkedSummary = '';
      this.linkedPreviewId = ''; this.linkedPreviewUrl = ''; this.linkedPreviewError = '';
      this.form = { blogHandle, title: '', description: '', notes: '', tagHandles: [] };
      await new Promise((resolve) => this.$nextTick(resolve));
      const ta = this.$refs.contentEditor;
      if (ta) ta.value = '';
      await new Promise((resolve) => this.$nextTick(resolve));
      await this.mountContentEditor();
    },

    async e_SubmitForm() {
      if (this.flags.submitting) return;
      if (this.topicType === 'standard') this.syncDescriptionFromEditor();
      this.validateTopicForm();
      if (!this.flags.formValid) return;
      this.submitError = '';
      this.flags.submitting = true;

      try {
        const tags = this.topicTags
          .filter((t) => this.form.tagHandles.includes(t.handle))
          .map((t) => ({ name: t.name, handle: t.handle, id: t.id }));

        const customerId =
          this.customer?.id ||
          this.$el?.dataset?.customerId ||
          document.querySelector('.modl-cf-create-topic')?.dataset?.customerId ||
          '';
        const profileImageUrl = UTILS.getSavedProfileImageUrl();
        const payload = UTILS.withCreateTopicAuthor({
          shop_name: Shopify.shop || 'modl-design.myshopify.com',
          blog_handle: this.form.blogHandle,
          title: this.form.title,
          description: this.topicType === 'linked' ? '' : this.form.description,
          ...(this.topicType === 'linked' ? { linked_page: this.linkedPage, linked_summary: this.linkedSummary } : {}),
          notes: this.form.notes,
          author_name: this.customer?.name,
          tags,
        }, customerId, profileImageUrl);

        const res = await UTILS.communityFetch('/api/create-topic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => null);
        this.response = data;
        if (!res.ok) {
          const err = new Error(data?.error || data?.message || 'Create topic failed');
          err.code = data?.code;
          err.status = res.status;
          throw err;
        }

        const blogHandle = data.blog_handle ?? data.blogHandle ?? this.form.blogHandle;
        const articleHandle =
          data.article_handle ?? data.handle ?? data.article?.handle;

        if (!blogHandle || !articleHandle) {
          throw new Error('Create topic response missing handles');
        }

        const pathsHost =
          document.querySelector('section.container[data-blog-paths]') ||
          document.querySelector('[data-blog-paths]');
        let pollBlogPaths = {};
        try {
          pollBlogPaths = JSON.parse(pathsHost?.dataset?.blogPaths || '{}');
        } catch {
          pollBlogPaths = {};
        }
        const pollPath = UTILS.articlePath(pollBlogPaths, blogHandle, articleHandle);
        if (!pollPath) {
          throw new Error('Cannot resolve article URL for polling');
        }
        const pollUrl = new URL(pollPath, window.location.origin);
        pollUrl.searchParams.set('section_id', 'modl-api-article-check');
        let pollAttempts = 0;
        const maxPollAttempts = 120;
        const checkInterval = setInterval(async () => {
          pollAttempts += 1;
          if (pollAttempts > maxPollAttempts) {
            clearInterval(checkInterval);
            console.warn('Topic did not appear on the storefront in time; refresh the page to see it.');
            this.submitError =
              'Your topic was created but is taking a while to appear. Reload the page to see it.';
            this.flags.submitting = false;
            return;
          }
          try {
            pollUrl.searchParams.set('ts', String(Date.now()));
            const checkRes = await fetch(pollUrl.pathname + pollUrl.search, {
              credentials: 'same-origin',
              headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
            });
            const text = await checkRes.text();
            if (!text.includes('true')) return;
            clearInterval(checkInterval);
            this.CreatedTopicSuccess(blogHandle, articleHandle);
            this.flags.submitting = false;
            this.flags.formValid = false;
          } catch (e) {
            console.error('Topic availability poll failed:', e);
          }
        }, 1000);
      } catch (error) {
        console.error('Error submitting form:', error);
        this.submitError = UTILS.createTopicErrorMessage(error);
        this.flags.submitting = false;
      }
    },

    CreatedTopicSuccess(blogHandle, articleHandle) {
      const r = this.response || {};
      const title = String(r.title ?? this.form.title ?? '').trim();
      const rawBody = this.topicType === 'linked'
        ? String(r.article?.summary ?? (this.linkedSummary.trim() || 'Explore the latest ideas from the MODL community.'))
        : String(r.description ?? this.form.description ?? '');
      const searchable_text = `${title} ${rawBody.replace(/<[^>]*>/g, ' ')}`.replace(/\s+/g, ' ').trim();
      const published_raw = r.published_at ?? r.publishedAt;
      let published_at;
      if (published_raw) {
        published_at =
          typeof published_raw === 'string'
            ? published_raw
            : new Date(published_raw).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
      } else {
        published_at = new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
      const profileImageUrl = UTILS.getSavedProfileImageUrl();
      const customerId =
        this.customer?.id ||
        this.$el?.dataset?.customerId ||
        document.querySelector('section.container[data-customer-id]')?.dataset?.customerId ||
        '';
      const authorNumericId = UTILS.customerNumericId(customerId);
      const authorName = String(r.author ?? r.author_name ?? this.customer?.name ?? '').trim();
      const searchArticle = title
        ? {
            title,
            author: authorName,
            author_name: authorName,
            author_id: authorNumericId,
            published_at,
            publishedAt: published_raw || new Date().toISOString(),
            blog_handle: blogHandle,
            article_handle: articleHandle,
            handle: articleHandle,
            searchable_text,
            excerpt: UTILS.stripHtmlForSearch(rawBody).trim(),
            comments_count: r.comments_count ?? 0,
            author_profile_image: profileImageUrl,
            first_tag: null,
            tags: [],
            metafields: authorNumericId ? { author_id: authorNumericId } : {},
          }
        : null;

      document.dispatchEvent(
        new CustomEvent('community:topic-created', {
          detail: {
            blogHandle,
            articleHandle,
            ...(searchArticle && { searchArticle }),
          },
        })
      );
    }
  }));

  Alpine.data('CommunityProfile', (rootEl) => ({
    customer: { name: '', id: null },
    initialBio: '',
    savedProfileImageUrl: '',
    readOnly: false,
    editing: false,
    memberDisplayName: '',
    memberId: '',
    memberProfileImageUrl: '',
    memberBioHtml: '',
    foundingMember: false,
    _foundingRequest: 0,
    recentTopicActivity: [],
    recentCommentActivity: [],
    activityLoading: false,
    activityError: '',
    activityTopicPage: 1,
    activityCommentPage: 1,
    activityPageSize: 10,
    activityTab: 'topics',
    /** Local blob preview while uploading */
    profilePhotoPreview: null,
    profilePhotoFile: null,
    bioContent: '',
    nameContent: '',
    _lastSavedBio: null,
    _lastSavedName: null,
    flags: { submitting: false, uploadingPhoto: false },
    message: { success: '', error: '' },

    init() {
      this.customer = this.getCustomerFromDataset(rootEl);
      this.initialBio = this.getJsonFromScript('modl-cf-profile-initial-bio', '');
      this.savedProfileImageUrl = this.getJsonFromScript('modl-cf-profile-initial-profile-image-url', '');
      const initialName = this.getJsonFromScript(
        'modl-cf-profile-initial-display-name',
        this.customer?.name || ''
      );
      this._lastSavedName = initialName;
      this.nameContent = initialName;
      if (initialName) this.customer.name = initialName;
      this.bioContent = this.initialBio;
      this._onOpen = (event) => void this.onProfileOpen(event);
      this._onClose = () => this.onProfileClose();
      this._onDisplayNameUpdated = (e) => {
        const name = e.detail?.name ? String(e.detail.name).trim() : '';
        if (!name) return;
        this.customer.name = name;
        this._lastSavedName = name;
        this.nameContent = name;
        const script = document.getElementById('modl-cf-profile-initial-display-name');
        if (script) script.textContent = JSON.stringify(name);
      };
      this._onPhotoUpdated = (event) => { this.savedProfileImageUrl = String(event.detail?.url || ''); };
      document.addEventListener('cf-profile-image-updated', this._onPhotoUpdated);
      this._onTopicCreated = (event) => this.onTopicCreated(event);
      document.addEventListener('cf-profile-open', this._onOpen);
      document.addEventListener('cf-profile-close', this._onClose);
      document.addEventListener('cf-display-name-updated', this._onDisplayNameUpdated);
      document.addEventListener('community:topic-created', this._onTopicCreated);
    },

    destroy() {
      document.removeEventListener('cf-profile-image-updated', this._onPhotoUpdated);
      if (this._onOpen) document.removeEventListener('cf-profile-open', this._onOpen);
      if (this._onClose) document.removeEventListener('cf-profile-close', this._onClose);
      if (this._onDisplayNameUpdated) {
        document.removeEventListener('cf-display-name-updated', this._onDisplayNameUpdated);
      }
      if (this._onTopicCreated) {
        document.removeEventListener('community:topic-created', this._onTopicCreated);
      }
    },

    getCustomerFromDataset(root) {
      const raw = root?.dataset?.customer;
      if (!raw) return { name: '', id: null };
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : { name: '', id: null };
      } catch {
        return { name: '', id: null };
      }
    },

    getJsonFromScript(id, defaultValue) {
      const el = document.getElementById(id);
      if (!el) return defaultValue;
      const t = el.textContent?.trim();
      if (t == null || t === '') return defaultValue;
      try {
        const v = JSON.parse(t);
        if (v == null) return defaultValue;
        if (typeof v === 'string') return v;
        if (v && typeof v === 'object' && v.value != null) return String(v.value);
        return defaultValue;
      } catch {
        return String(t) || defaultValue;
      }
    },

    getBioForEditor() {
      if (this._lastSavedBio != null) return this._lastSavedBio;
      return this.initialBio || '';
    },

    getNameForEditor() {
      if (this._lastSavedName != null) return this._lastSavedName;
      return this.customer?.name || '';
    },

    startEditing() {
      this.nameContent = this.getNameForEditor();
      this.editing = true;
      this.mountBioEditor();
    },

    cancelEditing() {
      this.editing = false;
      this.nameContent = this.getNameForEditor();
      this.bioContent = this.getBioForEditor();
      this.message = { success: '', error: '' };
    },

    memberAvatarLetter() {
      return UTILS.authorAvatarLetters(this.memberDisplayName || 'Member');
    },

    applyReadOnlyProfile(member) {
      this.readOnly = true;
      this.activityTab = 'topics';
      this.memberDisplayName = member?.name || 'Member';
      this.memberId = member?.id != null && member.id !== '' ? String(member.id) : '';
      this.memberProfileImageUrl = member?.profileImage ? String(member.profileImage) : '';
      this.memberBioHtml = member?.bio ? String(member.bio) : '';
      this.message = { success: '', error: '' };
    },

    resetReadOnlyProfile() {
      this.readOnly = false;
      this.memberDisplayName = '';
      this.memberId = '';
      this.memberProfileImageUrl = '';
      this.memberBioHtml = '';
    },

    viewedCustomerId() {
      if (this.readOnly) return UTILS.customerNumericId(this.memberId);
      return UTILS.customerNumericId(this.customer?.id);
    },

    async loadFoundingMember() {
      const id = this.viewedCustomerId(), request = ++this._foundingRequest;
      this.foundingMember = false;
      if (!id) return;
      try {
        const params = new URLSearchParams({shop_name: UTILS.shopName(), customer_id: id, keys: 'profile_name'});
        const response = await UTILS.communityFetch(`/api/customer-metafields?${params}`);
        const data = await response.json();
        if (response.ok && request === this._foundingRequest && id === this.viewedCustomerId())
          this.foundingMember = data.customer?.founding_member === true;
      } catch { /* A profile lookup failure should not block the profile. */ }
    },

    viewedCustomerName() {
      if (this.readOnly) return String(this.memberDisplayName || '').trim();
      return String(this.customer?.name || '').trim();
    },

    commentMatchesViewedCustomer(item) {
      if (!item) return false;
      const id = this.viewedCustomerId();
      const authorId = UTILS.resolveAuthorId(item) || UTILS.customerNumericId(item.author_id);
      if (id && authorId && authorId === id) return true;

      const viewedName = this.viewedCustomerName().toLowerCase();
      if (!viewedName) return false;
      const authorName = String(
        item.author_name ?? item.author ?? UTILS.resolveAuthorDisplayName(item) ?? ''
      )
        .trim()
        .toLowerCase();
      return Boolean(authorName && authorName === viewedName);
    },

    async normalizeCommentActivitySource(article, flatArticles) {
      if (!article) return null;
      let normalized = UTILS.normalizeSearchCommentItem(article, flatArticles);
      if (normalized || !article.handle) return normalized;

      try {
        const full = await UTILS.fetchCommentArticle('cs-comments', article.handle);
        normalized = full ? UTILS.normalizeSearchCommentItem(full, flatArticles) : null;
      } catch (err) {
        console.warn('normalizeCommentActivitySource fetch failed:', article.handle, err);
      }
      return normalized;
    },

    async waitForForumData(forum) {
      if (!forum) return;
      for (let attempt = 0; attempt < 50; attempt += 1) {
        if (!forum.articlesLoading) break;
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    },

    topicMatchesViewedCustomer(article) {
      if (!article) return false;
      const id = this.viewedCustomerId();
      const authorId = UTILS.resolveAuthorId(article) || UTILS.customerNumericId(article.author_id);
      if (id && authorId && authorId === id) return true;

      const viewedName = this.viewedCustomerName().toLowerCase();
      if (!viewedName) return false;
      const authorName = String(
        article.author_name ?? article.author ?? UTILS.resolveAuthorDisplayName(article) ?? ''
      )
        .trim()
        .toLowerCase();
      return Boolean(authorName && authorName === viewedName);
    },

    mapTopicToActivityItem(article) {
      if (!article) return null;
      return {
        type: 'topic',
        title: article.title || '',
        excerpt: UTILS.stripHtmlForSearch(
          article.body ||
            article.content ||
            article.excerpt ||
            article.summary ||
            article.searchable_text ||
            ''
        ),
        searchable_text: article.searchable_text || '',
        blogHandle: article.blog_handle || article.blogHandle,
        articleHandle: article.article_handle || article.handle,
        publishedAt: article.publishedAt || article.published_at || '',
        publishedLabel:
          article.published_at || UTILS.formatArticleDate(article.publishedAt || article.published_at),
      };
    },

    prependTopicActivity(article) {
      const item = this.mapTopicToActivityItem(article);
      if (!item?.blogHandle || !item?.articleHandle) return;
      const exists = this.recentTopicActivity.some(
        (topic) =>
          topic.blogHandle === item.blogHandle && topic.articleHandle === item.articleHandle
      );
      if (exists) return;
      this.recentTopicActivity = [item, ...this.recentTopicActivity].sort(
        (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
      );
      this.activityTab = 'topics';
    },

    onTopicCreated(event) {
      if (this.readOnly) return;
      const customerId = this.viewedCustomerId();
      if (!customerId) return;
      const { blogHandle, articleHandle, searchArticle } = event.detail || {};
      if (!blogHandle || !articleHandle) return;
      const article = {
        ...(searchArticle && typeof searchArticle === 'object' ? searchArticle : {}),
        blog_handle: blogHandle,
        article_handle: articleHandle,
        handle: articleHandle,
        author_id: searchArticle?.author_id || customerId,
        publishedAt: searchArticle?.publishedAt || new Date().toISOString(),
      };
      if (!this.topicMatchesViewedCustomer(article)) return;
      this.prependTopicActivity(article);
    },

    mapCommentToActivityItem(comment) {
      if (!comment) return null;
      return {
        type: 'comment',
        commentHandle: comment.comment_handle,
        title: comment.parent_title || comment.parent_article_handle || '',
        excerpt: UTILS.stripHtmlForSearch(comment.searchable_text || ''),
        blogHandle: comment.parent_blog_handle,
        articleHandle: comment.parent_article_handle,
        commentIndex: comment.comment_index,
        isNestedReply: Boolean(comment.is_nested_reply),
        nestedReplySlot: comment.nested_reply_slot,
        publishedAt: comment.publishedAt || '',
        publishedLabel: comment.published_at || UTILS.formatArticleDate(comment.publishedAt),
      };
    },

    dedupeCommentActivityItems(items) {
      const seen = new Set();
      return (items || []).filter((item) => {
        const key =
          item.comment_handle ||
          `${item.parent_blog_handle}-${item.parent_article_handle}-${item.comment_index}-${item.type}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    },

    async getCommentActivityItems(forum, customerId) {
      if (!UTILS.customerNumericId(customerId) || !forum) return [];

      const fromSearch = (forum.searchComments || []).filter((comment) =>
        this.commentMatchesViewedCustomer(comment)
      );

      let fromBlog = [];
      try {
        const data = await UTILS.getArticles('cs-comments', { limit: UTILS.getArticlesPageSize() });
        UTILS.logStorefrontConsumed('getCommentActivityItems:cs-comments', {
          source: data.source || 'unknown',
          count: Array.isArray(data?.articles) ? data.articles.length : 0,
          pageInfo: data.pageInfo,
          articles: data.articles,
        });
        const articles = Array.isArray(data?.articles) ? data.articles : [];
        const normalized = await Promise.all(
          articles.map(async (article) => {
            if (this.commentMatchesViewedCustomer(article)) {
              return this.normalizeCommentActivitySource(article, forum.flatArticles);
            }
            if (!article?.handle || UTILS.resolveAuthorId(article)) return null;
            const full = await UTILS.fetchCommentArticle('cs-comments', article.handle);
            if (!full || !this.commentMatchesViewedCustomer(full)) return null;
            return UTILS.normalizeSearchCommentItem(full, forum.flatArticles);
          })
        );
        fromBlog = normalized.filter(Boolean);
      } catch (err) {
        console.warn('getCommentActivityItems cs-comments fetch failed:', err);
      }

      return this.dedupeCommentActivityItems([...fromSearch, ...fromBlog])
        .map((comment) => this.mapCommentToActivityItem(comment))
        .filter(Boolean);
    },

    async loadRecentActivity(customerId) {
      const id = UTILS.customerNumericId(customerId);
      this.activityLoading = true;
      this.activityError = '';
      this.recentTopicActivity = [];
      this.recentCommentActivity = [];

      if (!id) {
        this.activityTopicPage = 1;
        this.activityCommentPage = 1;
        this.activityLoading = false;
        return;
      }

      try {
        const forum = UTILS.getCommunityForum();
        if (!forum) throw new Error('Community not ready.');

        await this.waitForForumData(forum);

        const topicItems = (forum.flatArticles || [])
          .filter((article) => this.topicMatchesViewedCustomer(article))
          .map((article) => this.mapTopicToActivityItem(article))
          .filter(Boolean);

        const commentItems = await this.getCommentActivityItems(forum, id);

        this.recentTopicActivity = topicItems.sort(
          (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        );
        this.recentCommentActivity = commentItems.sort(
          (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
        );
        if (this.activityTab !== 'notifications') this.activityTab = this.recentTopicActivity.length
          ? 'topics'
          : this.recentCommentActivity.length
            ? 'comments'
            : 'topics';
      } catch (err) {
        console.warn('loadRecentActivity failed:', err);
        this.activityError = 'Could not load recent activity.';
        this.recentTopicActivity = [];
        this.recentCommentActivity = [];
      } finally {
        this.activityTopicPage = 1;
        this.activityCommentPage = 1;
        this.activityLoading = false;
      }
    },

    hasRecentActivity() {
      return this.recentTopicActivity.length > 0 || this.recentCommentActivity.length > 0;
    },

    visibleRecentTopics() {
      const limit = this.activityTopicPage * this.activityPageSize;
      return this.recentTopicActivity.filter(a => !this.$store.moderation?.hidden(a.blogHandle,a.articleHandle)).slice(0, limit);
    },

    visibleRecentComments() {
      const limit = this.activityCommentPage * this.activityPageSize;
      return this.recentCommentActivity.filter(a => !this.$store.moderation?.hidden('cs-comments',a.commentHandle) && !this.$store.moderation?.hidden(a.blogHandle,a.articleHandle)).slice(0, limit);
    },

    hasMoreRecentTopics() {
      return this.visibleRecentTopics().length < this.recentTopicActivity.length;
    },

    hasMoreRecentComments() {
      return this.visibleRecentComments().length < this.recentCommentActivity.length;
    },

    loadMoreRecentTopics() {
      this.activityTopicPage += 1;
    },

    loadMoreRecentComments() {
      this.activityCommentPage += 1;
    },

    setActivityTab(tab) {
      this.activityTab = tab === 'notifications' && !this.readOnly && this.$store.notifications.enabled ? 'notifications' : tab === 'comments' ? 'comments' : 'topics';
      if (this.activityTab === 'notifications') void this.$store.notifications.refresh();
    },

    recentActivityTitle(item) {
      if (!item) return '';
      return String(item.title || '').trim();
    },

    recentActivityTypeLabel(item) {
      if (!item) return '';
      if (item.type === 'topic') return 'Topic';
      return 'Comment';
    },

    recentActivityTypeClass(item) {
      if (!item) return '';
      if (item.type === 'topic') return 'is-topic';
      return 'is-comment';
    },

    recentActivityMeta(item) {
      if (!item) return '';
      return String(item.publishedLabel || '').trim();
    },

    recentActivityPreview(item) {
      if (!item) return '';
      const maxLen = 120;
      let text = '';

      if (item.type === 'topic') {
        text = UTILS.stripHtmlForSearch(item.excerpt || '');
        if (!text && item.searchable_text) {
          text = UTILS.stripHtmlForSearch(item.searchable_text);
          const title = String(item.title || '').trim();
          if (title && text.toLowerCase().startsWith(title.toLowerCase())) {
            text = text.slice(title.length).trim();
          }
        }
      } else {
        text = UTILS.stripHtmlForSearch(item.excerpt || '');
      }

      text = String(text || '').trim();
      if (!text) return '';
      return text.length > maxLen ? `${text.slice(0, maxLen).trim()}…` : text;
    },

    recentActivityExcerpt(item) {
      return this.recentActivityPreview(item);
    },

    openRecentActivity(item) {
      if (!item) return;
      const forum = UTILS.getCommunityForum();
      if (!forum?.openArticle) return;

      let commentIndex = item.commentIndex;
      if (item.type !== 'topic' && commentIndex) {
        if (item.isNestedReply) {
          const slot = item.nestedReplySlot || 1;
          commentIndex = slot > 1 ? `${commentIndex}-reply-${slot}` : `${commentIndex}-reply`;
        }
        commentIndex = String(commentIndex);
      } else {
        commentIndex = undefined;
      }

      forum.openArticle(item.blogHandle, item.articleHandle, commentIndex);
    },

    async onProfileOpen(event) {
      const detail = event?.detail || {};
      if (detail.readOnly && detail.member) {
        this.$store.notifications.leaveWelcomeView();
        this.applyReadOnlyProfile(detail.member);
        void this.loadFoundingMember();
        await this.loadRecentActivity(this.viewedCustomerId());
        return;
      }
      this.resetReadOnlyProfile();
      void this.loadFoundingMember();
      this.setActivityTab(detail.tab || 'topics');
      this.message = { success: '', error: '' };
      this.editing = false;
      const latestName = this.getJsonFromScript(
        'modl-cf-profile-initial-display-name',
        this.customer?.name || ''
      );
      if (latestName) {
        this.customer.name = latestName;
        this._lastSavedName = latestName;
        this.nameContent = latestName;
      }
      const latestBio = this.getJsonFromScript('modl-cf-profile-initial-bio', this.initialBio || '');
      this.initialBio = latestBio;
      this._lastSavedBio = latestBio;
      this.bioContent = latestBio;
      // Show the profile at the top for half a second before scrolling to Notifications.
      if (this.activityTab === 'notifications') this.$nextTick(() => requestAnimationFrame(() => setTimeout(() => {
        const tab = this.$refs.notificationsTab;
        if (this.activityTab !== 'notifications' || !tab?.getClientRects().length) return;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
        tab.scrollIntoView({block: 'start', behavior});
        tab.focus({preventScroll: true});
      }, 500)));
      await this.loadRecentActivity(this.viewedCustomerId());
    },

    onProfileClose() {
      this.$store.notifications.leaveWelcomeView();
      this._foundingRequest++; this.foundingMember = false;
      this.editing = false;
      this.resetReadOnlyProfile();
      this.recentTopicActivity = [];
      this.recentCommentActivity = [];
      this.activityTopicPage = 1;
      this.activityCommentPage = 1;
      this.activityTab = 'topics';
      this.activityLoading = false;
      this.activityError = '';
      if (this.profilePhotoPreview && this.profilePhotoPreview.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.profilePhotoPreview);
        } catch {
          /* noop */
        }
      }
      this.profilePhotoFile = null;
      this.profilePhotoPreview = null;
      const input = this.$refs.photoInput;
      if (input) input.value = '';
    },

    syncBioFromEditor() {
      const ta = document.getElementById('cf-profile-bio');
      this.bioContent = ta?.value || '';
    },

    mountBioEditor() {
      const ta = document.getElementById('cf-profile-bio');
      if (!ta) return;
      const val = this.getBioForEditor();
      ta.value = val;
      this.bioContent = val;
    },

    updateProfileImageUI(url) {
      if (this.profilePhotoPreview && this.profilePhotoPreview.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.profilePhotoPreview);
        } catch {
          /* noop */
        }
      }
      this.savedProfileImageUrl = url ? String(url) : '';
      this.profilePhotoPreview = null;
      this.profilePhotoFile = null;
      const input = this.$refs?.photoInput;
      if (input) input.value = '';
      document.dispatchEvent(
        new CustomEvent('cf-profile-image-updated', {
          detail: { url: this.savedProfileImageUrl },
        })
      );
    },

    async onPhotoInputChange(event) {
      const input = event?.target;
      const file = input?.files?.[0];
      if (!file) return;

      const validationError = UTILS.validateProfileImageFile(file);
      if (validationError) {
        this.message = { success: '', error: validationError };
        if (input) input.value = '';
        return;
      }

      this.message = { success: '', error: '' };
      if (this.profilePhotoPreview && this.profilePhotoPreview.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.profilePhotoPreview);
        } catch {
          /* noop */
        }
      }
      this.profilePhotoFile = file;
      this.profilePhotoPreview = URL.createObjectURL(file);
      this.flags.uploadingPhoto = true;

      try {
        const secureUrl = await UTILS.uploadToCloudinary(file, 'image');
        await UTILS.saveCustomerProfileImage(secureUrl, this.customer?.id);
        this.updateProfileImageUI(secureUrl);
        this.message.success = 'Profile picture updated.';
      } catch (err) {
        console.error('Profile image upload failed:', err);
        this.updateProfileImageUI(this.savedProfileImageUrl);
        this.message.error = err?.message || 'Could not upload profile picture.';
      } finally {
        this.flags.uploadingPhoto = false;
        if (input) input.value = '';
      }
    },

    async submitProfile() {
      if (this.flags.submitting) return;
      if (this.editing) {
        this.syncBioFromEditor();
      } else {
        const ta = document.getElementById('cf-profile-bio');
        this.bioContent = ta?.value || '';
      }
      const trimmedName = String(this.nameContent || '').trim();
      if (!trimmedName) {
        this.message = { success: '', error: 'Username is required.' };
        return;
      }
      this.message = { success: '', error: '' };
      this.flags.submitting = true;
      try {
        if (!this.customer?.id) {
          throw new Error('Not signed in.');
        }
        await UTILS.saveCustomerDisplayNameMetafield(trimmedName, this.customer.id);
        await UTILS.saveCustomerBioMetafield(this.bioContent || '', this.customer.id);
        this._lastSavedBio = this.bioContent;
        this.initialBio = this.bioContent;
        this._lastSavedName = trimmedName;
        this.nameContent = trimmedName;
        this.customer.name = trimmedName;
        const nameScript = document.getElementById('modl-cf-profile-initial-display-name');
        if (nameScript) nameScript.textContent = JSON.stringify(trimmedName);
        UTILS.updateCommunityDisplayName(trimmedName, this.customer.id);
        this.editing = false;
        this.message.success = 'Profile saved.';
      } catch (err) {
        console.error('Profile save failed:', err);
        this.message.error = err?.message || 'Could not save profile.';
      } finally {
        this.flags.submitting = false;
      }
    },
  }));
});
